import { rolleZaehler } from '@theme/dp-zaehler-rolle';

/*
 * Das Wort der Pille "Vorschau" - mit der Vorschaufarbe, wenn eine gilt
 * (dp-veredelung.js setzt sie an die Seite, vorschauWort). Zellen, die nach
 * dem Setzen der Farbe entstehen, trugen sonst nur "Vorschau" statt
 * "Vorschau in Schwarz".
 */
function vorschauWortJetzt() {
  const name = document.documentElement.dataset.dpVorschaufarbeName || '';
  return name ? 'Vorschau in ' + name : 'Vorschau';
}
import { altMarken } from '@theme/dp-alt-marken';
import { macheModal } from '@theme/dp-fenster-fokus';
import { rolltInnen } from '@theme/dp-rollsperre';

/**
 * Grossansicht der Produktbilder.
 *
 * Horizon bringt eine eigene mit, oeffnet sie aber als modalen Dialog. Ein
 * solcher liegt zwingend im obersten Rang - das Kauffenster am unteren Rand,
 * das sichtbar bleiben soll, waere damit verdeckt. Deshalb diese Fassung: sie
 * fuellt den Platz oberhalb des Kauffensters und laesst es stehen.
 *
 * Geblaettert wird in ganzen Schritten. Bewusst kein nativer Scrollbereich:
 * eine kraeftige Wischgeste haette dort mehrere Bilder auf einmal ueberflogen.
 * Stattdessen wird der Streifen verschoben, und eine Geste rueckt genau ein
 * Bild weiter.
 *
 * Gezoomt wird mit zwei Fingern, mit dem Mausrad oder ueber die beiden Knoepfe.
 * Der Zoom sitzt am einzelnen Bild, nicht an der Seite - die bleibt unberuehrt.
 */
const MAX_ZOOM = 4;

const GROSSE_BREITE = 1600;

/*
 * Speicher am iPhone (Jimmi, Oktober 2026: Speicherspur - die Bilder fuellten
 * den Speicher, Safari lud nach Farbwechseln neu): Die Kopien der
 * Grossansicht sind 1600 Punkte breit, je Bild zweimal (Bild und weicher
 * Grund). Sie laden erst, wenn ihre Zelle gezeigt wird oder Nachbar ist
 * (ladeUmgebung), und geben ihr Bild beim Schliessen wieder ab - ohne
 * Adresse, damit Safari die entschluesselte Fassung freigibt.
 * Die Adresse bleibt in data-dp-spaet.
 */

function istLeer(bild) {
  return !bild.getAttribute('src');
}

/** Laedt ein Bild der Grossansicht, wenn es noch leer steht. */
function ladeSpaet(bild) {
  if (!(bild instanceof HTMLImageElement) || !bild.dataset.dpSpaet) return;
  if (!istLeer(bild)) return;
  bild.loading = 'eager';
  bild.src = bild.dataset.dpSpaet;
}

/** Gibt ein geladenes Bild der Grossansicht wieder ab - nicht die lebende Vorschau (blob:). */
function gibBildAb(bild) {
  if (!(bild instanceof HTMLImageElement) || !bild.dataset.dpSpaet) return;
  const s = bild.getAttribute('src') || '';
  if (!s || /^(blob|data):/.test(s)) return;
  // Ohne Adresse zaehlt das Bild als nicht geladen (naturalWidth 0) - die Masse kommen dann aus data-dp-natur.
  bild.removeAttribute('srcset');
  bild.removeAttribute('src');
}

/**
 * Fordert dieselbe Aufnahme in grosser Fassung an.
 *
 * Shopify liefert die Groesse ueber den Parameter width in der Adresse. Steht
 * er nicht drin, wird er angehaengt.
 *
 * @param {string} adresse
 * @returns {string}
 */
function grosseFassung(adresse) {
  if (!adresse) return adresse;
  /*
   * Die lebenden Ansichten (IMPRINT) tragen ihre Abzuege als blob-Adresse
   * in voller Aufloesung - dort gibt es keine groessere Fassung, und ein
   * angehaengter Parameter machte die Adresse kaputt.
   */
  if (adresse.startsWith('data:') || adresse.startsWith('blob:')) return adresse;
  if (/[?&]width=\d+/.test(adresse)) {
    return adresse.replace(/([?&])width=\d+/, '$1width=' + GROSSE_BREITE);
  }
  return adresse + (adresse.includes('?') ? '&' : '?') + 'width=' + GROSSE_BREITE;
}

const WISCH_SCHWELLE = 40;

/*
 * Das Ausrichten auf den bearbeiteten Bereich (zeigeBereich): Es greift,
 * sobald mehr als RAND_ZUGABE Punkte des Bereichs ausserhalb des sichtbaren
 * Teils liegen; Bewegungen unter ZITTER_GRENZE Punkten (ohne Zoom) bleiben
 * aus. Herausgezoomt wird in ZOOM_STUFEN Schritten bis zum ganzen Bild.
 */
const RAND_ZUGABE = 8;
const ZITTER_GRENZE = 12;
const ZOOM_STUFEN = 12;

/* Bis hierhin gilt eine Beruehrung als Tipp und nicht als Wischen. */
const TIPP_WEG = 12;

/* Zeit und Abstand, in denen zwei Tipps als Doppeltipp zusammengehoeren. */
const DOPPEL_ZEIT = 320;
const DOPPEL_WEG = 40;

/*
 * So lange nach einem erkannten Doppeltipp wird der Doppelklick des Browsers
 * uebergangen. Er meldet dieselbe Geste ein zweites Mal - und wuerde den
 * Zoom, den der Tipp eben gesetzt hat, gleich wieder zuruecknehmen.
 */
const DOPPEL_SPERRE = 700;

/* Dauer der Rueckfahrt in die Ausgangsgroesse. */
const AUSGANG_DAUER = 280;

/*
 * Ob das angetippte Bild beim Oeffnen aus der Galerie in die Ansicht fliegt
 * (fliegeEin). Auf Wunsch abgeschaltet - die Ansicht blendet dann schlicht
 * ein. Der Flug selbst bleibt im Code und laesst sich hier wieder anstellen.
 */
const BILD_FLUG = false;

/* Die kurze Vorfuehrung beim ersten Oeffnen - Dauer und wie weit sie heranfaehrt. */
const VORFUEHR_DAUER = 2600;
const VORFUEHR_ZOOM = 1.4;

/*
 * So lange muss ein Bild Ruhe gegeben haben, bevor die Vorfuehrung losfaehrt.
 *
 * Eine lebende Ansicht von IMPRINT schiebt nach dem Oeffnen noch mehrere
 * Fassungen desselben Motivs nach. Jede muss der Browser neu rastern, und das
 * geschieht auf demselben Faden, auf dem die Fahrt gerechnet wird - sie
 * verlor dabei ganze Bilder. Wer wartet, bis nichts mehr nachkommt, faehrt
 * ungestoert.
 */
const VORFUEHR_RUHE = 260;
/* Laenger als das wird nicht gewartet - dann faehrt sie eben trotzdem. */
const VORFUEHR_GEDULD = 2500;

/* Ist das grosse Bild gezeichnet, steht es so lange still, bevor die Vorfuehrung beginnt. */
const VORFUEHR_PAUSE = 350;
/* So lange darf das grosse Bild zum Laden brauchen - kommt es spaeter, entfaellt die Vorfuehrung. */
const VORFUEHR_LADEGEDULD = 8000;

/*
 * So weit zieht ein Doppeltipp heran. Nicht bis an die Obergrenze: das waere
 * ein Sprung, nach dem der Kunde meist wieder herausziehen muesste. Zwei-
 * einhalb zeigt ein Viertel der Flaeche - genug, um eine Naht oder eine
 * Beschriftung zu lesen.
 */
const DOPPEL_ZOOM = 2.5;

/**
 * Darf dieser Hinweis noch einmal gezeigt werden?
 *
 * Hoechstens zweimal je Besuch - danach kennt man ihn. Gezaehlt wird im
 * Sitzungsspeicher: er gilt fuer den Tab und ist beim naechsten Besuch wieder
 * leer. Dieselbe Zaehlung steht in dp-bild-overlay.js, dp-galerie-hinweis.js
 * und dp-scroller.js; jede fuehrt ihren eigenen Eintrag.
 *
 * Gezaehlt wird erst beim tatsaechlichen Zeigen, nicht beim Pruefen der
 * uebrigen Bedingungen.
 *
 * @param {string} [schluessel] Eintrag im Sitzungsspeicher - ohne Angabe der Zwei-Finger-Hinweis.
 * @returns {boolean}
 */
function darfHinweisZeigen(schluessel = 'dp-hinweis-geste') {

  try {
    const bisher = Number(window.sessionStorage.getItem(schluessel) || '0');
    if (bisher >= HINWEIS_HOECHSTENS) return false;

    window.sessionStorage.setItem(schluessel, String(bisher + 1));
    return true;
  } catch {
    /*
     * Im privaten Fenster ist der Speicher gesperrt. Dann eben ohne Zaehlung -
     * ein Hinweis ist kein Grund, hier auszusteigen.
     */
    return true;
  }
}

/** Wie oft ein Hinweis je Besuch hoechstens erscheint. */
const HINWEIS_HOECHSTENS = 2;


/**
 * Die gerade geoeffnete Ansicht.
 *
 * Wird die Variante gewechselt, tauscht das Theme die ganze Bildergalerie aus.
 * Die Ansicht haengt an der alten Galerie und muss auf die neue uebergehen -
 * sonst steht sie mit den Bildern der vorigen Farbe da oder verschwindet.
 *
 * @type {DpBildOverlay | null}
 */
/*
 * Seitensperre (Jimmi, 28.10.2026, iPad): Solange die Grossansicht offen ist - Foto, Druckvorschau oder 3D-Vorschau, die liegen alle in ihr -,
 * rollt die Seite dahinter weder per Wischen noch per Trackpad/Rad. Nur auf Touch-Geraeten (pointer: coarse); am Rechner aendert sich nichts.
 * html[data-dp-seitensperre] nimmt der Seite den Ueberlauf (divineprint.css), und weil Safari auf dem iPad das allein nicht immer haelt,
 * schlucken zwei Zuhoerer die Geste, wenn kein Rollbereich der Ansicht (Kauffenster, Streifen, Werkzeuge) sie braucht. Die Zuhoerer haengen
 * nur, solange die Ansicht offen ist. Kein position:fixed - der Rollstand bleibt, es gibt keinen Sprung beim Schliessen.
 */
function sperreTouch(e) {
  if (!e.cancelable || !window.matchMedia('(pointer: coarse)').matches) return;
  if (rolltInnen(e.target, false) || rolltInnen(e.target, true)) return;
  e.preventDefault();
}

function sperreRad(e) {
  if (!e.cancelable || e.ctrlKey || !window.matchMedia('(pointer: coarse)').matches) return;
  if (rolltInnen(e.target, false, e.deltaY) || rolltInnen(e.target, true, e.deltaX)) return;
  e.preventDefault();
}

function setzeSeitensperre(an) {
  const html = document.documentElement;
  if (an === html.hasAttribute('data-dp-seitensperre')) return;
  if (an) {
    html.setAttribute('data-dp-seitensperre', '');
    document.addEventListener('touchmove', sperreTouch, { passive: false });
    document.addEventListener('wheel', sperreRad, { passive: false });
  } else {
    html.removeAttribute('data-dp-seitensperre');
    document.removeEventListener('touchmove', sperreTouch);
    document.removeEventListener('wheel', sperreRad);
  }
}

let offeneAnsicht = null;

/*
 * Die Zurueck-Geste schliesst die Grossansicht, statt die Seite zu verlassen:
 * macheModal (dp-fenster-fokus.js) haengt das Feld beim Oeffnen in den Verlauf
 * ein (dp-zurueck.js) und gibt den Schritt beim Loesen still wieder her. Hier
 * steht dafuer nichts Eigenes mehr - ein zweiter Schritt waere ein Fehler.
 */

/** Alle angelegten Ansichten, um ersetzte wieder abzuraeumen. */
const ansichten = new Set();

/*
 * Der Hinweis auf die Zwei-Finger-Geste gilt fuer den Seitenaufruf, nicht fuer
 * die einzelne Ansicht: nach einem Variantenwechsel entsteht eine neue, und der
 * Hinweis liefe sonst ein zweites Mal.
 */
let gesteGezeigt = false;

/**
 * Wie lange der Wechsel der freigehaltenen Flaeche laeuft - je nach Richtung.
 *
 * Geht die Tastatur auf, gibt das Kauffenster seinen Platz frei und das Bild
 * wird groesser: Das folgt der Zeile, die nach oben schwebt (320).
 *
 * Geht sie zu, nimmt das Kauffenster den Platz zurueck und das Bild wird
 * wieder kleiner. Das darf laenger dauern, denn das Fenster selbst baut sich
 * erst auf (dp-tastaturzeile.js, AUFBAU 450). Schrumpfte das Bild in 320
 * Millisekunden, stuende es schon klein da, waehrend unten noch nichts zu
 * sehen ist.
 */
const FLAECHE_AUF = 320;
const FLAECHE_ZU = 560;


/* Die Kachel eines Bildes - in einer Bewertung die eigene, sonst die der Produktgalerie. */
const KACHEL_PRODUKT = '.media-gallery__grid .product-media-container';
/*
 * Videos einer Bewertung stehen in derselben Grossansicht an ihrer Stelle in
 * der Reihe (Jimmi, Oktober 2026) - ihre Kachel traegt das Standbild als img,
 * die Zelle legt das Video darueber (macheZelle, dp-bildfeld__zelle--video).
 */
const KACHEL_BEWERTUNG = '.dp-bewertung__bild';
/*
 * Livechat (snippets/dp-chat.liquid, assets/dp-chat.js, Jimmi, Oktober 2026: Klickt der
 * Kunde im Chat auf ein Bild, soll es im klassischen Bildoverlay geoeffnet werden): Der
 * Verlauf des Chats ist eine Galerie, seine Bildanhaenge (dp-chat.js, baueBild) sind die
 * Kacheln. Die Ansicht fuellt wie auf jeder Seite ohne Kauffenster den Bildschirm; sie
 * laesst das Kauffenster der Seite in Ruhe und liegt ueber dem Chat-Fenster (Stilblatt,
 * Abschnitt "Livechat"). Dateien, die keine Bilder sind, bleiben Download-Links.
 */
const KACHEL_CHAT = '.dp-chat__bild';
const CHAT_VERLAUF = '.dp-chat__verlauf';

/* Die Zeichen der Videobedienung: Abspielen, Pause, Ton an und aus - im Stil der uebrigen Overlay-Knoepfe. */
const VIDEO_ZEICHEN = {
  spielen: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5.5 3.6v8.8a.6.6 0 0 0 .9.5l6.9-4.4a.6.6 0 0 0 0-1L6.4 3.1a.6.6 0 0 0-.9.5z" fill="currentColor"/></svg>',
  pause: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect x="4" y="3" width="2.6" height="10" rx=".8" fill="currentColor"/><rect x="9.4" y="3" width="2.6" height="10" rx=".8" fill="currentColor"/></svg>',
  laut: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3.5 8v4h3l4 3.5v-11L6.5 8z"/><path d="M13.5 7.5a3.5 3.5 0 0 1 0 5M16 5.2a6.8 6.8 0 0 1 0 9.6"/></svg>',
  stumm: '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3.5 8v4h3l4 3.5v-11L6.5 8z"/><path d="M13.5 8l4 4M17.5 8l-4 4"/></svg>',
};

class DpBildOverlay {
  /** @param {HTMLElement} galerie */
  constructor(galerie) {
    this.galerie = galerie;
    // Der Verlauf des Livechats: eigene Kacheln, sonst dieselbe Ansicht (KACHEL_CHAT).
    this.istChat = galerie.matches(CHAT_VERLAUF);
    this.kachel = this.istChat ? KACHEL_CHAT : galerie.matches('.dp-bewertung__bilder') ? KACHEL_BEWERTUNG : KACHEL_PRODUKT;
    /*
     * Blaettern ueber die eigene Reihe hinaus (gehe, zeigeReihe): die Bewertungen
     * - und jede Galerie mit data-dp-reihen, etwa die Artikel auf "Bestellung
     * verfolgen" (dp-bestellung.js, Jimmi, Oktober 2026). Wer die Grenze meldet
     * bekommt, entscheidet die Seite (dp:bewertung-grenze).
     */
    this.reihen = this.kachel === KACHEL_BEWERTUNG || galerie.hasAttribute('data-dp-reihen');
    /*
     * Nur die Bilder der gewaehlten Farbe: der Farbfilter blendet fremde
     * Kacheln mit display:none aus (serverseitig beim Aufbau, dp-galeriewechsel
     * beim Wechsel) - die Grossansicht blaettert sonst durch Bilder, die in
     * der Galerie gar nicht zu sehen sind.
     */
    this.bilder = this.sammleBilder();
    if (!this.bilder.length) return;

    this.index = 0;

    /*
     * Der Platz im Streifen ist nicht dasselbe wie der Bildindex: vor dem
     * ersten und hinter dem letzten Bild steht je eine Kopie des jeweils
     * anderen Endes, damit das Blaettern im Kreis laufen kann.
     */
    this.platz = 0;
    this.hatKlone = false;
    this.zoom = 1;
    /*
     * Massstab, bei dem das Bild die Flaeche gerade ausfuellt. Er ist der
     * Ausgangspunkt beim Oeffnen und zugleich die Grenze, ab der geschoben
     * statt geblaettert wird.
     */
    this.grundZoom = 1;
    this.versatz = { x: 0, y: 0 };
    this.zeiger = new Map();
    this.startAbstand = 0;
    this.startPunkt = null;
    this.griffStart = 0;
    this.griffZoom = 1;
    this.zieht = false;

    /* Merkposten fuer den Doppeltipp - Zeit und Ort des letzten Tipps. */
    this.letzterTipp = 0;
    this.tippOrt = null;
    /** Die Lage, zu der ein zweiter Doppeltipp zurueckfuehrt. */
    this.ausgangVersatz = null;
    /* Wann der Tippweg zuletzt gedoppelt hat - der Doppelklick danach ist derselbe Griff. */
    this.zuletztGedoppelt = 0;

    /* Das eingeplante Zeichnen und der Platz, fuer den zuletzt aufgeraeumt wurde. */
    this.zeichenPlan = 0;
    this.geleertFuer = -1;

    /*
     * Was waehrend einer Zoomgeste gemessen wurde. Flaeche und Bildmasse
     * aendern sich dabei nicht - sie jedes Mal neu zu erfragen zwang den
     * Browser mitten in der Bewegung zum Nachrechnen des Aufbaus, weil im
     * Bild davor schon geschrieben worden war.
     */
    this.griffMerk = null;

    this.baue();
    // Das Zeichen im Stilblatt (Abschnitt "Livechat"): die Ansicht des Chats liegt ueber dem Chat-Fenster.
    if (this.istChat) this.feld.classList.add('dp-bildfeld--chat');

    /*
     * Als gespeicherte Referenz, damit raeumeAuf() den Horcher wieder loesen
     * kann. Ohne das sammelten sich bei jedem Neuaufbau (neue Kacheln,
     * Variantenwechsel) Geister-Instanzen an derselben Galerie: ein Klick
     * oeffnete alle zugleich, die unsichtbaren Alten verbrauchten die
     * Zoom-Vorfuehrung samt Zaehler, und die sichtbare zeigte nichts.
     */
    this.beiKlickGebunden = this.beiKlick.bind(this);
    galerie.addEventListener('click', this.beiKlickGebunden, true);

    /*
     * Horizons eigene Grossansicht wird stillgelegt. Ein eigener Zuhoerer
     * genuegt dafuer nicht: Horizon horcht am Dokument in der Einfangphase,
     * also vor jedem Zuhoerer weiter unten im Baum. Stattdessen bekommt der
     * Knopf seine Anweisung abgenommen - dann findet Horizon nichts mehr.
     */
    for (const knopf of galerie.querySelectorAll('.product-media-container__zoom-button')) {
      knopf.removeAttribute('on:click');
    }
  }

  /**
   * Sammelt die Bilder der Galerie in Anzeige-Reihenfolge.
   *
   * @returns {HTMLImageElement[]}
   */
  sammleBilder() {
    /*
     * Die 3D-Vorschau der Kalkulationsvorlage (dp-3d-vorschau.js) zaehlt als
     * erstes Bild, sobald ein Modell geladen ist: ihre Bildflaeche traegt
     * data-dp-live. Sie wird spaeter nicht kopiert, sondern als lebendes
     * Element in ihre Zelle geholt (holeLive) und beim Schliessen
     * zurueckgegeben (gibLiveZurueck).
     */
    /*
     * Ebenso die Vorschau der Druckdatei (Transferdruck-Vorlage,
     * dp-transferdruck.js): auch sie zieht als lebendes Element in ihre
     * Zelle, damit man darin genauso anordnen kann wie in der Galerie.
     *
     * Beide zaehlen nur mit, solange sie etwas zu zeigen haben - die
     * Bedingung steht am Zettel --da. Ohne Datei und Format stuende sonst
     * bloss der Hinweis in der Grossansicht, der schon in der Galerie steht,
     * und seine Flaeche fing dort die senkrechte Rollgeste ab, ohne selbst
     * etwas zum Rollen zu haben.
     */
    /*
     * Und die Vorschau von SOLID (dp-solid.js): Ihr Vollbild-Zeichen oeffnet
     * sie hier. Die Marke dafuer bleibt in der Kachel stehen, waehrend die
     * Vorschau selbst umzieht.
     */
    const live = this.galerie.querySelector(
      '.dp-3d-galerie.dp-3d-galerie--da .product-media[data-dp-live], .dp-druck-galerie.dp-druck-galerie--da .product-media[data-dp-live], .dp-solid-galerie.dp-solid-galerie--da .dp-solid-galerie__live[data-dp-live]'
    );
    /*
     * Die wartende Kachel (Standbild mit Knopf "3D-Vorschau starten") ist nur
     * dabei, wenn die Grossansicht von ihr aus geoeffnet wurde (mit3dWartend) -
     * beim Blaettern wird sie uebersprungen (Jimmi, Oktober 2026: "es geht nur
     * durch die Produktbilder"). Laeuft die 3D-Vorschau, gehoert sie wieder
     * zur Reihe. Ihre Bilder (Titel- und Standbild) zaehlen nie als Fotos.
     */
    const laeuft3d = this.galerie.querySelector('.dp-modell3d.dp-modell3d--da .dp-modell3d__live[data-dp-live]');
    const wartet3d = this.galerie.querySelector('.dp-modell3d.dp-modell3d--wartet .dp-modell3d__live[data-dp-live]');
    /* Die 3D-Kachel ist in der Grossansicht ein normales Galeriebild - im Standbild wie in 3D (Jimmi, 22.10.2026): sie bleibt in der Reihe, man blaettert zu ihr zurueck, und der Zaehler zaehlt sie mit. */
    const modell3d = laeuft3d || wartet3d;
    const ohne3dBilder = !!(laeuft3d || wartet3d);
    const bilder = [...this.galerie.querySelectorAll(this.kachel + ' img')].filter(
      (bild) => {
        /*
         * Was der Kunde selbst hochgeladen hat, gehoert nicht in die
         * Bilderreihe: die Motive liegen in der Vorschau, und die ist
         * bereits als lebende Zelle dabei.
         */
        if (bild.closest('.dp-druck-galerie')) return false;
        // 3D-MODELL-FUNKTION: Steht die 3D-Ansicht, zaehlt die Kachel als lebende Zelle, nicht ihr Titelbild.
        if (ohne3dBilder && bild.closest('.dp-modell3d')) return false;
        const kachel = bild.closest('.product-media-container, ' + KACHEL_BEWERTUNG);
        return !(kachel instanceof HTMLElement) || kachel.style.display !== 'none';
      }
    );
    /*
     * 3D-MODELL-FUNKTION: Ein-/Ausbau hier. Die 3D-Kachel (dp-modell3d.js)
     * steht nicht vorn, sondern an ihrer Stelle in der Galerie - eingereiht
     * nach der Lage im Dokument. Ohne WebGL (keine Klasse --da) bleibt sie
     * ein gewoehnliches Bild: ihr Titelbild.
     */
    if (modell3d) {
      const nach = bilder.findIndex((bild) => !!(modell3d.compareDocumentPosition(bild) & Node.DOCUMENT_POSITION_FOLLOWING));
      bilder.splice(nach < 0 ? bilder.length : nach, 0, modell3d);
    }
    return live ? [live, ...bilder] : bilder;
  }

  /** Ist die gezeigte Zelle ein Video (einer Bewertung)? Dann kein Zoom. */
  istVideo() {
    const zelle = this.streifen && this.streifen.children[this.platz];
    return !!(zelle && zelle.classList.contains('dp-bildfeld__zelle--video'));
  }

  /*
   * Nur das gezeigte Video laeuft - mit dem Ton, den der Kunde in dieser
   * Ansicht gewaehlt hat (ein in REVIEW stummes immer ohne). Jedes andere
   * haelt an - beim Weiterblaettern und beim Schliessen.
   */
  pflegeVideos(schliessen) {
    if (!this.streifen) return;
    const aktiv = schliessen ? null : this.streifen.children[this.platz];
    for (const video of this.streifen.querySelectorAll('video.dp-bildfeld__video')) {
      if (video.closest('.dp-bildfeld__zelle') === aktiv) {
        video.muted = video.dataset.dpStumm !== undefined || !this.tonAn;
        // Mit Ton verweigert der Browser das Selbststarten manchmal - dann eben stumm.
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      } else if (!video.paused) video.pause();
    }
  }

  /** Spielt das gezeigte Video oder haelt es an (Tipp, Leertaste). */
  schalteVideo() {
    const video = this.streifen && this.streifen.children[this.platz]?.querySelector('video.dp-bildfeld__video');
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  /** Ist die Vorschau-Zelle die gerade gezeigte? */
  istLive() {
    const zelle = this.streifen && this.streifen.children[this.platz];
    return !!(zelle && zelle.classList.contains('dp-bildfeld__zelle--live'));
  }

  /**
   * Fuellt den Streifen aus der Bilderliste - beim Aufbau und wenn sich die
   * Liste beim Oeffnen geaendert hat (etwa weil inzwischen ein Modell
   * geladen ist). Bei mehr als einem Bild kommen Klone des letzten und des
   * ersten an die Enden, fuer den Umlauf; die Klone tragen eine Marke, damit
   * die lebende Vorschau nie in einen Klon geholt wird.
   */
  fuelleStreifen() {
    // Die alten Zellen gehen - ihre Bilder vorher frei.
    this.gibBilderAb();
    this.streifen.replaceChildren();
    for (const bild of this.bilder) {
      this.streifen.appendChild(this.macheZelle(bild));
    }
    this.hatKlone = this.bilder.length > 1;
    if (this.hatKlone) {
      const vorne = this.streifen.lastElementChild.cloneNode(true);
      const hinten = this.streifen.firstElementChild.cloneNode(true);
      vorne.classList.add('dp-bildfeld__klon');
      hinten.classList.add('dp-bildfeld__klon');
      this.streifen.insertBefore(vorne, this.streifen.firstElementChild);
      this.streifen.appendChild(hinten);
    }
  }

  /** Bilderliste neu erheben; bei Abweichung den Streifen neu fuellen. */
  gleicheBilderAb() {
    const neue = this.sammleBilder();
    const gleich = neue.length === this.bilder.length && neue.every((bild, i) => bild === this.bilder[i]);
    if (gleich) return;
    /*
     * Der Streifen wird gleich neu gefuellt, und dabei fallen alle Zellen
     * weg - mit ihnen die lebende Vorschau, die in einer davon steht. Sie
     * geht deshalb vorher nach Hause und kommt danach zurueck. Ohne das
     * flackerte die Ansicht bei einem Formatwechsel kurz dunkel auf, weil
     * ihre Zelle einen Augenblick leer war.
     */
    const offen = !!(this.feld && !this.feld.hidden);
    this.gibLiveZurueck();
    this.bilder = neue;
    this.fuelleStreifen();
    if (offen) this.holeLive();
  }

  /**
   * Die Galerie hat gefiltert, ohne neu gebaut zu werden - die Vorschaufarbe
   * blendet Vorschaubilder ein oder aus (dp-galeriewechsel.js). Die offene
   * Ansicht zieht ihre Liste nach und bleibt dabei auf ihrem Bild; zum neuen
   * Bild blaettert danach das Kauffenster (dp-veredelung.js, zeigeAnsicht).
   * Bisher kannte sie das Vorschaubild nicht (Jimmi, September 2026).
   */
  /**
   * Die Galerie wurde ersetzt (Variantenwechsel), waehrend die Ansicht offen
   * ist: die neue Galerie uebernehmen und nur die Bilder tauschen - am
   * entsprechenden Bild der neuen Farbe. Das bisher gezeigte Bild liegt als
   * Abbild darueber und blendet weich aus; die Ansicht selbst blendet nie.
   * Steht die 3D-Ansicht offen, gibt es kein Abbild - ihre Buehne bleibt.
   *
   * @param {HTMLElement} galerie
   */
  tauscheGalerie(galerie) {
    const jetzt = this.bilder[this.index];
    const vorher = this.index;
    let abbild = null;
    const zelle = this.streifen && this.streifen.children[this.platz];
    if (this.buehne && zelle instanceof HTMLElement && !zelle.querySelector('.dp-modell3d__buehne, [data-dp-live]')) {
      abbild = /** @type {HTMLElement} */ (zelle.cloneNode(true));
      abbild.removeAttribute('id');
      for (const el of abbild.querySelectorAll('[id]')) el.removeAttribute('id');
      Object.assign(abbild.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', zIndex: '3', pointerEvents: 'none', transform: 'none' });
      abbild.setAttribute('aria-hidden', 'true');
      this.buehne.appendChild(abbild);
    }
    this.uebernimmGalerie(galerie);
    this.gleicheBilderAb();
    this.gehe(this.entsprechung(jetzt, vorher), true);
    this.meldeBildwechsel();
    if (!abbild) return;
    const weg = () => abbild.remove();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof abbild.animate !== 'function') {
      weg();
      return;
    }
    /* Erst ausblenden, wenn das neue Bild steht - hoechstens eine Sekunde warten. */
    const neu = this.streifen && this.streifen.children[this.platz];
    const bild = neu instanceof HTMLElement ? neu.querySelector('img') : null;
    const blende = () => abbild.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 320, easing: 'ease-out', fill: 'forwards' }).finished.then(weg, weg);
    if (bild instanceof HTMLImageElement && !bild.complete) {
      const uhr = window.setTimeout(blende, 1000);
      bild.addEventListener('load', () => { window.clearTimeout(uhr); blende(); }, { once: true });
      bild.addEventListener('error', () => { window.clearTimeout(uhr); blende(); }, { once: true });
    } else blende();
  }

  folgeFilter() {
    if (!this.feld || this.feld.hidden || !this.streifen) return;
    const neue = this.sammleBilder();
    const gleich = neue.length === this.bilder.length && neue.every((bild, i) => bild === this.bilder[i]);
    if (gleich) return;
    const jetzt = this.bilder[this.index];
    const vorher = this.index;
    this.gleicheBilderAb();
    // Dasselbe Bild - oder sein Gegenstueck der neuen Farbe (entsprechung), nicht das erste.
    this.gehe(this.entsprechung(jetzt, vorher), true);
    this.meldeBildwechsel();
  }

  /**
   * Woran ein Bild ueber einen Farbwechsel hinweg wiedererkannt wird: die
   * Ansicht von IMPRINT, die es zeigt (die Vorschau - je Farbe ein eigenes
   * Foto, aber dieselbe Ansicht), die 3D-Kachel, sonst das Medium des Shops.
   * Die Ansicht nennt dp-veredelung.js (dpImprintAnsichtVon) - auch fuer ein
   * Bild, das noch nicht gezeichnet wurde.
   *
   * @param {Element | undefined} bild
   */
  kennungVon(bild) {
    if (!(bild instanceof Element)) return '';
    let ansicht = bild instanceof HTMLElement ? bild.dataset.dpLiveAnsicht || '' : '';
    if (!ansicht && typeof window.dpImprintAnsichtVon === 'function') {
      try {
        ansicht = window.dpImprintAnsichtVon(bild) || '';
      } catch {
        ansicht = '';
      }
    }
    if (ansicht) return 'ansicht:' + ansicht;
    if (bild.closest('.dp-modell3d, .dp-bildfeld__zelle--modell3d, .dp-3d-galerie')) return 'modell3d';
    const medium = bild.closest('[data-media-id]');
    if (medium) return 'medium:' + medium.getAttribute('data-media-id');
    return 'quelle:' + String(bild.currentSrc || bild.src || '').split('?')[0];
  }

  /**
   * Die Stelle in der (neuen) Bilderliste, die einem bisher gezeigten Bild
   * entspricht (Jimmi, Oktober 2026: beim Farbwechsel wischte die
   * Grossansicht zum ersten Bild statt zur Vorschau). Dasselbe Bild, sonst
   * das mit derselben Kennung (Vorschau der neuen Farbe, 3D-Kachel), sonst
   * derselbe Platz - der Kunde bleibt, wo er war; nie stillschweigend das
   * erste Bild.
   *
   * @param {Element | undefined} alt
   * @param {number} altIndex
   */
  entsprechung(alt, altIndex) {
    if (!this.bilder.length) return 0;
    let index = alt ? this.bilder.indexOf(alt) : -1;
    const kennung = this.kennungVon(alt);
    if (index < 0 && kennung) index = this.bilder.findIndex((bild) => this.kennungVon(bild) === kennung);
    if (index < 0) index = Math.min(Math.max(0, Number(altIndex) || 0), this.bilder.length - 1);
    return index;
  }

  /**
   * Der Kunde blaettert selbst (Wischen, Pfeile, Tasten): Das ist seine Wahl - bis er
   * das naechste Feld bearbeitet, springt die Ansicht nicht von sich aus zur
   * Personalisierung (dp-veredelung.js hoert auf dp:grossansicht-geblaettert).
   */
  geheVomKunden(index) {
    this.gehe(index);
    document.dispatchEvent(new CustomEvent('dp:grossansicht-geblaettert', { detail: { bildfeld: this } }));
  }

  /** Sagt der Seite, dass die Ansicht nach einem Wechsel ein Bild zeigt - dp-veredelung.js richtet dann den bearbeiteten Bereich aus. */
  meldeBildwechsel() {
    window.setTimeout(() => document.dispatchEvent(new CustomEvent('dp:grossansicht-bild', { detail: { bildfeld: this } })), 0);
  }

  /** Holt die lebende 3D-Vorschau in ihre Zelle. */
  holeLive() {
    const halter = this.streifen.querySelector('.dp-bildfeld__zelle--live:not(.dp-bildfeld__klon) .dp-bildfeld__live');
    if (!halter) return;
    // 3D-MODELL-FUNKTION: Ein-/Ausbau hier - die Buehne der 3D-Kachel zieht in ihre Zelle und darf dort zoomen.
    if (halter.closest('.dp-bildfeld__zelle--modell3d')) {
      const buehne = this.galerie.querySelector('.dp-modell3d .dp-modell3d__buehne') || document.querySelector('.dp-modell3d__buehne');
      if (buehne && buehne.parentElement !== halter) {
        halter.appendChild(buehne);
        document.dispatchEvent(new CustomEvent('dp:modell3d-ort', { detail: { gross: true } }));
      }
      return;
    }
    const vorschau = document.querySelector('.dp-3d-galerie .fq-preview');
    if (vorschau) {
      if (vorschau.parentElement !== halter) halter.appendChild(vorschau);
      return;
    }
    // Die Vorschau von SOLID zieht samt Werkzeugleiste um; ihre Buehne passt sich selbst an.
    const solid = document.querySelector('.dp-solid-galerie__falte > .dp-vorschau');
    if (solid && solid.parentElement !== halter) {
      // Die Kachel behaelt ihre Hoehe, solange die Vorschau hier ist - die Galerie darunter bleibt stehen.
      const falte = solid.parentElement;
      if (falte instanceof HTMLElement) falte.style.height = falte.getBoundingClientRect().height + 'px';
      halter.appendChild(solid);
      /* Die Vorschau von SOLID steht jetzt gross da: das Kauffenster macht ihr am Handy Platz (dp-solid.js, divineprint.css). */
      document.dispatchEvent(new CustomEvent('dp:solid-vorschau-ort', { detail: { gross: true } }));
      return;
    }
    // Die Druckvorschau zieht als Ganzes um und rechnet sich auf die neue Groesse.
    const druck = document.querySelector('.dp-druck-galerie__flaeche');
    if (druck && druck.parentElement !== halter) {
      halter.appendChild(druck);
      /*
       * Die Zelle hat ihre Groesse erst, wenn die Ansicht aufgegangen ist -
       * einmal sofort und dann noch ein paar Mal, bis die Bewegung steht.
       */
      for (const wann of [0, 120, 400, 900]) window.setTimeout(() => window.dispatchEvent(new Event('resize')), wann);
    }
  }

  /** Gibt die Vorschau an ihre Kachel in der Galerie zurueck. */
  gibLiveZurueck() {
    // 3D-MODELL-FUNKTION: Ein-/Ausbau hier - die Buehne geht in die 3D-Kachel der Galerie zurueck.
    const buehne3d = this.feld && this.feld.querySelector('.dp-bildfeld__live > .dp-modell3d__buehne');
    const heim3d = this.galerie.querySelector('.dp-modell3d .dp-modell3d__live') || document.querySelector('.dp-modell3d .dp-modell3d__live');
    if (buehne3d && heim3d) {
      heim3d.appendChild(buehne3d);
      document.dispatchEvent(new CustomEvent('dp:modell3d-ort', { detail: { gross: false } }));
    }

    const vorschau = this.feld && this.feld.querySelector('.dp-bildfeld__live .fq-preview');
    const heim = document.querySelector('.dp-3d-galerie .product-media');
    if (vorschau && heim) heim.appendChild(vorschau);

    const solid = this.feld && this.feld.querySelector('.dp-bildfeld__live > .dp-vorschau');
    const solidHeim = document.querySelector('.dp-solid-galerie__falte');
    if (solid && solidHeim instanceof HTMLElement) {
      solidHeim.appendChild(solid);
      solidHeim.style.removeProperty('height');
      document.dispatchEvent(new CustomEvent('dp:solid-vorschau-ort', { detail: { gross: false } }));
    }

    const druck = this.feld && this.feld.querySelector('.dp-bildfeld__live .dp-druck-galerie__flaeche');
    const druckHeim = document.querySelector('.dp-druck-galerie__traeger');
    if (druck && druckHeim) {
      druckHeim.appendChild(druck);
      /*
       * Die Zelle hat ihre Groesse erst, wenn die Ansicht aufgegangen ist -
       * einmal sofort und dann noch ein paar Mal, bis die Bewegung steht.
       */
      for (const wann of [0, 120, 400, 900]) window.setTimeout(() => window.dispatchEvent(new Event('resize')), wann);
    }
  }

  /**
   * Gleitet zur lebenden 3D-Vorschau, wenn die Grossansicht gerade offen ist
   * - eine neue Datei bei offener Ansicht (dp-3d-vorschau.js): das neue
   * Modell soll zu sehen sein, nicht ein Produktfoto. Beim allerersten
   * Modell ist die Vorschau-Zelle neu; die Bilderliste wird dafuer neu
   * erhoben und der Streifen neu gefuellt.
   */
  gleiteZurLive() {
    if (!this.feld || !this.feld.classList.contains('dp-bildfeld--offen')) return;
    this.gleicheBilderAb();
    const index = this.bilder.findIndex((bild) => bild instanceof HTMLElement && bild.dataset.dpLive !== undefined);
    if (index < 0) return;
    this.holeLive();
    if (this.index !== index) this.gehe(index, false);
  }

  /**
   * Die Rollgeste auf den Randbereichen der Vorschau-Zelle - dieselbe wie an
   * der Kachel (dp-3d-vorschau.js, dp-3d-galerie--zeigtRand), nur waagerecht:
   * nach 800 ms werden die Streifen breit, dann leuchten sie auf und der
   * Finger laeuft nach links und zurueck. Nur auf dem Handy, hoechstens
   * zweimal je Besuch und je Artikel nur einmal.
   */
  zeigeRandGeste() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Je Artikel nur einmal: wer zu den Fotos blaettert und zurueckkommt, kennt die Geste schon.
    if (this.randGesteGezeigt) return;
    const zelle = this.streifen.querySelector('.dp-bildfeld__zelle--live:not(.dp-bildfeld__klon)');
    if (!(zelle instanceof HTMLElement)) return;
    /* Die Wischgeste der Randstreifen zeigt nur die laufende 3D-Vorschau, nie das Standbild (Jimmi, 22.10.2026) - und zaehlt dort auch nicht mit. */
    if (zelle.classList.contains('dp-bildfeld__zelle--modell3d') && !document.documentElement.classList.contains('dp-modell3d-laeuft')) return;
    if (!darfHinweisZeigen('dp-hinweis-3d-rand-gross')) return;
    this.randGesteGezeigt = true;
    /*
     * Hier laeuft der Finger waagerecht - gewischt wird nach links und
     * rechts. Dafuer werden die Streifen fuer die Dauer der Geste so breit,
     * wie der Weg des Fingers braucht (dp-bildfeld__zelle--breit, 400 ms),
     * dann laeuft die Geste (2,2 s), danach laufen sie in ihre Breite zurueck.
     */
    window.setTimeout(() => zelle.classList.add('dp-bildfeld__zelle--breit'), 800);
    window.setTimeout(() => zelle.classList.add('dp-bildfeld__zelle--zeigtRand'), 800 + 400);
    window.setTimeout(() => {
      zelle.classList.remove('dp-bildfeld__zelle--zeigtRand');
      zelle.classList.remove('dp-bildfeld__zelle--breit');
    }, 800 + 400 + 2200);
  }

  /** Oeffnet die Grossansicht auf der 3D-Vorschau (Knopf an der Kachel). */
  oeffneLive() {
    if (!this.feld) return;
    this.gleicheBilderAb();
    const index = this.bilder.findIndex((bild) => bild instanceof HTMLElement && bild.dataset.dpLive !== undefined);
    this.oeffne(Math.max(0, index));
  }

  baue() {
    const feld = document.createElement('div');
    feld.className = 'dp-bildfeld';
    feld.hidden = true;

    const teile = [
      '<div class="dp-bildfeld__kulisse" data-dp-zu><div class="dp-bildfeld__loch" hidden></div></div>',
      '<div class="dp-bildfeld__rahmen">',
      '<button type="button" class="dp-bildfeld__knopf dp-bildfeld__zu" data-dp-zu aria-label="Schließen">',
      '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
      '</button>',
      '<div class="dp-bildfeld__buehne" data-dp-buehne>',
      '<div class="dp-bildfeld__streifen" data-dp-streifen></div>',
      // Die Pillen des gezeigten Bildes - fest ueber dem Streifen, sie wischen nicht mit (pflegeMarken).
      '<span class="dp-kachel-marken dp-bildfeld__marken dp-bildfeld__marken--weg" aria-hidden="true" data-dp-marken></span>',
      // Zwei Punkte, die einmal auseinandergehen: der Hinweis, dass sich das
      // Bild mit zwei Fingern vergroessern laesst.
      '<span class="dp-bildfeld__geste" aria-hidden="true">',
      '<span class="dp-bildfeld__finger"></span>',
      '<span class="dp-bildfeld__finger"></span>',
      '</span>',
      // Eine Maus mit rollendem Rad: dasselbe fuer den Rechner - dort wird
      // mit dem Rad gezoomt.
      '<span class="dp-bildfeld__radhinweis" aria-hidden="true">',
      '<span class="dp-bildfeld__maus"><span class="dp-bildfeld__rad"></span></span>',
      '</span>',
      '</div>',
      '<button type="button" class="dp-bildfeld__knopf dp-bildfeld__pfeil dp-bildfeld__pfeil--zurueck" data-dp-zurueck aria-label="Vorheriges Bild">',
      '<svg viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="M6.5 1L1.5 6l5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      '</button>',
      '<button type="button" class="dp-bildfeld__knopf dp-bildfeld__pfeil dp-bildfeld__pfeil--weiter" data-dp-weiter aria-label="Nächstes Bild">',
      '<svg viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="M1.5 1l5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      '</button>',
      '<div class="dp-bildfeld__lupe">',
      '<button type="button" class="dp-bildfeld__knopf" data-dp-raus aria-label="Verkleinern"><svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1.5 7h11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>',
      '<button type="button" class="dp-bildfeld__knopf" data-dp-rein aria-label="Vergrößern"><svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1.5v11M1.5 7h11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>',
      '</div>',
      '<span class="dp-bildfeld__zaehler" data-dp-zaehler></span>',
      '</div>',
    ];
    /* Chat: ein Knopf zum Herunterladen des gezeigten Bildes, in der Gruppe der Lupen-Knöpfe (Jimmi, Oktober 2026). */
    if (this.istChat) {
      const i = teile.findIndex((t) => t.includes('data-dp-rein'));
      teile.splice(
        i + 1,
        0,
        '<button type="button" class="dp-bildfeld__knopf" data-dp-laden aria-label="Bild herunterladen"><svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1.5v7.2M3.8 5.8L7 9l3.2-3.2M2 12.2h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>'
      );
    }
    feld.innerHTML = teile.join('');

    const streifen = feld.querySelector('[data-dp-streifen]');
    this.streifen = streifen;
    // Zellen und Klone: siehe fuelleStreifen - dort auch die Kopien fuer den Umlauf.
    this.fuelleStreifen();

    document.body.appendChild(feld);
    this.feld = feld;

    /*
     * Das Kauffenster waechst und schrumpft, wenn die Beschreibung auf- und
     * zugeklappt wird. Die Unterkante der Grossansicht haengt daran, also wird
     * die Hoehe verfolgt statt nur beim Oeffnen gemessen.
     */
    if ('ResizeObserver' in window) {
      this.beobachter = new ResizeObserver(() => this.miss());
      this.beobachter.observe(this.galerie);
      const kauf = document.querySelector('.dp-kauffenster');
      if (kauf) this.beobachter.observe(kauf);
    }

    // Als benannte Funktion, damit sie beim Abraeumen wieder geloest werden kann.
    /*
     * Auf dem Telefon aendert die Tastatur nicht das Fenster, sondern den
     * sichtbaren Ausschnitt. Wer nur auf resize hoert, merkt davon nichts.
     */
    this.beiGroesse = () => this.miss();
    window.addEventListener('resize', this.beiGroesse, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.beiGroesse, { passive: true });
      window.visualViewport.addEventListener('scroll', this.beiGroesse, { passive: true });
    }

    /*
     * Bei offener Tastatur steht unten nicht mehr das Kauffenster, sondern nur
     * noch die eine gehobene Zeile; sie meldet ihre Oberkante als
     * --dp-tastaturband. Von allein merkt die Ansicht davon nichts: Das
     * Kauffenster behaelt dabei seine Groesse und wird nur unsichtbar, der
     * Groessenbeobachter darauf schweigt also. Die Unterkante des Rahmens
     * rueckt zwar ueber das Stilblatt mit, die Bildgroesse setzt aber dieses
     * Skript - ohne die Nachricht behielt das Bild sein altes Mass und stand
     * unten abgeschnitten im kleiner gewordenen Rahmen.
     */
    this.beiBand = () => this.miss();
    document.addEventListener('dp:tastaturband', this.beiBand);
    this.streifen = streifen;
    this.buehne = feld.querySelector('[data-dp-buehne]');
    this.zaehler = feld.querySelector('[data-dp-zaehler]');
    this.marken = feld.querySelector('[data-dp-marken]');
    this.markenStand = '';
    this.markenUhr = 0;

    /*
     * IMPRINT traegt die Pille "Vorschau" an den Kopien erst nach, wenn es
     * sie erkannt hat (dp-veredelung.js, markiereKopie) - dann auch hier.
     */
    this.beiPillenNeu = () => this.pflegeMarken();
    document.addEventListener('dp:pillen-neu', this.beiPillenNeu);
    /*
     * "Standbild" in der Grossansicht: Die 3D-Zelle wartet wieder und bleibt
     * stehen, solange sie gezeigt wird - der Zaehler zaehlt wieder ohne sie.
     */
    document.addEventListener('dp:modell3d-beendet', () => {
      if (!this.streifen || !this.feld || this.feld.hidden) return;
      const zelle = this.streifen.children[this.platz];
      if (zelle && zelle.classList.contains('dp-bildfeld__zelle--modell3d')) this.mit3dWartend = true;
      if (this.zaehler) rolleZaehler(this.zaehler, this.zaehlerText(), 'hoch', false);
      this.pflegeMarken();
    });
    document.addEventListener('dp:modell3d-gestartet', () => {
      if (!this.streifen || !this.feld || this.feld.hidden) return;
      if (this.zaehler) rolleZaehler(this.zaehler, this.zaehlerText(), 'hoch', false);
      this.pflegeMarken();
      /* Ist die 3D-Vorschau in der Grossansicht gestartet, darf die Wischgeste jetzt kommen (nach den bisherigen Regeln). */
      if (this.istLive()) this.zeigeRandGeste();
    });

    feld.addEventListener('click', (ereignis) => {
      const ziel = ereignis.target;
      if (!(ziel instanceof Element)) return;
      if (ziel.closest('[data-dp-zu]')) this.schliesse();
      if (ziel.closest('[data-dp-rein]')) this.setzeZoom(this.zoom * 1.5);
      if (ziel.closest('[data-dp-raus]')) this.setzeZoom(this.zoom / 1.5);
      if (ziel.closest('[data-dp-laden]')) this.ladeHerunter();
      if (ziel.closest('[data-dp-zurueck]')) this.geheVomKunden(this.index - 1);
      if (ziel.closest('[data-dp-weiter]')) this.geheVomKunden(this.index + 1);
    });

    this.beiTaste = (ereignis) => {
      // Escape schliesst der gemeinsame Fensterbaustein (dp-fenster-fokus.js): nur das oberste Fenster.
      // Die Leertaste spielt ein Video oder haelt es an.
      if (ereignis.key === ' ' && this.istVideo()) {
        ereignis.preventDefault();
        this.schalteVideo();
        return;
      }
      /*
       * Die Pfeiltasten schieben auf dem Druckbogen das gewaehlte Motiv
       * pixelgenau (dp-transferdruck.js) - dann wechseln sie hier kein Bild.
       * Der Bogen verbraucht die Taste vorher (preventDefault); zur Sicherheit
       * zaehlt auch ein gewaehltes Motiv im Overlay.
       */
      const pfeil = ereignis.key === 'ArrowRight' || ereignis.key === 'ArrowLeft';
      /* Solange das Anmalen offen ist, blaettern die Pfeiltasten die Galerie nicht (Jimmi, Oktober 2026) - die Tasten gehoeren dem Anmalen. */
      if (pfeil && document.documentElement.dataset.dpBildeditor === 'offen') return;
      /* Auch bei offenem Schnitt der 3D-Vorschau wechseln die Pfeiltasten kein Bild (Jimmi, Oktober 2026): Regler und Schnitt gehoeren ihnen. */
      if (pfeil && document.documentElement.dataset.dpSchnitt === 'an') return;
      if (pfeil && (ereignis.defaultPrevented || (this.feld && this.feld.querySelector('.dp-druck-motiv--aktiv')))) return;
      if (ereignis.key === 'ArrowRight') this.geheVomKunden(this.index + 1);
      if (ereignis.key === 'ArrowLeft') this.geheVomKunden(this.index - 1);
    };

    /*
     * Der Zoom mit zwei Fingern laeuft ueber die Beruehrungsereignisse und
     * nicht ueber die Zeigerereignisse.
     *
     * Zeigerereignisse sind bei zwei Fingern unzuverlaessig: Sobald der Browser
     * die Geste als Seitenzoom deutet, bricht er sie ab (pointercancel), und
     * unsere Rechnung bekommt nur den Anfang mit. Die Beruehrungsereignisse
     * melden dagegen alle Finger in einem Ereignis, und preventDefault haelt
     * den Seitenzoom sicher zurueck.
     */
    this.buehne.addEventListener('touchstart', this.beiGriffAn.bind(this), { passive: false });
    this.buehne.addEventListener('touchmove', this.beiGriffBewegt.bind(this), { passive: false });
    for (const art of ['touchend', 'touchcancel']) {
      this.buehne.addEventListener(art, this.beiGriffAus.bind(this));
    }

    this.buehne.addEventListener('wheel', this.beiRad.bind(this), { passive: false });
    this.buehne.addEventListener('click', (ereignis) => {
      if (!this.istVideo() || !this.videoTipp) return;
      if (ereignis.target instanceof Element && ereignis.target.closest('.dp-bildfeld__ton')) return;
      const weg = Math.hypot(ereignis.clientX - this.videoTipp.x, ereignis.clientY - this.videoTipp.y);
      this.videoTipp = null;
      if (weg > 10) return;
      this.schalteVideo();
    });
    /*
     * Am Rechner meldet der Browser den Doppelklick selbst - der Weg ueber
     * zwei Zeigerereignisse waere dort doppelt gemoppelt.
     */
    this.buehne.addEventListener('dblclick', (ereignis) => {
      if (this.istLive() || this.istVideo()) return;
      ereignis.preventDefault();
      /*
       * Am Telefon meldet der Browser nach zwei Tipps beides: die
       * Zeigerereignisse UND einen Doppelklick. Beide zoomten - der eine
       * herein, der andere sofort wieder heraus, und sichtbar geschah nichts.
       * Hat der Tippweg eben gehandelt, ist hier nichts mehr zu tun.
       */
      if (performance.now() - this.zuletztGedoppelt < DOPPEL_SPERRE) return;
      this.doppelzoom(ereignis.clientX, ereignis.clientY);
    });

    this.buehne.addEventListener('pointerdown', this.beiZeigerAn.bind(this));
    this.buehne.addEventListener('pointermove', this.beiZeigerBewegt.bind(this));
    for (const art of ['pointerup', 'pointercancel', 'pointerleave']) {
      this.buehne.addEventListener(art, this.beiZeigerAus.bind(this));
    }
    /*
     * Zeiger (Jimmi, Oktober 2026: "die Hand nur, wenn er die Ansicht bewegt"): im Ruhezustand der
     * normale Mauszeiger, die geschlossene Hand nur waehrend des Ziehens. Aufgeraeumt wird bei jedem
     * Ende - auch Abbruch, Verlassen des Fensters und verlorenem Zeiger.
     */
    const greiftAus = () => this.feld && this.feld.classList.remove('dp-bildfeld--greift');
    this.buehne.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button === 0 && this.feld) this.feld.classList.add('dp-bildfeld--greift');
    });
    for (const art of ['pointerup', 'pointercancel', 'lostpointercapture']) this.buehne.addEventListener(art, greiftAus);
    window.addEventListener('pointerup', greiftAus);
    window.addEventListener('blur', greiftAus);
  }

  /**
   * Baut die Streifenzelle zu einem Galeriebild.
   *
   * @param {HTMLImageElement} bild
   * @returns {HTMLElement}
   */
  macheZelle(bild) {
    const zelle = document.createElement('div');
    zelle.className = 'dp-bildfeld__zelle';

    // Die Zelle der 3D-Vorschau: ein leerer Halter, in den die lebende Vorschau beim Oeffnen kommt.
    if (bild instanceof HTMLElement && bild.dataset.dpLive !== undefined) {
      zelle.classList.add('dp-bildfeld__zelle--live');
      /*
       * 3D-MODELL-FUNKTION: Ein-/Ausbau hier. Die Zelle der 3D-Kachel: ein
       * Halter fuer die Buehne (holeLive) und die beiden Randstreifen zum
       * Blaettern auf dem Handy - auf der Flaeche selbst dreht der Finger.
       * Keine Pille.
       */
      if (bild.closest('.dp-modell3d')) {
        zelle.classList.add('dp-bildfeld__zelle--modell3d');
        const halter = document.createElement('div');
        halter.className = 'dp-bildfeld__live';
        zelle.appendChild(halter);
        for (const seite of ['links', 'rechts']) {
          const rand = document.createElement('div');
          rand.className = 'dp-3d-galerie__rand dp-3d-galerie__rand--' + seite;
          rand.setAttribute('aria-hidden', 'true');
          if (seite === 'rechts') {
            const finger = document.createElement('span');
            finger.className = 'dp-3d-galerie__finger';
            rand.appendChild(finger);
          }
          zelle.appendChild(rand);
        }
        return zelle;
      }
      /*
       * Die Druckvorschau bringt Hinweis, Knoepfe und Legende selbst mit -
       * ihre Zelle ist nur der Halter, in den sie beim Oeffnen einzieht.
       */
      if (bild.closest('.dp-druck-galerie, .dp-solid-galerie')) {
        zelle.classList.add(bild.closest('.dp-solid-galerie') ? 'dp-bildfeld__zelle--solid' : 'dp-bildfeld__zelle--druck');
        const eigen = document.createElement('div');
        eigen.className = 'dp-bildfeld__live';
        zelle.appendChild(eigen);
        /*
         * Und die beiden Randstreifen, genau wie bei der 3D-Vorschau: auf
         * ihnen blaettert ein Wisch durch die Bilder, waehrend der Finger
         * auf der Flaeche selbst die Motive anordnet.
         */
        for (const seite of ['links', 'rechts']) {
          const rand = document.createElement('div');
          rand.className = 'dp-3d-galerie__rand dp-3d-galerie__rand--' + seite;
          rand.setAttribute('aria-hidden', 'true');
          if (seite === 'rechts') {
            const finger = document.createElement('span');
            finger.className = 'dp-3d-galerie__finger';
            rand.appendChild(finger);
          }
          zelle.appendChild(rand);
        }
        /*
         * Die Pille "Vorschau" fuer den festen Pillenplatz (pflegeMarken) -
         * wie an der Zelle der 3D-Vorschau. Die Pille in der Druckflaeche
         * selbst steht in der Grossansicht nicht; ohne diese hier fehlte sie
         * dort (Jimmi, September 2026). SOLID bringt seine eigene mit.
         */
        if (!bild.closest('.dp-solid-galerie')) {
          const marken = document.createElement('span');
          marken.className = 'dp-kachel-marken';
          marken.setAttribute('aria-hidden', 'true');
          const marke = document.createElement('span');
          marke.className = 'dp-kachel-marke dp-kachel-marke--live';
          marke.textContent = vorschauWortJetzt();
          marken.appendChild(marke);
          zelle.appendChild(marken);
        }
        return zelle;
      }
      if (!document.querySelector('.dp-3d-galerie--da')) zelle.classList.add('dp-bildfeld__zelle--leer');
      const halter = document.createElement('div');
      halter.className = 'dp-bildfeld__live';
      zelle.appendChild(halter);
      /*
       * Die Randbereiche der Kachel (dp-3d-vorschau.js), 1:1: links und
       * rechts ein Streifen, im rechten der Finger der Rollgeste. Auf dem
       * Handy blaettert ein Wischen darauf durch die Galerie - auf dem
       * Canvas selbst dreht der Finger das Modell.
       */
      for (const seite of ['links', 'rechts']) {
        const rand = document.createElement('div');
        rand.className = 'dp-3d-galerie__rand dp-3d-galerie__rand--' + seite;
        rand.setAttribute('aria-hidden', 'true');
        if (seite === 'rechts') {
          const finger = document.createElement('span');
          finger.className = 'dp-3d-galerie__finger';
          rand.appendChild(finger);
        }
        zelle.appendChild(rand);
      }
      /*
       * Ohne Modell zeigt die Zelle denselben Hinweis wie die Kachel (Kopie
       * ihres Hinweises, dp-3d-vorschau.js) statt der leeren Vorschau der
       * App; ein Tipp darauf oeffnet die Dateiwahl. Ob ein Modell da ist,
       * sagt dp-3d-vorschau.js an der Zelle (dp-bildfeld__zelle--leer).
       */
      const vorlage = document.querySelector('.dp-3d-galerie__hinweis');
      if (vorlage) {
        const hinweis = vorlage.cloneNode(true);
        // Nur ein Tipp ohne Bewegung - nach einem Wischen ueber den Hinweis bleibt die Dateiwahl zu.
        let anfang = null;
        hinweis.addEventListener('pointerdown', (ereignis) => {
          anfang = { x: ereignis.clientX, y: ereignis.clientY };
        });
        hinweis.addEventListener('click', (ereignis) => {
          if (anfang && Math.hypot(ereignis.clientX - anfang.x, ereignis.clientY - anfang.y) > 8) return;
          const ablage = document.querySelector('#print-quote-widget-root .fq-dropzone input[type="file"]');
          if (ablage instanceof HTMLInputElement) ablage.click();
        });
        zelle.appendChild(hinweis);
      }
      const marken = document.createElement('span');
      marken.className = 'dp-kachel-marken';
      marken.setAttribute('aria-hidden', 'true');
      const marke = document.createElement('span');
      marke.className = 'dp-kachel-marke dp-kachel-marke--live';
      marke.textContent = vorschauWortJetzt();
      marken.appendChild(marke);
      zelle.appendChild(marken);
      return zelle;
    }
    const kopie = document.createElement('img');

    /*
     * Bewusst eine feste, grosse Fassung statt srcset.
     *
     * Die Kopien entstehen, waehrend die Ansicht noch verborgen ist - der
     * Browser misst dort eine Breite von null und waehlt aus einem srcset die
     * kleinste Fassung. Spaeter, wenn die Ansicht aufgeht, waehlt er nicht neu:
     * das Bild bliebe winzig. Genau das war auf dem Handy zu sehen.
     */
    /*
     * Chat-Bilder liegen beim Chat-Dienst (beliebige Groesse, kein Bild-CDN mit Groessenparameter):
     * die Adresse bleibt, wie sie ist - kein angehaengtes width.
     */
    const grossAdresse = this.istChat ? bild.currentSrc || bild.src : grosseFassung(bild.currentSrc || bild.src);
    // Erst geladen, wenn die Zelle gezeigt wird (ladeUmgebung) - lebende Vorschauen (blob:) sofort.
    kopie.dataset.dpSpaet = grossAdresse;
    if (/^(blob|data):/.test(grossAdresse)) kopie.src = grossAdresse;

    /*
     * Die echten Masse des Bildes - sie stehen am Quellbild der Galerie
     * laengst fest, waehrend die Kopie noch laedt. Ohne sie musste der
     * Ausgangsmassstab aus der gelegten Groesse geraten werden, und die ist
     * im Moment des Oeffnens noch nicht die endgueltige: das Bild fuhr sicht-
     * bar zu weit heran und sprang gleich darauf zurueck.
     */
    if (bild.naturalWidth && bild.naturalHeight) {
      kopie.dataset.dpNatur = bild.naturalWidth + 'x' + bild.naturalHeight;
    } else if (bild instanceof HTMLImageElement) {
      /* Das Quellbild laedt noch - dann eben, sobald es da ist. */
      bild.addEventListener(
        'load',
        () => {
          if (!bild.naturalWidth || !bild.naturalHeight) return;
          kopie.dataset.dpNatur = bild.naturalWidth + 'x' + bild.naturalHeight;
        },
        { once: true }
      );
    }
    kopie.alt = '';
    kopie.draggable = false;
    kopie.loading = 'lazy';

    /*
     * Der Fokuspunkt des Bildes (die Galerie traegt ihn an der Kachel)
     * wandert an die Kopie: muss das Bild herangezogen werden, um die
     * Flaeche zu fuellen, rueckt die Ansicht den Fokus in die Mitte statt
     * stumpf die Bildmitte (fokusVersatz).
     */
    const fokus = bild.closest('[data-focal-point]');
    if (fokus instanceof HTMLElement && fokus.dataset.focalPoint) {
      kopie.dataset.dpFokus = fokus.dataset.focalPoint;
    } else {
      // Kein Merkmal an der Kachel: dann verraet die Lage des Bildes selbst den Fokus (object-position).
      const lage = (bild.style.objectPosition || getComputedStyle(bild).objectPosition || '').trim();
      if (lage && !/^50% 50%$/.test(lage) && !/^center( center)?$/.test(lage)) kopie.dataset.dpFokus = lage;
    }

    /*
     * Kopien einer lebenden Ansicht (IMPRINT) behalten deren Markierung: das
     * Skript legt frische Abzuege in alle so markierten Bilder -
     * auch hier im Streifen bleibt die Grossansicht damit auf Stand.
     * Die Zelle bekommt dieselben Pillen wie die Kachel im Raster - im
     * gemeinsamen Traeger, mehrere stehen nebeneinander.
     */
    const pillen = [];
    /*
     * Im Warenkorb tragen die Bilder der Personalisierung ihre
     * Warenkorb-Bezeichnung (cart-products.liquid legt sie in
     * data-dp-marke): "Mitarbeiterausweis" etwa am hochgeladenen Foto. Sie
     * steht zuerst, also oben links, und immer ganz ausgeschrieben.
     */
    if (bild.dataset.dpMarke) {
      pillen.push({ text: bild.dataset.dpMarke, fest: true });
    }
    /* Welches Bild einer Ansicht - Geschwister (dieselbe Aufnahme in anderer Farbe) teilen sich die Ansicht. */
    if (bild.dataset.dpBild) kopie.dataset.dpBild = bild.dataset.dpBild;
    /*
     * Die Medienkennung des Galeriebildes reist mit: Die Kopien entstehen
     * beim Laden, bevor IMPRINT die Kacheln markiert, und wurden dann nur
     * ueber den Dateinamen ihrer Ansicht zugeordnet. Wird ein Bild im
     * Adminbereich ersetzt, nennt die Schablone noch den alten Namen - die
     * Grossansicht blieb ohne Personalisierung (Booties in Weiss, Oktober
     * 2026). Die Kennung bleibt beim Ersetzen dieselbe (dp-veredelung.js,
     * kopienVon).
     */
    const medium = bild.closest('[data-media-id]');
    if (medium instanceof HTMLElement && medium.dataset.mediaId) kopie.dataset.dpMedium = medium.dataset.mediaId;
    if (bild.dataset.dpLiveAnsicht) {
      kopie.dataset.dpLiveAnsicht = bild.dataset.dpLiveAnsicht;
      // Ruht, solange das Bild nichts vom Kunden traegt (dp-veredelung.js, setzeTraegt).
      pillen.push({ text: vorschauWortJetzt(), fest: false, live: true, ruht: !bild.hasAttribute('data-dp-traegt') });
    }
    /*
     * KI-Kennzeichen: die Alt-Marke "ki" aus dem Adminbereich, allein oder
     * neben anderen ("Zoom:50, ki" - dp-alt-marken.js). Diese Pille
     * steht immer voll da (fest), gekuerzt wird bei Platznot nur die
     * Live-Vorschau.
     */
    if (altMarken(bild.getAttribute('alt')).ki) {
      pillen.push({ text: 'KI', fest: true });
    }

    /*
     * Eine lebende Ansicht (IMPRINT) schiebt bei jeder Eingabe ein
     * frisch gezeichnetes Bild in dieselbe Kopie. Hat es dabei ein anderes
     * Seitenverhaeltnis als das Foto davor, stimmt das Fuellmass nicht mehr -
     * ohne Nachrechnen stand das Bild ploetzlich viel zu klein in der Flaeche.
     */
    kopie.addEventListener('load', () => this.pflegeGrundNach(kopie));

    zelle.appendChild(kopie);

    /*
     * Hinter dem Bild liegt dasselbe Bild noch einmal - formatfuellend und
     * stark weichgezeichnet.
     *
     * Herausgezoomt steht das Bild vollstaendig da und laesst oben und unten
     * (oder links und rechts) eine leere Flaeche. Die war einfach dunkel. Mit
     * dem weichen Grund laeuft die Farbe des Bildes bis an den Rand weiter -
     * dasselbe Milchglas-Motiv wie sonst im Laden.
     */
    const grund = document.createElement('img');
    grund.className = 'dp-bildfeld__grund';
    grund.dataset.dpSpaet = grossAdresse;
    if (kopie.getAttribute('src')) grund.src = kopie.src;
    grund.alt = '';
    grund.draggable = false;
    grund.loading = 'lazy';
    grund.setAttribute('aria-hidden', 'true');
    zelle.appendChild(grund);
    /*
     * VORLAGEN-FUNKTION: Das Galeriebild "Vorlagen" (snippets/dp-vorlagen.liquid) ist hier ein gewoehnliches Bild; dazu kommt
     * dieselbe Pille "Vorlagen ansehen" unten in der Mitte. Den Tipp nimmt dp-vorlagen.js (Klick, Aufnahmephase am Dokument).
     */
    if (bild instanceof Element && bild.closest('[data-dp-vorlagen-kachel]')) {
      /*
       * Der Slot der Vorlagen-Kachel zeigt die VORLAGEN (dieselbe Ziegelmauer wie in der Galerie, gross, ohne <img>), kein Produktbild:
       * das kopierte Kachelbild bleibt nur als Traeger fuer Masse und Zoom und ist unsichtbar. Ein Tipp auf die Mauer oder die Pille oeffnet
       * das Vorlagen-Overlay (dp-vorlagen.js, Aufnahmephase am Dokument - die Zelle traegt dieselbe Marke wie die Kachel).
       */
      zelle.classList.add('dp-bildfeld__zelle--vorlagen');
      zelle.setAttribute('data-dp-vorlagen-kachel', '');
      const satz = document.createElement('dp-vorlagen-satz');
      satz.className = 'dp-vorlagen-satz';
      satz.setAttribute('gross', '');
      satz.setAttribute('aria-hidden', 'true');
      zelle.appendChild(satz);
      const vorlagenKnopf = document.createElement('button');
      vorlagenKnopf.type = 'button';
      vorlagenKnopf.className = 'dp-modell3d__steuer dp-modell3d__steuer--start dp-vorlagen-knopf';
      vorlagenKnopf.setAttribute('data-dp-vorlagen-oeffnen', '');
      vorlagenKnopf.innerHTML =
        '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="miter" aria-hidden="true" focusable="false"><rect x="1.8" y="2.8" width="12.4" height="10.4"/><path d="M1.8 8H14.2M5.9 2.8V8M10.1 8V13.2"/></svg><span>Vorlagen ansehen</span>';
      for (const art of ['pointerdown', 'pointerup', 'touchstart']) vorlagenKnopf.addEventListener(art, (ereignis) => ereignis.stopPropagation());
      zelle.appendChild(vorlagenKnopf);
    }
    /*
     * Die Pillen kommen erst NACH dem Bild in die Zelle: Zoom und Massstab
     * greifen auf das erste Kind der Zelle zu - stand dort etwas anderes,
     * wurde es statt des Bildes vergroessert und gezoomt.
     */
    /*
     * Ein Video einer Bewertung (Jimmi, Oktober 2026): eingepasst ueber dem
     * Standbild, das als erstes Kind fuer Masse und Grund stehen bleibt (es
     * selbst ist unsichtbar). playsinline - kein Vollbild noetig. Ohne die
     * Leiste des Browsers: mitten darauf das runde Zeichen - pausiert steht
     * das Abspielen-Dreieck da, beim Start blitzt das Zeichen kurz auf und
     * blendet weich aus; ein Tipp spielt oder haelt an. Unten links der
     * Ton-Knopf, solange REVIEW das Video nicht stumm zeigt; es beginnt
     * stumm (so verlangen es die Browser fuers Selbststarten). Unten eine
     * schmale Laufleiste. Gezoomt wird ein Video nicht.
     */
    const videoKachel = bild instanceof Element ? bild.closest('.dp-bewertung__bild--video') : null;
    if (videoKachel instanceof HTMLElement && videoKachel.dataset.dpVideo) {
      zelle.classList.add('dp-bildfeld__zelle--video');
      const video = document.createElement('video');
      video.className = 'dp-bildfeld__video';
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'none';
      video.poster = grossAdresse;
      if (videoKachel.hasAttribute('data-dp-stumm')) {
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.loop = true;
        video.dataset.dpStumm = '';
      } else {
        video.muted = true;
      }
      const quelle = document.createElement('source');
      quelle.src = videoKachel.dataset.dpVideo;
      if (videoKachel.dataset.dpVideoTyp) quelle.type = videoKachel.dataset.dpVideoTyp;
      video.appendChild(quelle);
      zelle.appendChild(video);

      const zeichen = document.createElement('span');
      zeichen.className = 'dp-bildfeld__knopf dp-bildfeld__abspielen dp-bildfeld__abspielen--da';
      zeichen.setAttribute('aria-hidden', 'true');
      zeichen.innerHTML = VIDEO_ZEICHEN.spielen;
      zelle.appendChild(zeichen);
      /*
       * Die Laufleiste ist bedienbar (Jimmi, Oktober 2026): Tippen oder
       * Klicken springt, Ziehen spult - mit einer groesseren unsichtbaren
       * Trefferflaeche als die Linie, beim Ziehen etwas dicker. Hat sie den
       * Fokus, springen die Pfeiltasten fuenf Sekunden.
       */
      const lauf = document.createElement('div');
      lauf.className = 'dp-bildfeld__laufleiste';
      lauf.tabIndex = 0;
      lauf.setAttribute('role', 'slider');
      lauf.setAttribute('aria-label', 'Stelle im Video');
      lauf.setAttribute('aria-valuemin', '0');
      lauf.setAttribute('aria-valuemax', '100');
      // Spur und gelaufener Teil - beide rund, der Teil schiebt per translateX herein (Stilblatt dp-bewertungen.css).
      lauf.innerHTML = '<span class="dp-bildfeld__spur"><span class="dp-bildfeld__gelaufen"></span></span>';
      zelle.appendChild(lauf);
      const zeigeStand = () => {
        if (!video.duration) return;
        const anteil = video.currentTime / video.duration;
        lauf.style.setProperty('--anteil', String(anteil));
        lauf.setAttribute('aria-valuenow', String(Math.round(anteil * 100)));
      };
      video.addEventListener('timeupdate', zeigeStand);
      video.addEventListener('seeked', zeigeStand);
      /* Waehrend es laeuft, zieht die Leiste in jedem Bild nach (Jimmi: "smooth, nicht ruckartig") - timeupdate kommt nur rund viermal je Sekunde. */
      let laufUhr = 0;
      const fahre = () => {
        zeigeStand();
        laufUhr = video.paused || video.ended ? 0 : requestAnimationFrame(fahre);
      };
      video.addEventListener('play', () => {
        cancelAnimationFrame(laufUhr);
        laufUhr = requestAnimationFrame(fahre);
      });
      video.addEventListener('pause', () => {
        cancelAnimationFrame(laufUhr);
        laufUhr = 0;
        zeigeStand();
      });
      const springe = (clientX) => {
        const r = lauf.getBoundingClientRect();
        if (!video.duration || !r.width) return;
        video.currentTime = Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * video.duration;
        zeigeStand();
      };
      let ziehtLauf = null;
      lauf.addEventListener('pointerdown', (ereignis) => {
        // Die Leiste gehoert sich selbst - kein Wischen der Ansicht, kein Abspielen per Tipp.
        ereignis.stopPropagation();
        ereignis.preventDefault();
        ziehtLauf = ereignis.pointerId;
        try {
          lauf.setPointerCapture(ereignis.pointerId);
        } catch {
          /* ohne Erfassung springt sie trotzdem */
        }
        lauf.classList.add('dp-bildfeld__laufleiste--zieht');
        springe(ereignis.clientX);
      });
      lauf.addEventListener('pointermove', (ereignis) => {
        if (ziehtLauf === ereignis.pointerId) springe(ereignis.clientX);
      });
      const lasseLos = (ereignis) => {
        if (ziehtLauf !== ereignis.pointerId) return;
        ziehtLauf = null;
        lauf.classList.remove('dp-bildfeld__laufleiste--zieht');
      };
      lauf.addEventListener('pointerup', lasseLos);
      lauf.addEventListener('pointercancel', lasseLos);
      lauf.addEventListener('click', (ereignis) => ereignis.stopPropagation());
      lauf.addEventListener('touchstart', (ereignis) => ereignis.stopPropagation(), { passive: true });
      lauf.addEventListener('keydown', (ereignis) => {
        if (ereignis.key !== 'ArrowLeft' && ereignis.key !== 'ArrowRight') return;
        ereignis.preventDefault();
        ereignis.stopPropagation();
        if (!video.duration) return;
        video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + (ereignis.key === 'ArrowRight' ? 5 : -5)));
        zeigeStand();
      });
      /* Pausiert: das Dreieck bleibt stehen. Laeuft es an: das Pausezeichen blitzt kurz auf und geht. */
      video.addEventListener('pause', () => {
        zeichen.innerHTML = VIDEO_ZEICHEN.spielen;
        zeichen.classList.remove('dp-bildfeld__abspielen--blitz');
        zeichen.classList.add('dp-bildfeld__abspielen--da');
      });
      video.addEventListener('play', () => {
        zeichen.innerHTML = VIDEO_ZEICHEN.pause;
        zeichen.classList.remove('dp-bildfeld__abspielen--da', 'dp-bildfeld__abspielen--blitz');
        void zeichen.offsetWidth;
        zeichen.classList.add('dp-bildfeld__abspielen--blitz');
      });
      if (video.dataset.dpStumm === undefined) {
        const ton = document.createElement('button');
        ton.type = 'button';
        ton.className = 'dp-bildfeld__knopf dp-bildfeld__ton';
        const pflegeTon = () => {
          ton.innerHTML = video.muted ? VIDEO_ZEICHEN.stumm : VIDEO_ZEICHEN.laut;
          ton.setAttribute('aria-label', video.muted ? 'Ton an' : 'Ton aus');
        };
        pflegeTon();
        video.addEventListener('volumechange', pflegeTon);
        ton.addEventListener('click', (ereignis) => {
          ereignis.stopPropagation();
          // Der Ton gilt fuer diese Ansicht - auch fuer das naechste Video.
          this.tonAn = video.muted;
          video.muted = !this.tonAn;
        });
        zelle.appendChild(ton);
      }
    }
    if (pillen.length) {
      const marken = document.createElement('span');
      marken.className = 'dp-kachel-marken';
      marken.setAttribute('aria-hidden', 'true');
      for (const pille of pillen) {
        const marke = document.createElement('span');
        marke.className =
          'dp-kachel-marke' +
          (pille.fest ? ' dp-kachel-marke--ki' : '') +
          (pille.live ? ' dp-kachel-marke--live' : '') +
          (pille.ruht ? ' dp-kachel-marke--ruht' : '');
        marke.textContent = pille.text;
        marken.appendChild(marke);
      }
      zelle.appendChild(marken);
    }
    return zelle;
  }

  /**
   * Nimmt spaeter entstandene Kacheln in die laufende Ansicht auf, ohne sie
   * neu zu bauen - gedacht fuer die offene Grossansicht, unter deren
   * Betrachter nichts abgerissen werden soll. Die Live-Kacheln haengen am
   * Ende der Galerie, also waechst auch der Streifen nur hinten: die
   * gezeigte Stelle bleibt exakt stehen.
   *
   * @returns {boolean} true, wenn die Ansicht danach auf Stand ist.
   */
  ergaenze() {
    const neue = this.sammleBilder();
    if (neue.length < this.bilder.length) return false;
    for (let i = 0; i < this.bilder.length; i += 1) {
      if (neue[i] !== this.bilder[i]) return false;
    }
    if (neue.length === this.bilder.length) return true;
    // Ohne Klone (eine einzige Zelle) ist der Umbau ein anderer - neu bauen.
    if (!this.hatKlone) return false;

    /*
     * Neue Zellen kommen vor den End-Klon (die Kopie des ersten Bildes -
     * die bleibt gueltig). Der Anfangs-Klon zeigt die letzte echte Zelle
     * und wird ersetzt, denn die Letzte ist jetzt eine andere.
     */
    const endKlon = this.streifen.lastElementChild;
    for (const bild of neue.slice(this.bilder.length)) {
      this.streifen.insertBefore(this.macheZelle(bild), endKlon);
    }
    const letzteEchte = this.streifen.children[this.streifen.children.length - 2];
    this.streifen.replaceChild(letzteEchte.cloneNode(true), this.streifen.firstElementChild);

    this.bilder = neue;
    rolleZaehler(this.zaehler, this.index + 1 + '/' + this.bilder.length, 'hoch', true);
    this.ladeUmgebung();
    return true;
  }

  /**
   * Chat: lädt das gezeigte Bild herunter. Die Bilder liegen bei einem anderen Dienst - das Attribut "download" wirkt dort
   * nicht, deshalb wird das Bild geholt und als Datei aus dem Speicher angeboten. Gelingt das nicht, öffnet es in einem neuen Tab.
   */
  async ladeHerunter() {
    const bild = this.bilder && this.bilder[this.index];
    if (!(bild instanceof HTMLImageElement)) return;
    const adresse = bild.currentSrc || bild.src;
    if (!adresse) return;
    const knopf = this.feld && this.feld.querySelector('[data-dp-laden]');
    if (knopf instanceof HTMLButtonElement) {
      if (knopf.disabled) return;
      knopf.disabled = true;
    }
    try {
      /* "reload": das Bild wurde ohne Herkunftsangabe angezeigt (Zwischenspeicher ohne Freigabe) - hier wird es frisch geholt. */
      const antwort = await fetch(adresse, { cache: 'reload' });
      if (!antwort.ok) throw new Error('Abruf');
      const blob = await antwort.blob();
      let name = String(bild.alt || '').trim() || 'bild';
      if (!/\.[a-z0-9]{2,5}$/i.test(name)) name += '.' + ((blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg').replace(/[^a-z0-9]/gi, '') || 'png');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      document.body.append(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      window.open(adresse, '_blank', 'noopener');
    } finally {
      if (knopf instanceof HTMLButtonElement) knopf.disabled = false;
    }
  }

  /** @param {MouseEvent} ereignis */
  beiKlick(ereignis) {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;
    // Die 3D-Vorschau der Kalkulationsvorlage (dp-3d-vorschau.js) ist kein Bild - dort wird gedreht, nicht geoeffnet.
    if (ziel.closest('.dp-3d-galerie')) return;
    /*
     * 3D-MODELL-FUNKTION: Ein-/Ausbau hier. Wer an der 3D-Kachel eben gedreht
     * hat, laesst irgendwo los - der Klick danach oeffnet nicht die Grossansicht.
     */
    const modell3d = ziel.closest('.dp-modell3d');
    if (modell3d instanceof HTMLElement && Date.now() - Number(modell3d.dataset.dpGezogen || 0) < 400) {
      ereignis.preventDefault();
      ereignis.stopPropagation();
      return;
    }
    // LAYER: Wer an der 3D-Ansicht der Druckvorschau (dp-layer-3d.js) eben gedreht hat, oeffnet damit nicht die Grossansicht.
    const layer3d = ziel.closest('.dp-layer3d__buehne');
    if (layer3d instanceof HTMLElement && Date.now() - Number(layer3d.dataset.dpGezogen || 0) < 400) {
      ereignis.preventDefault();
      ereignis.stopPropagation();
      return;
    }
    /*
     * Die Druckvorschau ohne Datei ist kein Bild, sondern die Aufforderung,
     * eine zu waehlen: ein Tipp darauf oeffnet die Dateiwahl
     * (dp-transferdruck.js), nicht die Grossansicht. Mit Datei laesst sie
     * sich wie ein Bild oeffnen.
     */
    const druck = ziel.closest('.dp-druck-galerie__flaeche');
    if (druck) {
      if (!druck.classList.contains('dp-druck-galerie__flaeche--da')) return;
      /*
       * Die Bedienung der Vorschau fuehrt ihre eigene Handlung aus:
       * Muelleimer, Ebenen, Drehgriff, Masse, Lupen und die Rollbalken.
       * Ohne diese Ausnahme oeffnete jeder Griff die Grossansicht.
       */
      if (ziel.closest('button, input, [data-dp-griff], [data-dp-dreh], [data-dp-mass], [data-dp-balken-x], [data-dp-balken-y], [data-dp-rahmen]')) return;
      /*
       * Und ebenso kurz nach einer Geste: wer am Drehgriff oder an der Ecke
       * gezogen hat, laesst irgendwo auf der Flaeche los. Der Klick, der
       * daraus entsteht, traegt dann die Flaeche als Ziel - und oeffnete die
       * Grossansicht, obwohl gerade skaliert oder gedreht wurde.
       */
      if (Date.now() - Number(druck.dataset.dpEben || 0) < 400) return;
    }

    const zelle = ziel.closest(this.kachel);
    if (!zelle) {
      /*
       * Hat der Artikel keine Fotos, gibt es kein Bilderraster: die
       * Druckvorschau steht dann allein (dp-druck-galerie--allein) und
       * liegt in keiner Rasterzelle. Ein Tipp auf ihre Flaeche soll sie
       * trotzdem gross zeigen - in der Bilderliste ist sie die erste und
       * einzige. Ohne diesen Zweig brach der Klick hier ab, und auf einem
       * Artikel ohne Fotos liess sich die Vorschau gar nicht oeffnen.
       */
      if (!druck) return;
      ereignis.preventDefault();
      ereignis.stopPropagation();
      this.gleicheBilderAb();
      if (this.feld && !this.feld.hidden) this.gehe(0);
      else this.oeffne(0);
      return;
    }

    ereignis.preventDefault();
    ereignis.stopPropagation();

    // Von der wartenden 3D-Kachel aus geoeffnet: sie ist dabei, bis weitergeblaettert wird (sammleBilder).
    this.mit3dWartend = !!zelle.closest('.dp-modell3d--wartet');

    /*
     * Der Index gilt in der gefilterten Bilderliste - nicht unter allen
     * Kacheln: die versteckten fremder Farben zaehlen dort nicht mit. Die
     * Liste wird vorher abgeglichen: ist seit dem letzten Mal die 3D-Vorschau
     * dazugekommen (sie steht vorn), stimmte der Index sonst um eins nicht
     * mehr, und ein Tipp auf das erste Foto oeffnete die Vorschau.
     */
    this.gleicheBilderAb();
    const index = this.bilder.findIndex((bild) => zelle.contains(bild));

    /*
     * Steht die Ansicht schon offen (etwa beim Wechseln zwischen den
     * Vorschaubildern im Personalisiert-Fenster), gleitet der Streifen zum
     * gewaehlten Bild, statt hart darauf zu springen.
     */
    if (this.feld && !this.feld.hidden) {
      this.gehe(Math.max(0, index));
      return;
    }

    // Das angetippte Bild und seine Lage - von dort fliegt es in die Ansicht.
    const quelle = index >= 0 ? this.bilder[index] : null;
    const von = quelle instanceof Element ? zelle.getBoundingClientRect() : null;
    this.oeffne(Math.max(0, index));
    if (BILD_FLUG && quelle && von) this.fliegeEin(quelle, zelle, von);
  }

  /**
   * Das Oeffnen folgt dem Finger: das angetippte Bild loest sich aus der
   * Galerie und gleitet an die Stelle, an der es in der Grossansicht steht -
   * Lage, Groesse, Rundung und Beschnitt wandern mit. Waehrenddessen blendet
   * die Kulisse ein; die Buehne der Ansicht bleibt so lange unsichtbar und
   * uebernimmt am Ende, das fliegende Abbild tritt dabei ab.
   *
   * Das Abbild zeigt das Bild mit dem Ausschnitt der Kachel (object-fit
   * cover, derselbe Fokuspunkt) - so beginnt der Flug genau mit dem, was der
   * Kunde angetippt hat, und endet deckungsgleich mit dem Bild der Ansicht.
   *
   * @param {Element} quelle - das Bild in der Kachel
   * @param {Element} zelle - die Kachel
   * @param {DOMRect} von - ihre Lage beim Antippen
   */
  fliegeEin(quelle, zelle, von) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const buehne = this.feld.querySelector('[data-dp-buehne]');
    if (!buehne || !von.width || !von.height) return;
    if (!buehne.getBoundingClientRect().width) return;

    // Ohne Bildadresse (3D-Vorschau, Druckvorschau): kein Flug, die Ansicht blendet wie bisher ein.
    const adresse = quelle instanceof HTMLImageElement ? quelle.currentSrc || quelle.src : '';
    if (!adresse || !quelle.naturalWidth || !quelle.naturalHeight) return;

    /*
     * Wo das Bild in der Kachel wirklich liegt: object-fit cover um den
     * Fokuspunkt (object-position), in der Box des Bildes - die bei
     * gezoomten Kacheln groesser ist als die Kachel selbst.
     */
    const box = quelle.getBoundingClientRect();
    const [lageX, lageY] = getComputedStyle(quelle)
      .objectPosition.split(' ')
      .map((wert) => (wert.endsWith('%') ? parseFloat(wert) / 100 : 0.5));
    const deck = Math.max(box.width / quelle.naturalWidth, box.height / quelle.naturalHeight);
    const breit = quelle.naturalWidth * deck;
    const hoch = quelle.naturalHeight * deck;
    const vonBild = {
      left: box.left + (box.width - breit) * (isNaN(lageX) ? 0.5 : lageX),
      top: box.top + (box.height - hoch) * (isNaN(lageY) ? 0.5 : lageY),
      width: breit,
      height: hoch,
    };

    const flug = document.createElement('div');
    flug.className = 'dp-bildfeld-flug';
    flug.setAttribute('aria-hidden', 'true');
    const bild = document.createElement('img');
    bild.src = adresse;
    bild.alt = '';
    flug.appendChild(bild);

    const rahmen = buehne.closest('.dp-bildfeld__rahmen');
    /*
     * Der Flug liegt im Rahmen, unter den Schaltflaechen - nicht als eigene
     * Ebene ueber allem. Frueher zog das Bild ueber X, Zaehler, Lupe und die
     * Pille hinweg und gab sie am Ende auf einen Schlag frei; sie schnellten
     * ein. Jetzt stehen sie waehrend des ganzen Fluges an ihrem Platz, und das
     * Bild waechst dahinter auf (Stilblatt: .dp-bildfeld__rahmen >
     * .dp-bildfeld-flug). Im Rahmen wird absolut gelegt, also in seinen
     * Koordinaten - gerechnet wird weiter im Bildschirm, abgezogen wird die
     * Lage des Rahmens.
     */
    if (rahmen) rahmen.appendChild(flug);
    else document.body.appendChild(flug);
    const eigene = parseFloat(getComputedStyle(buehne).borderTopLeftRadius) || 0;
    const rundungVon = parseFloat(getComputedStyle(zelle).borderTopLeftRadius) || 0;
    const rundungNach = eigene || (rahmen ? parseFloat(getComputedStyle(rahmen).borderTopLeftRadius) || 0 : 0);

    /*
     * Das Ziel wird in jedem Bild neu gemessen, nicht vorab: die Ansicht legt
     * ihr Bild mit eigenem Mass und Fokusversatz (setzeGrund) und zieht nach,
     * sobald die grosse Fassung da ist. Der Flug steuert also genau die Lage
     * an, in der das Bild der Ansicht gerade steht - und endet deckungsgleich
     * mit ihr. Gegen die Rahmenkante wird beschnitten wie dort.
     */
    const zielBild = () => {
      const zielZelle = this.streifen && this.streifen.children[this.platz];
      const echt = zielZelle && zielZelle.firstElementChild;
      return echt instanceof HTMLElement ? echt.getBoundingClientRect() : buehne.getBoundingClientRect();
    };

    /*
     * Ruhig und weich an beiden Enden.
     *
     * Frueher 420 ms auf einer Kurve, die schnell losging (1 - (1 - t)^4): In
     * den ersten Bildern legte das Bild fast die Haelfte des Weges zurueck, und
     * weil jedes Bild dabei Lage und Groesse neu setzt, sah das wie Stottern
     * aus. Jetzt laeuft es in 640 ms an, gleitet und kommt sanft an - die
     * Schritte je Bild sind klein und gleichmaessig.
     */
    const DAUER = 640;
    const kurve = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const misch = (a, b, t) => a + (b - a) * t;
    this.feld.classList.add('dp-bildfeld--flug');

    let fertig = false;
    const uebergib = () => {
      if (fertig) return;
      fertig = true;
      /*
       * Erst kommt das Bild der Ansicht, dann geht das Abbild - nacheinander.
       *
       * Frueher blendeten beide gleichzeitig ueber: das Abbild aus, der Streifen
       * darunter ein. In der Mitte waren beide halb durchsichtig, das Milchglas
       * schien durch, und das Bild wurde kurz blass, bevor es wieder
       * aufleuchtete. Jetzt blendet der Streifen (160 ms, Stilblatt) unter dem
       * noch voll deckenden Abbild ein; erst wenn er steht, geht das Abbild -
       * ueber einem deckungsgleichen, voll deckenden Bild faellt das nicht auf.
       */
      this.feld.classList.remove('dp-bildfeld--flug');
      const entferne = () => flug.remove();
      window.setTimeout(() => {
        const weg = flug.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'ease-out', fill: 'forwards' });
        weg.finished.then(entferne, entferne);
      }, 180);
      window.setTimeout(entferne, 600);
    };

    const beginn = performance.now();
    const schritt = (jetzt) => {
      if (fertig) return;
      const t = Math.min(1, (jetzt - beginn) / DAUER);
      const k = kurve(t);
      const rand = buehne.getBoundingClientRect();
      const ziel = zielBild();
      const bezug = rahmen ? rahmen.getBoundingClientRect() : { left: 0, top: 0 };
      const links = misch(von.left, rand.left, k);
      const oben = misch(von.top, rand.top, k);
      flug.style.left = links - bezug.left + 'px';
      flug.style.top = oben - bezug.top + 'px';
      flug.style.width = misch(von.width, rand.width, k) + 'px';
      flug.style.height = misch(von.height, rand.height, k) + 'px';
      flug.style.borderRadius = misch(rundungVon, rundungNach, k) + 'px';
      bild.style.left = misch(vonBild.left, ziel.left, k) - links + 'px';
      bild.style.top = misch(vonBild.top, ziel.top, k) - oben + 'px';
      bild.style.width = misch(vonBild.width, ziel.width, k) + 'px';
      bild.style.height = misch(vonBild.height, ziel.height, k) + 'px';
      if (t < 1) requestAnimationFrame(schritt);
      else uebergib();
    };
    schritt(beginn);
    requestAnimationFrame(schritt);
    // Laeuft kein Bild (verborgener Reiter), uebernimmt die Ansicht trotzdem.
    window.setTimeout(uebergib, DAUER + 200);
  }

  /** @param {number} index */
  oeffne(index) {
    /* Frisch geoeffnet gibt es keinen Vorgaengerwert - die Flaeche steht sofort richtig. */
    this.kaufHoehe = undefined;
    /* Blickpunkt und Vorrang gelten je Oeffnen - beim naechsten Mal beginnt jedes Bild bei seinem Fokus. */
    this.blick = null;
    this.kundeHand = false;
    this.bereichKennung = '';

    /*
     * Wird die Ansicht aus einer Arbeit ueber der Tastatur heraus geoeffnet,
     * behaelt das Feld den Schreibpunkt.
     *
     * Beim Oeffnen wird die halbe Seite umgebaut - Bilder werden geholt,
     * abgeglichen und eingehaengt. Verliert das Eingabefeld dabei den Punkt,
     * faellt unter Android die Tastatur zu, und die gehobene Zeile faehrt in
     * das Kauffenster zurueck, das waehrend der Ansicht geparkt ist: Sie war
     * damit einfach verschwunden. Gemerkt wird das Feld hier, zurueckgegeben
     * am Ende des Oeffnens - im selben Griff des Kunden, denn nur dann laesst
     * ein System die Tastatur stehen.
     */
    const vorher = document.activeElement;
    const gehoben = document.querySelector('.dp-veredelung__feld--gehoben');
    const schreibend =
      vorher instanceof HTMLElement && vorher.closest('.dp-veredelung__feld--gehoben')
        ? vorher
        : gehoben
          ? gehoben.querySelector('.dp-perso__eingabe')
          : null;
    // Bricht ein noch ausstehendes Verstecken ab - siehe schliesse().
    this.schliessLauf = (this.schliessLauf || 0) + 1;
    this.gleicheBilderAb();
    this.holeLive();

    this.miss();
    this.feld.hidden = false;
    // Frisch messen: das Fenster wechselt beim Oeffnen seinen Platz.
    this.lochStand = '';
    this.verfolgeFenster();
    this.gehe(index, true);
    requestAnimationFrame(() => this.feld.classList.add('dp-bildfeld--offen'));
    setzeSeitensperre(true);
    // Ein stummes Video laeuft an, sobald die Ansicht steht - der Aufruf in gehe() kommt beim Oeffnen zu frueh.
    window.setTimeout(() => {
      if (offeneAnsicht === this) this.pflegeVideos(false);
    }, 360);
    document.addEventListener('keydown', this.beiTaste);
    this.zeigeGeste();
    /*
     * Merkzettel am Wurzelelement: solange die Grossansicht offen ist, darf das
     * Kauffenster ueber ihr liegen. Sonst bleibt es bewusst unter dem Header,
     * damit es sich nicht vor das aufgeklappte Menue schiebt.
     *
     * Die Grossansicht der Warenkorb-Vorschauen bleibt davon aussen vor: sie
     * hat mit dem Kauffenster der Produktseite nichts zu tun, und dieses
     * ordnete sich sonst ohne Not neu - hinter dem Warenkorb zwar kaum
     * sichtbar, aber eben doch in Bewegung.
     */
    /*
     * Die Bilder einer Bewertung melden sich mit eigenem Wert: Das Kauffenster
     * hat mit ihnen nichts zu tun und bleibt am Handy, wo es ist - unten steht
     * dann die Bewertung selbst (dp-bewertungen.js, Jimmi, Oktober 2026).
     */
    if (!this.feld.classList.contains('dp-bildfeld--korb')) {
      // Auch die Bilder des Chats lassen das Kauffenster in Ruhe - gleicher Wert wie bei einer Bewertung.
      document.documentElement.dataset.dpBildfeld = this.kachel === KACHEL_BEWERTUNG || this.istChat ? 'bewertung' : 'offen';
    }
    offeneAnsicht = this;

    /*
     * Fensterlogik (dp-fenster-fokus.js): Rolle und Name, Fokusfalle, ruhiger Hintergrund,
     * Escape, Rueckkehr zum Ausloeser. Das Kauffenster bleibt neben der Ansicht bedienbar
     * (Stilblatt, html[data-dp-bildfeld]) und gehoert deshalb zur Falle - nur bei der Ansicht
     * der Produktseite. Der Fokus wird nur bei Tastaturbedienung gesetzt (auf das Kreuz),
     * nie bei Tipp und nie, wenn ein Feld den Schreibpunkt haelt.
     */
    if (this.griff) this.griff.loese();
    const mitKauf = !this.feld.classList.contains('dp-bildfeld--korb') && !this.istChat;
    this.griff = macheModal(this.feld, {
      name: 'Großansicht',
      schliessen: () => this.schliesse(),
      ausloeser: vorher instanceof HTMLElement ? vorher : null,
      rang: 25,
      mit: mitKauf ? () => [...document.querySelectorAll('.dp-kauffenster')] : undefined,
    });
    if (!schreibend && document.documentElement.classList.contains('dp-tastatur')) {
      this.feld.querySelector('.dp-bildfeld__zu')?.focus({ preventScroll: true });
    }

    /* Und der Schreibpunkt zurueck ins Feld - ohne die Seite zu bewegen. */
    if (schreibend && document.activeElement !== schreibend) {
      try {
        schreibend.focus({ preventScroll: true });
      } catch (fehler) {
        /* Dann bleibt es beim Verlust - die Zeile faehrt zurueck, wie bisher. */
      }
    }
  }

  /*
   * Kurz vorgemacht: Beim ersten Oeffnen zeigt eine kleine Vorfuehrung, wie
   * sich das Bild vergroessern laesst. Auf dem Handy laufen zwei Punkte
   * auseinander und wieder zusammen - die Zwei-Finger-Geste. Am Rechner rollt
   * das Rad einer angedeuteten Maus - dort zoomt das Mausrad.
   *
   * Nur einmal je Seitenaufruf; wer es einmal gesehen hat, braucht es nicht
   * wieder.
   */
  zeigeGeste() {
    if (gesteGezeigt) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!darfHinweisZeigen()) return;

    gesteGezeigt = true;
    this.vorfuehrWartet = 0;

    /*
     * Das Bild zieht mit heran, und dazu laufen die beiden Punkte.
     *
     * Die Punkte zeigen die Geste, das Bild zeigt, was sie bewirkt - ohne das
     * bleibt es eine Bewegung im Leeren. Beides beginnt deshalb im selben
     * Augenblick: die Klassen setzt erst fuehreVor(), wenn das Bild wirklich
     * losfaehrt. Vorher wurden die Punkte sofort gestartet, und wenn die Fahrt
     * auf eine nachliefernde Ansicht warten musste, lief sie ihnen hinterher -
     * und wurde am Ende sogar abgeschnitten.
     *
     * Gefahren wird die Vorfuehrung ueber die Animationsschnittstelle des
     * Browsers und nicht ueber this.zoom: der eigentliche Zoom bleibt damit
     * unberuehrt - er steht weiter bei eins, und nach der Vorfuehrung ist
     * nichts zurueckzusetzen.
     */
    this.fuehreVor();
  }

  /**
   * Laesst die Vorfuehrung anlaufen: Punkte an, Wecker stellen, und wer
   * selbst anfaengt, beendet sie.
   */
  starteVorfuehrung() {
    const amRechner = window.matchMedia('(min-width: 750px)').matches;
    this.feld.classList.add(amRechner ? 'dp-bildfeld--zeigtRad' : 'dp-bildfeld--zeigtGeste');

    const beenden = () => {
      this.feld.classList.remove('dp-bildfeld--zeigtGeste');
      this.feld.classList.remove('dp-bildfeld--zeigtRad');
      this.buehne.removeEventListener('pointerdown', beenden);
      this.buehne.removeEventListener('wheel', beenden);
      this.beendeVorfuehrung = null;

      // Abgebrochen wird sofort - das Bild springt damit auf seinen Massstab.
      if (this.vorfuehrung) {
        this.vorfuehrung.cancel();
        this.vorfuehrung = null;
      }
    };

    /*
     * Gemerkt, damit auch das Blaettern sie beenden kann. Die Pfeile liegen
     * neben der Buehne, nicht auf ihr - ein Klick darauf kam hier nie an, und
     * die Vorfuehrung lief ins naechste Bild weiter.
     */
    this.beendeVorfuehrung = beenden;

    // Wer selbst anfaengt, braucht die Vorfuehrung nicht mehr.
    this.buehne.addEventListener('pointerdown', beenden, { once: true });
    this.buehne.addEventListener('wheel', beenden, { once: true, passive: true });
    window.setTimeout(beenden, VORFUEHR_DAUER + 60);
  }

  /**
   * Faehrt das Bild einmal heran und wieder zurueck - im Takt der beiden
   * Punkte daneben.
   *
   * Gerechnet wird mit festen Werten, nicht mit einer Vergroesserung, die
   * sich auf den Stand des Bildes legt. Der Unterschied ist keiner der
   * Rechnung, sondern einer der Zustaendigkeit: eine Animation, die addiert
   * (composite: add), muss der Browser Bild fuer Bild auf demselben Faden
   * rechnen, auf dem auch die Seite aufgebaut wird. Sie stockte deshalb,
   * waehrend die beiden Punkte daneben - reines CSS auf der Grafikkarte -
   * ruhig weiterliefen. Mit festen Werten darf auch das Bild dorthin.
   *
   * Den Grund, aus dem einmal addiert wurde, gibt es dann nicht mehr: Der
   * Massstab, mit dem das Bild die Flaeche fuellt, steht erst fest, wenn das
   * Bild geladen ist - also wird gewartet, bis es so weit ist. Aendert er
   * sich danach doch noch, setzt zeichneJetzt() die Fahrt an derselben Stelle
   * neu auf.
   *
   * @param {number} [abZeit] Stelle, an der die Fahrt fortgesetzt wird.
   */
  fuehreVor(abZeit) {
    const zelle = this.streifen.children[this.platz];
    const bild = zelle && zelle.firstElementChild;
    if (!(bild instanceof HTMLElement) || !bild.animate) return;

    /** Ob die Ansicht noch dieselbe ist und niemand selbst gezoomt hat. */
    const nochGueltig = () =>
      this.streifen.children[this.platz] === zelle &&
      this.feld.classList.contains('dp-bildfeld--offen') &&
      this.zoom <= 1.01;

    /*
     * Noch nicht geladen: dann faengt die Vorfuehrung eben spaeter an. Das
     * grosse Bild laedt erst beim Oeffnen (lazy) - gewartet wird deshalb
     * laenger als eine Vorfuehrung dauert, sonst fiel sie bei langsamem Netz
     * ganz aus.
     */
    if (!bild.complete || !bild.naturalWidth) {
      const spaeter = () => {
        if (nochGueltig()) this.fuehreVor();
      };
      bild.addEventListener('load', spaeter, { once: true });
      window.setTimeout(() => bild.removeEventListener('load', spaeter), VORFUEHR_LADEGEDULD);
      return;
    }

    /*
     * Geladen heisst noch nicht gezeichnet: erst entschluesseln, dann einen
     * Augenblick stehen lassen. Vorher lief die Vorfuehrung los, waehrend das
     * Bild noch aufbaute oder das Fenster noch einblendete - das Bild zoomte,
     * bevor man es richtig sah.
     */
    if (!abZeit && this.vorfuehrBereit !== bild) {
      /*
       * Gemerkt wird die Stelle, nicht das Element: der Streifen wird nach
       * dem Oeffnen noch einmal abgeglichen, und das Bild an derselben Stelle
       * ist danach ein neues Element. Mit dem alten verglichen, brach die
       * Vorfuehrung ab, als sei geblaettert worden.
       */
      const platz = this.platz;
      const fertig = typeof bild.decode === 'function' ? bild.decode().catch(() => {}) : Promise.resolve();
      fertig.then(() => {
        window.setTimeout(() => {
          if (this.platz !== platz || !this.feld.classList.contains('dp-bildfeld--offen') || this.zoom > 1.01) return;
          const jetzt = this.streifen.children[this.platz];
          const aktuell = jetzt && jetzt.firstElementChild;
          // Dasselbe Bild: es ist gezeichnet und hat gestanden. Ein neues wartet selbst noch einmal.
          if (aktuell === bild) this.vorfuehrBereit = bild;
          this.fuehreVor();
        }, VORFUEHR_PAUSE);
      });
      return;
    }

    /*
     * Geladen ist nicht gleich fertig: eine lebende Ansicht liefert noch nach.
     * Kam eben erst etwas an, wird gewartet - aber nicht endlos, sonst kaeme
     * die Vorfuehrung bei einem Bild, an dem staendig gezeichnet wird, nie.
     */
    if (this.vorfuehrWartet === undefined) this.vorfuehrWartet = 0;
    const seitLaden = performance.now() - (this.zuletztGeladen || 0);
    if (seitLaden < VORFUEHR_RUHE && this.vorfuehrWartet < VORFUEHR_GEDULD) {
      this.vorfuehrWartet += VORFUEHR_RUHE;
      window.setTimeout(() => {
        if (nochGueltig()) this.fuehreVor(abZeit);
      }, VORFUEHR_RUHE);
      return;
    }

    const kurve =
      getComputedStyle(document.documentElement).getPropertyValue('--dp-ease').trim() || 'ease-in-out';

    /*
     * Von dort, wo das Bild gerade steht - Lage und Fuellmass bleiben
     * unberuehrt.
     *
     * Herangezogen wird um die Mitte des Bildschirms, nicht um die Mitte des
     * Bildes. Liegt das Bild wegen seines Fokuspunkts verschoben (versatz),
     * sind das zwei verschiedene Punkte: vergroessert wurde frueher um die
     * Bildmitte, und die Vorfuehrung zog sichtbar zur Seite. Waechst die
     * Verschiebung mit demselben Faktor mit, bleibt der Punkt in der Mitte
     * des Bildschirms stehen.
     */
    const massstab = this.zoom * this.grundZoom;
    const lage = (faktor) => 'translate(' + this.versatz.x * faktor + 'px, ' + this.versatz.y * faktor + 'px) ';
    const ruhe = lage(1) + 'scale(' + massstab + ')';
    const nah = lage(VORFUEHR_ZOOM) + 'scale(' + massstab * VORFUEHR_ZOOM + ')';

    /*
     * Die Kurve gehoert an jeden einzelnen Abschnitt, nicht an die Animation
     * als Ganzes: als Gesamtkurve zieht sie den Ablauf zusammen und das Bild
     * waere lange vor den Punkten wieder am Platz. In CSS gilt sie ohnehin
     * je Abschnitt - so laufen beide gleich.
     */
    /* Beim ersten Anlauf laufen die Punkte mit los; beim Neuaufsetzen laufen sie schon. */
    if (!abZeit && !this.feld.classList.contains('dp-bildfeld--zeigtGeste') && !this.feld.classList.contains('dp-bildfeld--zeigtRad')) {
      this.starteVorfuehrung();
    }

    /*
     * Waehrend der Fahrt bekommt das Bild eine eigene Ebene. Ohne sie legt der
     * Browser sie erst beim ersten Bild der Fahrt an - und genau dann steht
     * sie still.
     */
    bild.style.willChange = 'transform';

    this.vorfuehrung = bild.animate(
      [
        { transform: ruhe, offset: 0, easing: kurve },
        { transform: ruhe, offset: 0.15, easing: kurve },
        { transform: nah, offset: 0.5, easing: kurve },
        { transform: ruhe, offset: 0.85, easing: kurve },
        { transform: ruhe, offset: 1 },
      ],
      { duration: VORFUEHR_DAUER }
    );
    if (abZeit) this.vorfuehrung.currentTime = abZeit;

    /* Die Ebene wieder freigeben - dauerhaft gehalten kostet sie Speicher. */
    const loesen = () => {
      bild.style.willChange = '';
    };
    this.vorfuehrung.addEventListener('finish', loesen);
    this.vorfuehrung.addEventListener('cancel', loesen);
  }

  schliesse() {
    // Die 3D-Szene darf weiterrechnen (dp-modell3d.js) - sie pausierte, solange hier ein Foto stand.
    document.dispatchEvent(new CustomEvent('dp:grossansicht-zu'));
    this.pflegeVideos(true);
    setzeSeitensperre(false);
    this.feld.classList.remove('dp-bildfeld--offen');
    this.gibLiveZurueck();
    document.removeEventListener('keydown', this.beiTaste);
    if (this.griff) this.griff.loese();
    this.griff = null;
    // Zur anderen Bewertung geblaettert (zeigeReihe) - die eigene Reihe ist wieder die Galerie.
    if (this.heimGalerie) {
      this.galerie = this.heimGalerie;
      this.heimGalerie = null;
    }
    // Nur wegnehmen, was diese Ansicht selbst gesetzt hat - siehe oeffne().
    if (!this.feld.classList.contains('dp-bildfeld--korb')) {
      delete document.documentElement.dataset.dpBildfeld;
    }
    if (offeneAnsicht === this) offeneAnsicht = null;

    /*
     * Versteckt wird erst nach der Blende: "hidden" heisst display: none, und
     * das schneidet jede laufende Ueberblendung sofort ab - das Schliessen war
     * ein harter Schnitt, waehrend das Oeffnen weich einblendet. Jetzt laeuft
     * dieselbe Blende rueckwaerts, und erst danach verschwindet das Feld aus
     * dem Aufbau.
     *
     * Der Zaehler bricht das Verstecken ab, wenn zwischenzeitlich wieder
     * geoeffnet wurde. Der Wecker greift, falls das Ende der Ueberblendung
     * nie gemeldet wird - etwa wenn das System Bewegung abschaltet.
     */
    this.schliessLauf = (this.schliessLauf || 0) + 1;
    const lauf = this.schliessLauf;

    const fertig = () => {
      this.feld.removeEventListener('transitionend', beiEnde);
      clearTimeout(wecker);
      if (lauf === this.schliessLauf) {
        this.feld.hidden = true;
        // Zu: Die Fotos der Grossansicht geben ihren Speicher ab - beim naechsten Oeffnen laden sie neu.
        this.gibBilderAb();
      }
    };
    const beiEnde = (ereignis) => {
      if (ereignis.target === this.feld) fertig();
    };

    this.feld.addEventListener('transitionend', beiEnde);
    const wecker = setTimeout(fertig, 400);
    // Beim naechsten Oeffnen entscheidet der Tipp neu, ob die wartende 3D-Zelle dabei ist.
    window.clearTimeout(this.wartend3dUhr);
    this.mit3dWartend = false;
  }

  /**
   * Loest die Ansicht von der Seite.
   *
   * Wird gebraucht, wenn die zugehoerige Galerie ersetzt wurde: das Feld liegt
   * am Body und bliebe sonst als Leiche liegen, mitsamt seinen Horchern.
   */
  /**
   * Haengt die laufende Ansicht an eine neue Galerie um.
   *
   * Gedacht fuer den Fall, dass sich beim Neuaufbau des Abschnitts kein
   * einziges Bild aendert - auf der Vorlage mit der Druckvorschau ist das
   * so. Dann waere eine Ueberblendung nur ein Flackern: die Ansicht bleibt
   * stehen und bekommt nur die neuen Verweise.
   *
   * @param {HTMLElement} galerie
   */
  uebernimmGalerie(galerie) {
    if (this.galerie === galerie) return;
    if (this.galerie && this.beiKlickGebunden) {
      this.galerie.removeEventListener('click', this.beiKlickGebunden, true);
    }
    this.galerie = galerie;
    galerie.dataset.dpBildfeld = '';
    galerie.dpBildfeld = this;
    if (this.beiKlickGebunden) galerie.addEventListener('click', this.beiKlickGebunden, true);
    for (const knopf of galerie.querySelectorAll('.product-media-container__zoom-button')) {
      knopf.removeAttribute('on:click');
    }
    if (this.beobachter) {
      try {
        this.beobachter.disconnect();
        this.beobachter.observe(galerie);
        const kauf = document.querySelector('.dp-kauffenster');
        if (kauf) this.beobachter.observe(kauf);
      } catch {
        /* ohne Beobachter wird beim naechsten Messen nachgezogen */
      }
    }
    /*
     * Waehrend der Abschnitt darunter ausgetauscht wird, ruht das Milchglas.
     *
     * Der Rahmen der Ansicht zeichnet den Hintergrund weich - und weich
     * gezeichnet wird genau die Seite, die gerade neu aufgebaut wird. Manche
     * Browser tasten dafuer staendig neu ab und bauen den Weichzeichner
     * sichtbar neu auf; weil der Rahmen fast den ganzen Bildschirm einnimmt,
     * ist das ein Flackern ueber die ganze Flaeche. Fuer eine halbe Sekunde
     * deckt der Rahmen deshalb voll - bei 82 Prozent Deckung faellt der
     * Unterschied nicht auf.
     */
    if (this.feld) {
      this.feld.classList.add('dp-bildfeld--ruhig');
      window.clearTimeout(this.ruhigUhr);
      this.ruhigUhr = window.setTimeout(() => {
        if (this.feld) this.feld.classList.remove('dp-bildfeld--ruhig');
      }, 600);
    }

    /*
     * Der Streifen bleibt, wie er ist. Seine Zellen tragen Kopien der
     * Bilder, keine Verweise auf die Kacheln - sie zeigen also weiter
     * dasselbe, obwohl die Galerie darunter ausgetauscht wurde. Ihn hier neu
     * zu fuellen hiesse, die sichtbare Zelle im laufenden Betrieb
     * abzureissen und neu aufzubauen: genau das war das Flackern. Die Liste
     * wird beim naechsten Oeffnen ohnehin neu erhoben.
     */
    this.miss();
  }

  /**
   * Zeigt bei offener Ansicht die Bilder einer anderen Bewertung - der Kunde
   * wischt die Bewertung daneben zur Seite (dp-bewertungen.js, Jimmi, Oktober
   * 2026). Die Ansicht bleibt offen; die Bilder gleiten in Wischrichtung
   * hinaus und von der anderen Seite herein. Die eigene Reihe bleibt die
   * Heimat: beim Schliessen kehrt die Ansicht zu ihr zurueck, ihre Klicks
   * behaelt die andere Reihe ohnehin selbst.
   *
   * @param {HTMLElement} galerie Bildreihe der anderen Bewertung
   * @param {number} richtung 1 = zur naechsten, -1 = zur vorigen
   * @param {{ ende?: boolean }} [wie] ende: beim letzten Bild beginnen (rueckwaerts ueber die Grenze geblaettert)
   */
  zeigeReihe(galerie, richtung, wie = {}) {
    if (!this.feld || this.feld.hidden || !this.reihen || galerie === this.galerie) return;
    const heim = this.heimGalerie || this.galerie;
    const tausche = () => {
      if (!this.feld || this.feld.hidden) return;
      const vorher = this.galerie;
      this.galerie = galerie;
      const neue = this.sammleBilder();
      if (!neue.length) {
        this.galerie = vorher;
        return;
      }
      this.heimGalerie = galerie === heim ? null : heim;
      this.bilder = neue;
      this.fuelleStreifen();
      this.gehe(wie.ende ? neue.length - 1 : 0, true);
      this.pflegeVideos(false);
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !this.streifen) {
      tausche();
      return;
    }
    const weg = Math.round(Math.min(120, window.innerWidth * 0.25));
    this.streifen
      .animate(
        [{ translate: '0 0', opacity: 1 }, { translate: -richtung * weg + 'px 0', opacity: 0 }],
        { duration: 180, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      )
      .finished.then(
        (lauf) => {
          tausche();
          lauf.cancel();
          this.streifen.animate(
            [{ translate: richtung * weg + 'px 0', opacity: 0 }, { translate: '0 0', opacity: 1 }],
            { duration: 240, easing: 'cubic-bezier(0, 0, 0.2, 1)' }
          );
        },
        () => tausche()
      );
  }

  raeumeAuf() {
    // Eine Ansicht ohne Bild beim Aufbau (3D-Vorlage ohne Fotos, noch kein Modell) hat weder Feld noch Horcher.
    if (this.griff) this.griff.loese();
    this.griff = null;
    if (this.feld) this.feld.remove();
    if (this.beiKlickGebunden) this.galerie.removeEventListener('click', this.beiKlickGebunden, true);
    document.removeEventListener('keydown', this.beiTaste);
    window.removeEventListener('resize', this.beiGroesse);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.beiGroesse);
      window.visualViewport.removeEventListener('scroll', this.beiGroesse);
    }
    if (this.beiBand) document.removeEventListener('dp:tastaturband', this.beiBand);
    if (this.beiPillenNeu) document.removeEventListener('dp:pillen-neu', this.beiPillenNeu);
    window.clearTimeout(this.markenUhr);
    window.clearTimeout(this.leerUhr);
    this.beobachter?.disconnect();
    ansichten.delete(this);
    if (offeneAnsicht === this) offeneAnsicht = null;
  }

  /**
   * @param {number} index
   * @param {boolean} [sofort] Ohne Uebergang - beim Oeffnen soll nichts gleiten.
   */
  /** Blaettern - danach erfaehrt die 3D-Szene, ob sie zu sehen ist (dp-modell3d.js pausiert sonst). */
  gehe(index, sofort) {
    const r = this.geheIntern(index, sofort);
    document.dispatchEvent(new CustomEvent('dp:grossansicht-zeigt', { detail: { bildfeld: this } }));
    return r;
  }

  geheIntern(index, sofort) {
    /*
     * Ueber das erste oder letzte Bild einer Bewertung hinaus geht es zur
     * vorigen oder naechsten Bewertung mit Bildern (Jimmi, Oktober 2026) -
     * dp-bewertungen.js uebernimmt das und sagt es mit preventDefault an. Hat
     * nur eine Bewertung Bilder, bleibt es beim Kreis in ihr.
     */
    if (this.reihen && !sofort && this.bilder.length && (index < 0 || index >= this.bilder.length)) {
      const grenze = new CustomEvent('dp:bewertung-grenze', { cancelable: true, detail: { richtung: index < 0 ? -1 : 1, galerie: this.galerie } });
      document.dispatchEvent(grenze);
      if (grenze.defaultPrevented) return;
    }
    /* Woher geblaettert wird - der Zaehler rollt danach nach oben oder unten. */
    const vorherIndex = this.index;
    /* Ein Bildwechsel beendet die Vorfuehrung - sie gehoerte zum vorigen Bild. */
    if (this.beendeVorfuehrung && index !== this.index) this.beendeVorfuehrung();

    const anzahl = this.bilder.length;

    /*
     * Ueber das letzte Bild hinaus geht es auf die Kopie des ersten und
     * umgekehrt. Der Zaehler zeigt dabei schon das echte Bild - nur der
     * Streifen steht noch auf der Kopie.
     */
    let ziel = index;
    let laeuftUm = false;
    if (this.hatKlone) {
      if (index < 0) {
        ziel = -1;
        laeuftUm = true;
      } else if (index >= anzahl) {
        ziel = anzahl;
        laeuftUm = true;
      }
    } else {
      ziel = Math.max(0, Math.min(anzahl - 1, index));
    }

    this.index = ((ziel % anzahl) + anzahl) % anzahl;
    this.platz = this.hatKlone ? ziel + 1 : ziel;
    /* Blickpunkt und Vorrang des Kunden gehoeren zum Bild - ein anderes beginnt bei seinem Fokus. */
    if (this.index !== vorherIndex) {
      if (!this.blick || this.blick.index !== this.index) this.blick = null;
      this.kundeHand = false;
    }
    this.geheZeit = performance.now();
    this.grundZoom = 1;
    this.zoom = 1;
    this.versatz = { x: 0, y: 0 };

    if (sofort) this.streifen.style.transition = 'none';
    this.streifen.style.transform = 'translateX(' + -this.platz * 100 + '%)';
    if (sofort) {
      // Erzwingt einen Rechendurchgang, damit der naechste Wechsel wieder gleitet.
      void this.streifen.offsetWidth;
      this.streifen.style.transition = '';
    }

    if (laeuftUm && !sofort) this.setzeStillUm();

    this.ladeUmgebung();
    this.setzeGrund();
    // Auf der Vorschau-Zelle entfallen Lupe und Zoom-Hinweise (Stilblatt, dp-bildfeld--live).
    this.feld.classList.toggle('dp-bildfeld--live', this.istLive());
    // Auf einem Video entfallen Lupe und Zoom-Hinweise wie auf der Vorschau (Stilblatt, dp-bildfeld--video).
    this.feld.classList.toggle('dp-bildfeld--video', this.istVideo());
    this.pflegeVideos(false);
    // Auf der Vorschau-Zelle die Rollgeste auf den Randbereichen (Handy).
    if (this.istLive()) this.zeigeRandGeste();
    rolleZaehler(this.zaehler, this.zaehlerText(), index < vorherIndex ? 'runter' : 'hoch', sofort);
    this.verlasse3dWartend();
    // Ohne sichtbaren Zaehler rutschen Ton-Knopf und Laufleiste eines Videos an seinen Platz.
    const zaehlerWeg = !this.zaehler || this.zaehler.hidden || getComputedStyle(this.zaehler).display === 'none' || getComputedStyle(this.zaehler).visibility === 'hidden';
    this.feld.classList.toggle('dp-bildfeld--ohne-zaehler', zaehlerWeg);
    // Die Breite der Zaehler-Pille - der Ton-Knopf eines Videos steht mittig darueber (dp-bewertungen.css).
    const missZaehler = () => {
      if (this.zaehler && this.feld) this.feld.style.setProperty('--dp-zaehler-breite', this.zaehler.getBoundingClientRect().width + 'px');
    };
    missZaehler();
    window.setTimeout(missZaehler, 320);
    this.pflegeMarken();

    /*
     * Im Kreis gibt es kein Ende - die Pfeile bleiben immer benutzbar. Nur bei
     * einem einzelnen Bild fuehren sie nirgendwohin.
     */
    const zurueck = this.feld.querySelector('[data-dp-zurueck]');
    const weiter = this.feld.querySelector('[data-dp-weiter]');
    if (zurueck) zurueck.disabled = !this.hatKlone;
    if (weiter) weiter.disabled = !this.hatKlone;
  }

  /** Die Zelle der wartenden 3D-Kachel - oder -1. */
  platzWartend3d() {
    /* Die wartende Kachel faellt nicht mehr aus der Reihe und traegt keine eigene Zaehlerpille "3D" mehr: sie zaehlt wie jedes Bild ("1/n"). */
    return -1;
  }

  /*
   * Der Zaehler zaehlt nur die Produktbilder, solange die 3D-Vorschau nicht
   * laeuft. Auf ihrer wartenden Zelle selbst steht "3D".
   */
  zaehlerText() {
    const k = this.platzWartend3d();
    if (k < 0) return this.index + 1 + '/' + this.bilder.length;
    if (this.index === k) return '3D';
    return (this.index < k ? this.index + 1 : this.index) + '/' + (this.bilder.length - 1);
  }

  /*
   * Weitergeblaettert, ohne die 3D-Vorschau zu starten: Ist das Bild
   * angekommen, faellt die wartende Zelle aus der Reihe - zurueckblaettern
   * fuehrt dann nicht mehr zu ihr, nur durch die Produktbilder.
   */
  verlasse3dWartend() {
    window.clearTimeout(this.wartend3dUhr);
    const k = this.platzWartend3d();
    if (k < 0 || this.index === k) return;
    this.wartend3dUhr = window.setTimeout(() => {
      if (this.platzWartend3d() < 0 || this.index === this.platzWartend3d()) return;
      this.mit3dWartend = false;
      this.folgeFilter();
    }, 450);
  }

  /**
   * Zeigt auf dem festen Pillenplatz die Pillen des gezeigten Bildes.
   *
   * Die Pillen sassen frueher in jeder Zelle und wischten mit dem Bild aus
   * dem Rahmen. Jetzt stehen sie still: Traegt das neue Bild dieselben, bleibt
   * alles, wie es ist. Sonst blendet der Platz aus, bekommt die neuen und
   * blendet wieder ein; ohne Pillen bleibt er ausgeblendet.
   */
  pflegeMarken() {
    if (!this.marken || !this.streifen) return;
    const zelle = this.streifen.children[this.platz];
    /*
     * 3D-MODELL-FUNKTION: Die Zelle der 3D-Kachel traegt keine eigenen Pillen -
     * es gelten die der Kachel (dp-veredelung.js setzt dort "Vorschau").
     */
    const vorlage =
      zelle && zelle.classList.contains('dp-bildfeld__zelle--modell3d')
        ? (this.galerie.isConnected ? this.galerie : document).querySelector('.dp-modell3d .dp-modell3d__live > .dp-kachel-marken')
        : zelle && zelle.querySelector(':scope > .dp-kachel-marken');
    // Eine ruhende Pille (Vorschau ohne Personalisierung im Bild) zeigt der Platz nicht.
    let pillen = vorlage ? [...vorlage.children].filter((p) => !p.classList.contains('dp-kachel-marke--ruht')) : [];
    // Die Pille "Vorschau" steht auch auf dem Standbild der 3D-Kachel (Jimmi, 22.10.2026) - wie bei jedem Bild, solange etwas vom Kunden darin liegt.
    /* Waehrend eines Galeriewechsels hat die frische 3D-Kachel kurz keine stehende Pille, obwohl der Kunde etwas traegt (html.dp-modell3d-traegt): der Platz behaelt die Pille - kein Aus- und Einblenden. */
    if (!pillen.length && zelle && zelle.classList.contains('dp-bildfeld__zelle--modell3d') && document.documentElement.classList.contains('dp-modell3d-traegt') && this.marken.children.length) return;
    const stand = pillen.map((p) => p.className + '|' + p.textContent).join('||');
    if (stand === this.markenStand) return;
    this.markenStand = stand;

    const setze = () => {
      this.marken.replaceChildren(...pillen.map((p) => p.cloneNode(true)));
      this.marken.classList.toggle('dp-bildfeld__marken--weg', pillen.length === 0);
    };

    window.clearTimeout(this.markenUhr);
    const leer = this.marken.classList.contains('dp-bildfeld__marken--weg') || !this.marken.children.length;
    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (leer || ruhig) {
      setze();
      return;
    }
    // Erst ausblenden, dann tauschen - die neuen blenden mit dem Platz wieder ein.
    this.marken.classList.add('dp-bildfeld__marken--weg');
    this.markenUhr = window.setTimeout(setze, 180);
  }

  /**
   * Setzt den Streifen nach dem Umlauf still von der Kopie auf das echte Bild.
   *
   * Das darf erst geschehen, wenn der Uebergang durch ist - sonst schnitte es
   * die Bewegung ab. Der Zeitgeber darunter faengt den Fall ab, dass gar kein
   * Uebergang laeuft, etwa weil das Feld gerade nicht sichtbar ist.
   */
  setzeStillUm() {
    let erledigt = false;

    /**
     * @param {TransitionEvent} [ereignis]
     */
    const umsetzen = (ereignis) => {
      /*
       * Nur auf den Streifen selbst hoeren.
       *
       * Auch die Bilder in den Zellen haben einen Uebergang auf transform, und
       * der ist mit 180 Millisekunden kuerzer als die 320 des Streifens. Sein
       * Ende blubbert bis hierher hoch und loeste das Umsetzen mitten in der
       * laufenden Bewegung aus - der Streifen sprang, und der Massstab wurde
       * ein zweites Mal gezeichnet. Genau das war beim Umlauf als Ruckeln zu
       * sehen.
       */
      if (ereignis && ereignis.target !== this.streifen) return;
      if (erledigt) return;
      erledigt = true;
      this.streifen.removeEventListener('transitionend', umsetzen);

      this.streifen.style.transition = 'none';
      this.platz = this.index + 1;
      this.streifen.style.transform = 'translateX(' + -this.platz * 100 + '%)';
      void this.streifen.offsetWidth;
      this.streifen.style.transition = '';

      this.ladeUmgebung();

      /*
       * Der Massstab muss ausdruecklich neu gezeichnet werden.
       *
       * zeichneZoom() loescht ihn auf allen Zellen und setzt ihn nur auf der
       * aktuellen - bis hierher war das die Kopie. setzeGrund() allein genuegt
       * nicht: es bricht ab, wenn der Fuellwert schon stimmt, und genau das ist
       * beim Umlauf der Fall, weil Kopie und echtes Bild dasselbe Bild sind.
       * Die echte Zelle bliebe sonst ohne Massstab und das Bild ungezoomt.
       *
       * Ohne Uebergang, denn das Bild steht in diesem Moment schon vor Augen:
       * ein Uebergang liesse es sichtbar von eins auf den Fuellwert wachsen.
       */
      const zelle = this.streifen.children[this.platz];
      const bild = zelle && zelle.firstElementChild;
      if (bild) bild.style.transition = 'none';
      this.zeichneZoom();
      if (bild) {
        void bild.offsetWidth;
        bild.style.transition = '';
      }

      this.setzeGrund();
    };

    this.streifen.addEventListener('transitionend', umsetzen);
    window.setTimeout(umsetzen, 600);
  }

  /**
   * Holt das aktuelle Bild und seine beiden Nachbarn vor.
   *
   * Die Bilder laden sonst erst, wenn sie sichtbar werden - beim Blaettern
   * bliebe die Flaeche einen Moment leer. Die Nachbarn genuegen: weiter als
   * ein Bild kommt eine Geste nicht.
   */
  ladeUmgebung() {
    for (let i = this.platz - 1; i <= this.platz + 1; i += 1) {
      const zelle = this.streifen.children[i];
      const bild = zelle && zelle.firstElementChild;
      if (bild && bild.loading === 'lazy') bild.loading = 'eager';
      if (zelle) for (const b of zelle.querySelectorAll('img[data-dp-spaet]')) ladeSpaet(b);
    }
  }

  /** Beim Schliessen und vor dem Neufuellen: alle Kopien geben ihr Bild ab (gibBildAb). */
  gibBilderAb(wurzel) {
    const ort = wurzel || this.streifen;
    if (!ort) return;
    for (const b of ort.querySelectorAll('img[data-dp-spaet]')) gibBildAb(b);
  }

  /**
   * Rechnet aus, wie weit das Bild herangezogen werden muss, damit es die
   * Flaeche ausfuellt.
   *
   * Gemessen wird die gelegte Groesse ohne Vergroesserung - offsetWidth und
   * offsetHeight lassen Transformationen ausser Acht.
   *
   * @returns {number}
   */
  fuellSkala() {
    /*
     * Chat (Jimmi, Oktober 2026: "sind sie ganz oft extrem reingezoomt ... bitte rauszoomen, damit er das ganze Bild
     * anschauen kann"): Die Bilder des Kunden haben jedes Format - sie stehen als GANZES Bild in der Fläche (zwei Kanten
     * berühren den Rand), nicht so herangezogen, dass die Kanten gedeckt sind.
     */
    const messe = (z) => this.grundVon(z);
    const eigen = messe(this.streifen.children[this.platz]);
    if (eigen) return eigen;

    /*
     * Steht gerade eine Kopie am Rand, ist sie im Moment des Umlaufs oft noch
     * nicht geladen und laesst sich nicht messen. Ihr Zwilling in der Mitte
     * zeigt dasselbe Bild in derselben Zelle - sein Massstab gilt genauso.
     */
    const zwilling = this.hatKlone ? this.index + 1 : this.index;
    return messe(this.streifen.children[zwilling]) || 1;
  }

  /**
   * Fuellmassstab einer einzelnen Zelle - null, solange ihr Bild sich nicht
   * messen laesst.
   *
   * @param {Element} [zelle]
   * @returns {number}
   */
  /**
   * Der Massstab, bei dem das ganze Bild in der Flaeche steht - zwei Kanten
   * beruehren den Rand. Weiter heraus als bis hierher soll niemand zoomen
   * koennen.
   *
   * Gerechnet wird gegen die gelegte Groesse des Bildes, nicht gegen eins:
   * ein kleines Bild wird vom Stilblatt nicht vergroessert, und bei Massstab
   * eins stuende es verloren in der Flaeche. Genau das war beim Unholy-Shirt
   * an Bild eins und vier zu sehen.
   *
   * @param {Element} [zelle]
   * @returns {number}
   */
  ganzSkala(zelle) {
    const mass = this.bildMasse(zelle);
    if (!mass) return 0;
    const flaeche = this.flaeche(zelle);
    return Math.min(flaeche.breite / mass.breite, flaeche.hoehe / mass.hoehe);
  }

  /** Der Grundmaßstab einer Zelle: Produktbilder füllen die Fläche (Kanten gedeckt), Chat-Bilder stehen als ganzes Bild darin. */
  grundVon(zelle) {
    return this.istChat ? this.ganzSkala(zelle) : this.skalaVon(zelle);
  }

  skalaVon(zelle) {
    const mass = this.bildMasse(zelle);
    if (!mass) return 0;
    const flaeche = this.flaeche(zelle);
    return Math.max(flaeche.breite / mass.breite, flaeche.hoehe / mass.hoehe);
  }

  /**
   * Die Masse, die das Bild in seiner Zelle einnimmt - bevor irgendein Zoom
   * daraufkommt.
   *
   * Gerechnet wird aus den echten Massen des Bildes: es wird in die Flaeche
   * gelegt, ohne es zu beschneiden und ohne es zu vergroessern - genau das
   * macht auch das Stilblatt. Damit steht der Wert schon fest, bevor der
   * Browser das Bild ueberhaupt gelegt hat.
   *
   * Nur wenn die echten Masse fehlen, wird die gelegte Groesse genommen.
   *
   * @param {Element} [zelle]
   * @returns {{ breite: number, hoehe: number } | null}
   */
  bildMasse(zelle) {
    if (this.griffMerk && this.griffMerk.bild !== undefined) return this.griffMerk.bild;
    const gemessen = this.bildMasseRoh(zelle);
    if (this.griffMerk) this.griffMerk.bild = gemessen;
    return gemessen;
  }

  /**
   * Die groesste Fassung, die von diesem Bild bisher zu sehen war.
   *
   * Gemerkt wird sie am Bild selbst. Eine neue Quelle in derselben Kopie ist
   * dasselbe Motiv in anderer Aufloesung - es sei denn, das Seitenverhaeltnis
   * weicht ab; dann ist es wirklich ein anderes Bild, und das Gedaechtnis
   * faengt von vorn an.
   *
   * @param {HTMLElement} bild
   * @param {number} breit
   * @param {number} hoch
   * @returns {{ breite: number, hoehe: number, sicher: boolean }}
   *   sicher sagt, ob die Masse aus einer wirklich geladenen Fassung stammen.
   */
  groessteFassung(bild, breit, hoch) {
    const verhaeltnis = breit / hoch;
    const echt = Boolean(bild.naturalWidth && bild.naturalHeight);
    const gemerkt = bild.dpGroesste;

    if (gemerkt && Math.abs(gemerkt.breite / gemerkt.hoehe - verhaeltnis) < 0.02 * verhaeltnis) {
      if (echt && breit > gemerkt.breite) {
        bild.dpGroesste = { breite: breit, hoehe: hoch, sicher: true };
        return bild.dpGroesste;
      }
      return gemerkt;
    }

    bild.dpGroesste = { breite: breit, hoehe: hoch, sicher: echt };
    return bild.dpGroesste;
  }

  /**
   * Wo die Zelle auf dem Schirm liegt.
   *
   * Waehrend einer Geste aus dem Merkbuch: die Zelle bewegt sich dabei nicht,
   * und jede Abfrage zwaenge den Browser zum Nachrechnen des Aufbaus.
   *
   * @param {Element} zelle
   * @returns {DOMRect | {left: number, top: number, width: number, height: number}}
   */
  rahmenVon(zelle) {
    if (this.griffMerk && this.griffMerk.rahmen) return this.griffMerk.rahmen;
    const rahmen = zelle.getBoundingClientRect();
    if (this.griffMerk) this.griffMerk.rahmen = rahmen;
    return rahmen;
  }

  /**
   * Die Flaeche, in der das Bild liegt.
   *
   * Waehrend einer Geste kommt sie aus dem Merkbuch: die Flaeche aendert sich
   * nicht, solange zwei Finger auf dem Bild liegen.
   *
   * @param {Element} zelle
   * @returns {{ breite: number, hoehe: number }}
   */
  flaeche(zelle) {
    if (this.griffMerk && this.griffMerk.flaeche) return this.griffMerk.flaeche;
    const mass = { breite: zelle.clientWidth, hoehe: zelle.clientHeight };
    if (this.griffMerk) this.griffMerk.flaeche = mass;
    return mass;
  }

  /** Misst wirklich - siehe bildMasse. */
  bildMasseRoh(zelle) {
    const bild = zelle && zelle.firstElementChild;
    const flaeche = this.flaeche(zelle);
    if (!(bild instanceof HTMLElement) || !flaeche.breite || !flaeche.hoehe) return null;

    /* Die eigenen Masse der Kopie - sie gelten, sobald sie geladen ist. */
    let breit = bild.naturalWidth || 0;
    let hoch = bild.naturalHeight || 0;
    let eigene = Boolean(breit && hoch);

    if (!eigene && bild.dataset && bild.dataset.dpNatur) {
      const teile = bild.dataset.dpNatur.split('x').map((zahl) => parseInt(zahl, 10));
      if (teile.length === 2 && teile.every((zahl) => zahl > 0)) {
        breit = teile[0];
        hoch = teile[1];
      }
    }

    /*
     * Die groesste Fassung zaehlt, nicht die gerade geladene.
     *
     * Eine lebende Ansicht von IMPRINT schiebt dasselbe Motiv nacheinander in
     * mehreren Aufloesungen in dieselbe Kopie: erst eine grobe von 600 Punkten
     * Breite, dann eine von 700, zuletzt die volle von 1400. Fuer die Regel
     * "nie vergroessern" ist die grobe Zwischenfassung ein kleines Bild - auf
     * einem grossen Schirm blieb sie klein stehen, das Fuellmass fiel zu gross
     * aus, und das Bild zog hart heran, bis die volle Fassung kam und es
     * zuruecksprang. Gemeint ist mit der Regel aber ein wirklich kleines Bild,
     * und wie gross dieses Motiv wirklich ist, sagt die groesste Fassung.
     */
    if (breit && hoch) {
      const gross = this.groessteFassung(bild, breit, hoch);
      breit = gross.breite;
      hoch = gross.hoehe;
      eigene = eigene || gross.sicher;
    }

    if (breit && hoch) {
      /*
       * Mit den eigenen Massen gilt: hineingelegt, aber nie vergroessert -
       * ein kleines Bild bleibt klein.
       *
       * Stammen die Masse dagegen vom Quellbild der Galerie, zaehlt allein
       * das Seitenverhaeltnis. Die Galerie zeigt oft eine kleine Fassung, die
       * Grossansicht laedt eine grosse; als Groesse genommen ergab die kleine
       * Fassung beim Oeffnen kurz das Vierfache des richtigen Massstabs.
       */
      const hinein = eigene
        ? Math.min(1, flaeche.breite / breit, flaeche.hoehe / hoch)
        : Math.min(flaeche.breite / breit, flaeche.hoehe / hoch);
      return { breite: breit * hinein, hoehe: hoch * hinein };
    }

    /*
     * Solange die echten Masse fehlen, wird nicht geraten. Ein Bild, das noch
     * laedt, ist ein schmaler Kasten von ein paar Punkten - daraus ein
     * Fuellmass zu rechnen ergab beim Oeffnen kurz das Vierfache, und das sah
     * man. Ohne Mass bleibt der Ausgangsmassstab bei eins: das Bild steht
     * vollstaendig da, bis es sich messen laesst.
     */
    if (!bild.complete || !bild.offsetWidth || !bild.offsetHeight) return null;
    return { breite: bild.offsetWidth, hoehe: bild.offsetHeight };
  }

  /**
   * Setzt den Ausgangsmassstab des aktuellen Bildes.
   *
   * Solange das Bild noch laedt, steht seine gelegte Groesse nicht fest - dann
   * wird nach dem Laden nachgerechnet.
   */
  setzeGrund() {
    const zelle = this.streifen.children[this.platz];
    const bild = zelle && zelle.firstElementChild;
    if (!bild) return;

    /*
     * Waehrend sich die Ansicht einrichtet, faehrt nichts.
     *
     * Der Ausgangsmassstab steht erst fest, wenn die Flaeche ihre endgueltige
     * Groesse hat; bis dahin wird ein- bis zweimal nachgerechnet. Mit der
     * Uebergangsregel des Stilblatts wurde daraus eine sichtbare Fahrt: das
     * Bild zog weit heran und sprang zurueck. Ohne Uebergang setzt es sich
     * einfach hin.
     */
    this.feld.classList.add('dp-bildfeld--setzt');
    window.clearTimeout(this.setztUhr);
    this.setztUhr = window.setTimeout(() => this.feld.classList.remove('dp-bildfeld--setzt'), 520);

    /*
     * Der Massstab wird nach dem Laden nachgerechnet. Bis dahin kann laengst
     * etwas anderes passiert sein: weitergeblaettert oder von Hand gezoomt.
     * Beides darf der Nachzuegler nicht ueberschreiben - sonst passt der
     * Grundmassstab nicht mehr zum gezeigten Bild, und daran haengt, ob eine
     * Geste als Wischen oder als Schieben gilt. Genau so gingen Wischen und
     * Zoomen verloren.
     */
    const fuerIndex = this.index;

    const anwenden = () => {
      if (this.index !== fuerIndex) return;
      // Wer inzwischen selbst gezoomt hat, soll nicht zurueckgesetzt werden.
      if (Math.abs(this.zoom - 1) > 0.01) return;

      /*
       * So weit heranziehen, dass die Kanten gedeckt sind - und keinen Schritt
       * weiter. Die Alt-Marke "zoom:XX" gehoert den Vorschaukacheln und wird
       * hier bewusst nicht gelesen: in der Grossansicht will man das Bild
       * sehen, nicht einen Ausschnitt daraus.
       */
      const skala = this.fuellSkala();
      if (!skala || skala === this.grundZoom) return;

      this.grundZoom = skala;
      // Mit dem Fuellmass rueckt der Fokuspunkt des Bildes in die Mitte.
      this.versatz = this.fokusVersatz(this.streifen.children[this.platz], skala);

      /*
       * Ohne Bewegung anwenden: das ist keine Zoom-Geste, sondern die
       * Korrektur auf das Mass, mit dem das Bild die Flaeche fuellt. Die
       * Uebergangsregel des Stilblatts machte daraus eine sichtbare
       * Zoomfahrt, sobald das Bild erst nach dem Oeffnen ankam - besonders
       * beim Farbwechsel mit offener Ansicht.
       */
      /*
       * Kommt das Bild erst nach dem Oeffnen an, faehrt es weich auf sein
       * Fuellmass und den Fokuspunkt - ein harter Sprung vom ungezoomten
       * zum gezoomten Bild sah aus wie ein Fehler. Die Uebergangsregel des
       * Stilblatts uebernimmt die Bewegung.
       */
      this.zeichneZoom();
    };

    if (bild.complete && bild.naturalWidth) {
      anwenden();
      return;
    }

    bild.addEventListener('load', anwenden, { once: true });

    /*
     * Bis das Bild da ist, gilt der Massstab, den fuellSkala ersatzweise vom
     * Zwilling holt. Ohne ihn wurde hier mit dem Ausgangswert eins gezeichnet:
     * das Bild sprang beim Umlauf kurz heraus und nach dem Laden wieder herein.
     */
    const ersatz = this.fuellSkala();
    if (ersatz) this.grundZoom = ersatz;
    this.zeichneZoom();

    /*
     * Und noch einmal nachmessen, sobald die Flaeche wirklich steht.
     *
     * Beim Oeffnen ist das Bild oft noch nicht gelegt; gemessen wird dann eine
     * Groesse, die es gleich darauf nicht mehr hat, und das Fuellmass faellt zu
     * gross aus - die Ansicht stand zu weit herangezogen da. Liegt das Bild
     * schon im Zwischenspeicher, kommt dafuer auch kein load mehr, an dem sich
     * das bemerken liesse. Wer selbst gezoomt hat, wird nicht angefasst.
     */
    for (const wann of [0, 140, 420]) {
      window.setTimeout(() => {
        if (this.index !== fuerIndex) return;
        if (Math.abs(this.zoom - 1) > 0.01) return;
        const zelle = this.streifen.children[this.platz];
        const jetzt = this.grundVon(zelle);
        if (!jetzt || Math.abs(jetzt - this.grundZoom) < 0.001) return;
        this.grundZoom = jetzt;
        this.versatz = this.fokusVersatz(zelle, jetzt);
        this.zeichneZoom();
      }, wann);
    }
  }

  /**
   * Zieht die Grundlage nach, wenn in die gezeigte Kopie eine neue Quelle
   * geladen wurde.
   *
   * Nachgerechnet wird nur das Fuellmass. Der Zoom des Kunden bleibt, wie er
   * ist - er hat sich seine Ansicht ausgesucht, und ein neu gezeichneter Text
   * im Bild ist kein Grund, sie ihm wegzunehmen.
   *
   * @param {HTMLElement} bild
   */
  pflegeGrundNach(bild) {
    /* Woran die Vorfuehrung erkennt, dass das Bild noch nicht zur Ruhe gekommen ist. */
    this.zuletztGeladen = performance.now();

    const zelle = this.streifen.children[this.platz];
    // Nur das Bild, das gerade gezeigt wird - die Klone am Rand zaehlen nicht.
    if (!zelle || zelle.firstElementChild !== bild) return;

    /* Erst im naechsten Bild misst der Browser die neue Groesse. */
    requestAnimationFrame(() => {
      const jetzt = this.streifen.children[this.platz];
      if (!jetzt || jetzt.firstElementChild !== bild) return;
      const skala = this.fuellSkala();
      if (!skala || Math.abs(skala - this.grundZoom) < 0.001) return;

      /* Die Ansicht des Kunden bleibt gleich gross, auch wenn die neue Quelle ein anderes Fuellmass mitbringt. */
      if (this.hatKundenAnsicht()) this.zoom = (this.zoom * this.grundZoom) / skala;
      this.grundZoom = skala;
      if (this.zoom <= 1.01) this.versatz = this.fokusVersatz(jetzt, this.zoom * skala);
      this.begrenzeVersatz();
      this.zeichneZoom();
    });
  }

  /** @param {number} wert */
  setzeZoom(wert) {
    if (this.istVideo()) return;
    /* Zoomt der Kunde selbst, hat seine Ansicht Vorrang vor dem Ausrichten auf den Bereich (zeigeBereich). */
    if (!this.automatisch) {
      this.kundeHand = true;
      this.blick = null;
    }
    /* Ab hier zoomt der Kunde selbst - und das soll man sehen. */
    window.clearTimeout(this.setztUhr);
    this.feld.classList.remove('dp-bildfeld--setzt');

    /*
     * Eins bedeutet: das Bild fuellt die Flaeche. Nach oben bis zur
     * Obergrenze, nach unten so weit, dass das vollstaendige Bild zu sehen ist
     * - mehr braucht es nicht, denn darunter stuende es nur noch kleiner in
     * einer leeren Flaeche.
     */
    /*
     * Nach unten ist Schluss, sobald das ganze Bild zu sehen ist - zwei Kanten
     * beruehren dann den Rand. Kleiner braucht es niemand, und das Bild stuende
     * nur verloren in der Flaeche.
     */
    const zelle = this.streifen.children[this.platz];
    const ganz = this.ganzSkala(zelle);
    const untergrenze = Math.min(1, (ganz || 1) / this.grundZoom);
    this.zoom = Math.max(untergrenze, Math.min(MAX_ZOOM, wert));

    /*
     * Beim Zoomen gilt der Fokuspunkt nicht mehr. Er gehoert zum Oeffnen: da
     * soll die Ansicht gleich an der richtigen Stelle stehen. Danach folgt sie
     * dem Kunden - sie ruckt ihm nicht zurueck.
     */
    this.begrenzeVersatz();
    this.zeichneZoom();
  }

  /**
   * Der Versatz, der den Fokuspunkt des Bildes in die Mitte der Flaeche
   * rueckt, sobald das Bild auf Fuellmass herangezogen ist - begrenzt, damit
   * keine leere Kante ins Bild rutscht. Ohne Fokus bleibt alles bei der
   * Mitte, wie bisher.
   *
   * @param {Element} [zelle]
   * @param {number} skala
   * @returns {{ x: number, y: number }}
   */
  fokusVersatz(zelle, skala) {
    const bild = zelle && zelle.firstElementChild;
    /*
     * Ein Blickpunkt sticht den Fokus (Oktober 2026): der Punkt des Bildes,
     * den der Kunde selbst hingeschoben hat (senkrechtes Schieben) oder auf
     * den die Ansicht beim Bearbeiten eines Bereichs gerichtet wurde
     * (zeigeBereich). So bleibt die Lage, wenn das Fuellmass nachgerechnet
     * wird - etwa wenn eine lebende Ansicht ihr Bild neu liefert.
     */
    const blick = this.blick && this.blick.index === this.index ? this.blick : null;
    if (!(bild instanceof HTMLElement) || (!bild.dataset.dpFokus && !blick)) return { x: 0, y: 0 };

    const teile = blick ? [blick.u * 100, blick.v * 100] : bild.dataset.dpFokus.split(/\s+/).map((teil) => parseFloat(teil));
    if (teile.length < 2 || teile.some((zahl) => Number.isNaN(zahl))) return { x: 0, y: 0 };

    /* Dieselben Masse wie beim Massstab - sonst rechnen beide gegeneinander. */
    const mass = this.bildMasse(zelle);
    if (!mass) return { x: 0, y: 0 };

    const wunschX = -((teile[0] - 50) / 100) * mass.breite * skala + (blick ? blick.dx || 0 : 0);
    const wunschY = -((teile[1] - 50) / 100) * mass.hoehe * skala + (blick ? blick.dy || 0 : 0);
    const flaeche = this.flaeche(zelle);
    const grenzeX = Math.max(0, (mass.breite * skala - flaeche.breite) / 2);
    const grenzeY = Math.max(0, (mass.hoehe * skala - flaeche.hoehe) / 2);

    return {
      x: Math.max(-grenzeX, Math.min(grenzeX, wunschX)),
      y: Math.max(-grenzeY, Math.min(grenzeY, wunschY)),
    };
  }

  /**
   * Plant das Zeichnen fuer das naechste Bild ein.
   *
   * Waehrend einer Zoomgeste kommen die Beruehrungsereignisse schneller, als
   * der Bildschirm zeichnet - zwei bis vier je Bild auf einem 120-Hz-Schirm.
   * Jedes einzeln umzusetzen hiess, den Aufbau mehrfach je Bild neu rechnen
   * zu lassen; die Geste blieb weich, das Bild folgte ruckelnd. Jetzt wird
   * hoechstens einmal je Bild gezeichnet.
   */
  zeichneZoom() {
    /*
     * Ausserhalb einer Geste wird sofort gezeichnet - ein Bild spaeter waere
     * beim Oeffnen ein sichtbarer Sprung. Gebuendelt wird nur, solange zwei
     * Finger auf dem Bild liegen und die Ereignisse schneller kommen, als der
     * Schirm zeichnet.
     */
    if (!this.griffStart) {
      this.zeichneJetzt();
      return;
    }
    if (this.zeichenPlan) return;
    this.zeichenPlan = requestAnimationFrame(() => {
      this.zeichenPlan = 0;
      if (this.streifen && this.streifen.isConnected) this.zeichneJetzt();
    });
  }

  /**
   * Nimmt den Geschwistern des gezeigten Bildes ihren Zoom - aber erst, wenn
   * der Streifen angekommen ist.
   *
   * Frueher geschah das im selben Augenblick, in dem das neue Bild an der
   * Reihe war. Das alte war da noch mitten im Hinausgleiten und verlor
   * seinen Fuellwert: es schrumpfte sichtbar, waehrend es ging - bei
   * Breitbildern, die am staerksten herangezogen sind, besonders deutlich.
   * Jetzt behaelt es seine Groesse, bis es aus dem Rahmen ist. Wer bis dahin
   * zurueckwischt, findet es unveraendert vor; geleert wird immer nur, was
   * im Moment des Leerens nicht gezeigt wird.
   *
   * Die beiden direkten Nachbarn werden dabei nicht geleert, sondern auf
   * ihren Ruhezustand gesetzt (legeInRuhe). Sie sind es, die der Finger beim
   * Wischen hereinzieht - ungezoomt kamen sie klein herein und sprangen erst
   * beim Loslassen auf ihr Fuellmass.
   */
  leereNachbarnDanach() {
    const streifen = this.streifen;
    if (!streifen) return;
    window.clearTimeout(this.leerUhr);
    if (this.leerHoerer) streifen.removeEventListener('transitionend', this.leerHoerer);

    const leeren = () => {
      window.clearTimeout(this.leerUhr);
      streifen.removeEventListener('transitionend', hoerer);
      if (this.leerHoerer === hoerer) this.leerHoerer = null;
      if (!streifen.isConnected) return;
      const gezeigt = streifen.children[this.platz];
      const nachbarn = [streifen.children[this.platz - 1], streifen.children[this.platz + 1]];
      for (const andere of streifen.children) {
        if (andere === gezeigt) continue;
        if (nachbarn.includes(andere)) {
          this.legeInRuhe(andere);
          continue;
        }
        const bild = andere.firstElementChild;
        if (bild) {
          bild.style.transform = '';
          bild.style.width = '';
          bild.style.height = '';
          bild.style.maxWidth = '';
          bild.style.maxHeight = '';
          bild.style.objectFit = '';
          delete bild.dpBox;
        }
      }
    };
    /** @param {TransitionEvent} ereignis */
    const hoerer = (ereignis) => {
      // Nur der Streifen selbst - die Bilder darin haben eigene, kuerzere Uebergaenge.
      if (ereignis.target === streifen) leeren();
    };
    this.leerHoerer = hoerer;
    streifen.addEventListener('transitionend', hoerer);
    /*
     * Laeuft gar kein Uebergang (Oeffnen, Umsetzen nach dem Umlauf), gleich -
     * sonst stuenden die Nachbarn eine Weile ungezoomt bereit. Der Zeitgeber
     * faengt den Fall ab, dass das Ende eines Uebergangs nie gemeldet wird.
     */
    if (!streifen.getAnimations().length) {
      leeren();
      return;
    }
    this.leerUhr = window.setTimeout(leeren, 600);
  }

  /**
   * Setzt eine Zelle neben der gezeigten auf das Bild, mit dem sie
   * hereinkommen wird: Fuellmass und Fokuspunkt, ohne Zoom des Kunden - so,
   * wie gehe() es beim Ankommen zeichnet. Gemessen wird direkt an der Zelle,
   * nicht ueber das Merkbuch der laufenden Geste, das der gezeigten gehoert.
   *
   * Laedt ihr Bild noch, wird nach dem Laden gesetzt. Lebende Ansichten
   * tragen nie einen Zoom und bleiben unberuehrt.
   *
   * @param {Element} zelle
   */
  legeInRuhe(zelle) {
    const bild = zelle.firstElementChild;
    if (!(bild instanceof HTMLElement)) return;
    if (zelle.classList.contains('dp-bildfeld__zelle--live')) return;
    if (bild instanceof HTMLImageElement && !(bild.complete && bild.naturalWidth)) {
      bild.addEventListener(
        'load',
        () => {
          const streifen = this.streifen;
          if (!streifen || !streifen.isConnected) return;
          const i = [...streifen.children].indexOf(zelle);
          if (Math.abs(i - this.platz) === 1) this.legeInRuhe(zelle);
        },
        { once: true }
      );
      return;
    }

    const mass = this.bildMasseRoh(zelle);
    const breite = zelle.clientWidth;
    const hoehe = zelle.clientHeight;
    if (!mass || !breite || !hoehe) return;
    /* Chat: auch die Nachbarbilder (zu denen man wischt) stehen als GANZES Bild da, nicht auf ihr Füllmaß herangezogen. */
    const skala = this.istChat ? Math.min(breite / mass.breite, hoehe / mass.hoehe) : Math.max(breite / mass.breite, hoehe / mass.hoehe);

    // Wie fokusVersatz, nur mit den eigenen Massen der Zelle.
    let x = 0;
    let y = 0;
    const teile = (bild.dataset.dpFokus || '').split(/\s+/).map((teil) => parseFloat(teil));
    if (teile.length >= 2 && !teile.some((zahl) => Number.isNaN(zahl))) {
      const grenzeX = Math.max(0, (mass.breite * skala - breite) / 2);
      const grenzeY = Math.max(0, (mass.hoehe * skala - hoehe) / 2);
      x = Math.max(-grenzeX, Math.min(grenzeX, -((teile[0] - 50) / 100) * mass.breite * skala));
      y = Math.max(-grenzeY, Math.min(grenzeY, -((teile[1] - 50) / 100) * mass.hoehe * skala));
    }

    // Ohne Uebergang - die Zelle steht ausserhalb des Rahmens, es ist keine Bewegung.
    const vorher = bild.style.transition;
    bild.style.transition = 'none';
    bild.style.width = mass.breite + 'px';
    bild.style.height = mass.hoehe + 'px';
    bild.style.maxWidth = 'none';
    bild.style.maxHeight = 'none';
    bild.style.objectFit = 'contain';
    bild.style.transform = 'translate(' + x + 'px, ' + y + 'px) scale(' + skala + ')';
    bild.dpBox = Math.round(mass.breite) + 'x' + Math.round(mass.hoehe);
    void bild.offsetWidth;
    bild.style.transition = vorher;
  }

  zeichneJetzt() {
    /*
     * Nur die Geschwister des gerade gezeigten Bildes tragen ueberhaupt einen
     * Zoom - und auch das nur, solange sie gezeigt wurden. Sie bei jedem
     * Zeichnen zu leeren, hiess bei jedem Zeichnen an den Aufbau zu ruehren.
     */
    if (this.geleertFuer !== this.platz) {
      this.geleertFuer = this.platz;
      this.leereNachbarnDanach();
    }

    if (this.istLive()) {
      this.feld.classList.remove('dp-bildfeld--gezoomt');
      return;
    }
    const zelle = this.streifen.children[this.platz];
    const bild = zelle && zelle.firstElementChild;
    if (!bild) return;

    /*
     * Gezeichnet wird der Zoom mal dem Fuellwert. Der Fuellwert sorgt dafuer,
     * dass das Bild die Flaeche ausfuellt; der Zoom bleibt davon unberuehrt und
     * faengt bei eins an.
     *
     * Solange der Kunde nicht selbst gezoomt hat, wird der Fuellwert hier noch
     * einmal frisch genommen. Sonst veraltet er: eine lebende Ansicht von
     * IMPRINT wird zuerst klein gezeichnet und gleich darauf gross
     * nachgeliefert, und das Bild bekommt damit eine andere gelegte Groesse.
     * Mit dem alten Fuellwert stand es dann zu weit herangezogen da - je
     * groesser der Bildschirm, desto deutlicher.
     */
    if (Math.abs(this.zoom - 1) < 0.01) {
      const frisch = this.grundVon(zelle);

      /*
       * Aendert sich das Fuellmass, waehrend die Vorfuehrung laeuft, faehrt
       * sie auf einen Massstab zu, den es nicht mehr gibt - am Ende schnappte
       * das Bild auf den neuen. Statt sie abzubrechen, wird sie mit den neuen
       * Werten neu aufgesetzt und an derselben Stelle fortgesetzt: der Takt
       * mit den beiden Punkten daneben bleibt damit erhalten.
       */
      const wandert = frisch && this.vorfuehrung && Math.abs(frisch - this.grundZoom) > 0.001;
      if (frisch) this.grundZoom = frisch;
      if (wandert) {
        const stand = this.vorfuehrung.currentTime;
        this.vorfuehrung.cancel();
        this.vorfuehrung = null;
        this.fuehreVor(Number(stand) || 0);
      }
    }
    /*
     * Die Groesse, mit der das Bild in der Flaeche liegt, wird hier selbst
     * gesetzt - nicht dem Stilblatt ueberlassen.
     *
     * Der Grund: Ein Bild, das kleiner ist als die Flaeche, legt der Browser
     * in seiner Originalgroesse hin, statt es einzupassen. Auf einem grossen
     * Schirm stand die Kopie damit kleiner da, als die Rechnung annahm, und
     * der Massstab traf ins Leere: das Bild beruehrte die Raender nicht mehr.
     * Bei einer lebenden Ansicht kam dazu, dass jede nachgelieferte Fassung
     * die gelegte Groesse aenderte, waehrend der Massstab noch zur alten
     * gehoerte - fuer ein Bild lang zog es weit heran und sprang zurueck.
     *
     * Jetzt gilt in beiden Richtungen dieselbe Zahl: bildMasse bestimmt, wie
     * gross das Bild liegt, und derselbe Wert traegt den Massstab.
     */
    const mass = this.bildMasse(zelle);

    /*
     * Aendert sich die gelegte Groesse, darf der Massstab nicht hinterherfahren.
     *
     * Die Groesse springt sofort, der Massstab haette laut Stilblatt 180 ms
     * Uebergang - fuer diese Zeitspanne gilt die alte Zahl fuer die neue Box.
     * Bei einer lebenden Ansicht, die ihre Aufloesung nachliefert, sah man
     * dabei genau einen harten Zoom hinein und gleich wieder heraus. Beides
     * gehoert in denselben Augenblick: es ist keine Bewegung, sondern eine
     * Korrektur der Grundlage.
     */
    const neueBox = mass ? Math.round(mass.breite) + 'x' + Math.round(mass.hoehe) : '';
    const boxWandert = Boolean(neueBox) && bild.dpBox !== undefined && bild.dpBox !== neueBox;
    if (boxWandert) {
      const vorher = bild.style.transition;
      bild.style.transition = 'none';
      /* Erst im naechsten Bild wieder frei - dann stehen Groesse und Massstab beide. */
      requestAnimationFrame(() => {
        bild.style.transition = vorher;
      });
    }
    if (neueBox) bild.dpBox = neueBox;

    if (mass) {
      bild.style.width = mass.breite + 'px';
      bild.style.height = mass.hoehe + 'px';
      /*
       * Die Schranken des Stilblatts muessen weichen, sonst schneiden sie in
       * die gesetzte Groesse hinein: schrumpft die Flaeche - auf dem Telefon
       * faehrt die Tastatur herauf -, begrenzte max-width die Breite, waehrend
       * die Hoehe stehen blieb. Das Bild wurde dabei sichtbar gestaucht.
       *
       * object-fit ist der zweite Riegel: selbst wenn die Box einen Augenblick
       * lang nicht zum Bild passt, bleibt das Seitenverhaeltnis gewahrt.
       */
      bild.style.maxWidth = 'none';
      bild.style.maxHeight = 'none';
      bild.style.objectFit = 'contain';
    }

    /*
     * Solange nicht gezoomt ist, ist die gerade gezeigte Lage zugleich die,
     * zu der ein Doppeltipp spaeter zurueckfuehrt.
     */
    if (this.zoom <= 1.01) this.ausgangVersatz = { x: this.versatz.x, y: this.versatz.y };

    const massstab = this.zoom * this.grundZoom;
    bild.style.transform =
      'translate(' + this.versatz.x + 'px, ' + this.versatz.y + 'px) scale(' + massstab + ')';
    /*
     * Als vergroessert gilt erst, was ueber den Ausgangsmassstab hinausgeht -
     * sonst waere die Ansicht von Anfang an im Schiebe-Zustand und liesse sich
     * nicht mehr blaettern.
     */
    this.feld.classList.toggle('dp-bildfeld--gezoomt', this.zoom > 1.01);
  }

  /**
   * Merkt die gerade gezeigte Lage als Blickpunkt des Bildes - den Punkt, der
   * in der Mitte der Flaeche steht. Nachgerechnete Fuellmasse halten sich
   * daran (fokusVersatz), statt auf den Fokus zurueckzuspringen.
   */
  merkeBlick() {
    const zelle = this.streifen.children[this.platz];
    const mass = zelle ? this.bildMasse(zelle) : null;
    const s = this.zoom * this.grundZoom;
    if (!mass || !s) return;
    this.blick = { index: this.index, u: 0.5 - this.versatz.x / (mass.breite * s), v: 0.5 - this.versatz.y / (mass.hoehe * s), dx: 0, dy: 0 };
  }

  /**
   * Richtet die Ansicht auf einen Bereich des gezeigten Bildes (Jimmi,
   * Oktober 2026): Wer am Handy ein Feld bearbeitet, waehrend das
   * Kauffenster gross ist, sah den Bereich oft gar nicht - er lag ausserhalb
   * des Teils der Flaeche, der zu sehen ist. Liegt weniger als 60 Prozent
   * des Bereichs (oder seine Mitte nicht) im sichtbaren Teil, faehrt das Bild
   * weich so, dass der Bereich mittig darin steht; ist er groesser als der
   * sichtbare Teil, wird dazu herausgezoomt - hoechstens so weit, dass das
   * ganze Bild zu sehen ist.
   *
   * Der Kunde hat Vorrang: Hat er im selben Feld selbst geschoben oder
   * gezoomt, bleibt seine Ansicht, bis er ein anderes Feld bearbeitet
   * (kennung). Gerufen von dp-veredelung.js, nachdem es zur Ansicht des
   * Feldes geblaettert hat - gewartet wird hier, bis der Wechsel steht.
   *
   * @param {{ u0: number, v0: number, u1: number, v1: number }} box Anteile des Bildes
   * @param {string} kennung das Feld, fuer das gerichtet wird
   * @param {{ index?: number }} [wie] index: das Bild, auf das sich der Bereich bezieht
   */
  zeigeBereich(box, kennung, wie = {}) {
    if (!box || !this.feld || this.feld.hidden || !this.streifen) return;
    /* Hat der Kunde herangezoomt oder die Ansicht verschoben, aendert sich bei keiner Aenderung im Kauffenster etwas von selbst. */
    if (this.hatKundenAnsicht()) {
      this.bereichKennung = kennung;
      window.clearTimeout(this.bereichUhr);
      window.clearTimeout(this.nachUhr);
      this.bereichUhr = 0;
      this.nachUhr = 0;
      return;
    }
    if (kennung !== this.bereichKennung) {
      this.bereichKennung = kennung;
      this.kundeHand = false;
    }
    if (this.kundeHand) return;
    window.clearTimeout(this.bereichUhr);
    const ziel = typeof wie.index === 'number' ? wie.index : this.index;
    let versuche = 0;
    const versuch = () => {
      this.bereichUhr = 0;
      if (!this.feld || this.feld.hidden || this.kundeHand || this.index !== ziel) return;
      if (this.istLive() || this.istVideo() || this.zieht || this.schiebtHoch || this.griffStart || this.zeiger.size) return;
      const zelle = this.streifen.children[this.platz];
      const bild = zelle && zelle.firstElementChild;
      /* Erst wenn der Wechsel steht und das Bild da ist - sonst wird gegen eine Lage gerechnet, die gleich nicht mehr gilt. */
      const ruhe = performance.now() - (this.geheZeit || 0);
      const geladen = !(bild instanceof HTMLImageElement) || (bild.complete && bild.naturalWidth > 0);
      if ((ruhe < 520 || !geladen) && versuche < 20) {
        versuche += 1;
        this.bereichUhr = window.setTimeout(versuch, ruhe < 520 ? 540 - ruhe : 150);
        return;
      }
      this.richteAufBereich(box);
      /* Geht dabei die Tastatur auf oder faehrt die Feldzeile, aendert sich der sichtbare Teil - nach ihrer Fahrt noch einmal nachsehen. */
      if (!this.nachUhr) this.nachUhr = window.setTimeout(() => {
        this.nachUhr = 0;
        if (!this.kundeHand && this.bereichKennung === kennung && this.index === ziel && this.feld && !this.feld.hidden && !this.zeiger.size) this.richteAufBereich(box);
      }, 650);
    };
    versuch();
  }

  /**
   * Hat der Kunde die Ansicht selbst gewaehlt - ueber das Fuellmass hinaus gezoomt oder verschoben? Dann aendert die Grossansicht
   * von selbst weder Zoom noch Lage (Jimmi, Oktober 2026), egal was im Kauffenster geschieht; nur die Klemmung an die Bildkanten bleibt.
   */
  hatKundenAnsicht() {
    if (this.zoom > 1.02) return true;
    return this.kundeHand && (Math.abs(this.versatz.x) > 1 || Math.abs(this.versatz.y) > 1);
  }

  /** Der sichtbare Teil der Flaeche in Bildschirmpunkten - ohne das, was Kauffenster, Tastaturzeile oder Bildschirmrand verdecken. */
  sichtbarerTeil(zelle) {
    const r = zelle.getBoundingClientRect();
    const sicht = window.visualViewport;
    let oben = Math.max(r.top, sicht ? sicht.offsetTop : 0);
    let unten = Math.min(r.bottom, sicht ? sicht.offsetTop + sicht.height : window.innerHeight);
    const links = Math.max(r.left, 0);
    const rechts = Math.min(r.right, window.innerWidth);
    /* Bei offener Tastatur liegt die gehobene Zeile ueber dem Bild (dp-tastaturzeile.js meldet ihre Hoehe). */
    const band = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dp-tastaturband'));
    if (Number.isFinite(band) && band > 0) unten = Math.min(unten, window.innerHeight - band);
    /* Das Kauffenster, wo es ueber der Flaeche liegt (am Rechner daneben - dann schneidet es nichts). */
    const kauf = document.querySelector('.dp-kauffenster');
    if (kauf instanceof HTMLElement && kauf.getClientRects().length && getComputedStyle(kauf).visibility !== 'hidden' && Number(getComputedStyle(kauf).opacity) > 0.1) {
      const k = kauf.getBoundingClientRect();
      if (k.left < rechts && k.right > links && k.top < unten && k.bottom > oben) {
        if (k.top > oben + 40) unten = Math.min(unten, k.top);
        else if (k.bottom < unten - 40) oben = Math.max(oben, k.bottom);
      }
    }
    return { links, rechts, oben, unten: Math.max(oben, unten) };
  }

  /** @param {{ u0: number, v0: number, u1: number, v1: number }} box */
  richteAufBereich(box) {
    if (this.hatKundenAnsicht()) return;
    const zelle = this.streifen.children[this.platz];
    const mass = zelle instanceof HTMLElement ? this.bildMasse(zelle) : null;
    if (!mass || !this.grundZoom) return;
    const rahmen = zelle.getBoundingClientRect();
    const teil = this.sichtbarerTeil(zelle);
    const tw = teil.rechts - teil.links;
    const th = teil.unten - teil.oben;
    if (tw < 40 || th < 40) return;
    const s = this.zoom * this.grundZoom;
    const mx = rahmen.left + rahmen.width / 2;
    const my = rahmen.top + rahmen.height / 2;
    const auf = (u, v, skala, versatz) => ({ x: mx + versatz.x + (u - 0.5) * mass.breite * skala, y: my + versatz.y + (v - 0.5) * mass.hoehe * skala });
    const a = auf(box.u0, box.v0, s, this.versatz);
    const b = auf(box.u1, box.v1, s, this.versatz);
    const flaecheBereich = Math.max(1, (b.x - a.x) * (b.y - a.y));
    const ix = Math.max(0, Math.min(b.x, teil.rechts) - Math.max(a.x, teil.links));
    const iy = Math.max(0, Math.min(b.y, teil.unten) - Math.max(a.y, teil.oben));
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    const mitteDa = cx >= teil.links && cx <= teil.rechts && cy >= teil.oben && cy <= teil.unten;
    this.letzteMessung = { vorher: { x: Math.round(cx - (teil.links + teil.rechts) / 2), y: Math.round(cy - (teil.oben + teil.unten) / 2) }, sichtbar: Math.round(((ix * iy) / flaecheBereich) * 100) };
    /* Die letzten Messungen bleiben zum Nachsehen stehen (Pruefstand). */
    this.messungen = [...(this.messungen || []).slice(-4), this.letzteMessung];
    /*
     * Schwelle (Jimmi, Oktober 2026: ein Retainer lag nur zum Teil ausserhalb
     * und blieb so stehen): Gerichtet wird, sobald der Bereich nicht ganz im
     * sichtbaren Teil liegt - mehr als RAND_ZUGABE Punkte seines Kastens
     * ragen hinaus. Liegt er ganz darin, bleibt alles stehen.
     */
    const ueber = (p, q) =>
      Math.max(0, teil.links - p.x) + Math.max(0, q.x - teil.rechts) + Math.max(0, teil.oben - p.y) + Math.max(0, q.y - teil.unten);
    this.letzteMessung.ausserhalb = Math.round(ueber(a, b));
    this.letzteMessung.teil = { oben: Math.round(teil.oben), unten: Math.round(teil.unten), links: Math.round(teil.links), rechts: Math.round(teil.rechts) };
    this.letzteMessung.bereich = { oben: Math.round(a.y), unten: Math.round(b.y), links: Math.round(a.x), rechts: Math.round(b.x) };
    if (ueber(a, b) <= RAND_ZUGABE) return;
    /*
     * Hat der Kunde ueber das Fuellmass hinaus herangezoomt (Jimmi, Oktober 2026: "ich zoome weit hinein, aendere etwas und es zoomt
     * staendig etwas heraus"), bleibt sein Zoom, wie er ist: es wird nie herausgezoomt. Liegt die Mitte des Bereichs noch im sichtbaren
     * Teil, bleibt auch die Lage stehen; sonst wird nur verschoben, damit er im Bild ist.
     */
    const kundeZoom = this.zoom > 1.02;
    if (kundeZoom && mitteDa) return;

    const uc = (box.u0 + box.u1) / 2;
    const vc = (box.v0 + box.v1) / 2;
    /* Der Versatz, der die Bereichsmitte auf die Mitte des sichtbaren Teils legt. */
    const dx = (teil.links + teil.rechts) / 2 - mx;
    const dy = (teil.oben + teil.unten) / 2 - my;
    const ganz = this.ganzSkala(zelle) || 0;
    const kleinst = Math.min(s, ganz || s);
    /*
     * Die Lage bei einem Massstab: Bereichsmitte auf die Mitte des sichtbaren
     * Teils, begrenzt auf die Kanten des Bildes (fokusVersatz). Liegt das Bild
     * schon an einer Kante an, laesst es sich nicht weiter schieben - dann
     * wird herausgezoomt, Schritt fuer Schritt, bis der Bereich ganz zu sehen
     * ist, hoechstens bis das ganze Bild zu sehen ist.
     */
    const lageBei = (skala) => {
      const merke = this.blick;
      this.blick = { index: this.index, u: uc, v: vc, dx, dy };
      const versatz = this.fokusVersatz(zelle, skala);
      this.blick = merke;
      const p = auf(box.u0, box.v0, skala, versatz);
      const q = auf(box.u1, box.v1, skala, versatz);
      return { skala, versatz, ueber: ueber(p, q) };
    };
    let wahl = lageBei(s);
    for (let i = 1; !kundeZoom && i <= ZOOM_STUFEN && wahl.ueber > RAND_ZUGABE && kleinst < s; i += 1) {
      wahl = lageBei(s - ((s - kleinst) * i) / ZOOM_STUFEN);
    }
    const weg = Math.hypot(wahl.versatz.x - this.versatz.x, wahl.versatz.y - this.versatz.y);
    this.letzteMessung.weg = Math.round(weg);
    this.letzteMessung.zoomVorher = Math.round(this.zoom * 1000) / 1000;
    // Keine Mini-Bewegung: unter ZITTER_GRENZE Punkten und ohne Zoom bleibt es stehen.
    if (weg < ZITTER_GRENZE && Math.abs(wahl.skala - s) < 0.001) return;
    this.automatisch = true;
    try {
      this.fahreZoom();
      this.blick = { index: this.index, u: uc, v: vc, dx, dy };
      this.zoom = wahl.skala / this.grundZoom;
      this.versatz = wahl.versatz;
      this.begrenzeVersatz();
      this.zeichneZoom();
    } finally {
      this.automatisch = false;
    }
    const c2 = auf(uc, vc, wahl.skala, this.versatz);
    const p2 = auf(box.u0, box.v0, wahl.skala, this.versatz);
    const q2 = auf(box.u1, box.v1, wahl.skala, this.versatz);
    this.letzteMessung.nachher = { x: Math.round(c2.x - (teil.links + teil.rechts) / 2), y: Math.round(c2.y - (teil.oben + teil.unten) / 2) };
    this.letzteMessung.ausserhalbNachher = Math.round(ueber(p2, q2));
    this.letzteMessung.bereichNachher = { oben: Math.round(p2.y), unten: Math.round(q2.y), links: Math.round(p2.x), rechts: Math.round(q2.x) };
    this.letzteMessung.zoomNachher = Math.round(this.zoom * 1000) / 1000;
  }

  /** @param {WheelEvent} ereignis */
  beiRad(ereignis) {
    // Auf der 3D-Vorschau gehoert das Rad der App (sie zoomt das Modell). Ein Video zoomt nicht.
    if (this.istLive() || this.istVideo()) return;
    /*
     * Auf der Druckvorschau gehoert es dem Anordnen: dort zoomt Strg mit
     * dem Rad das Blatt. Sicherheitshalber auch dann, wenn die Zelle gerade
     * nicht als lebend gilt - sonst zoomte die Ansicht das Bild statt das
     * Blatt.
     */
    if (ereignis.target instanceof Element && ereignis.target.closest('.dp-druck-galerie__flaeche')) return;
    // Nicht die Seite mitzoomen - das Rad gehoert hier dem Bild.
    ereignis.preventDefault();
    this.zoomeAufPunkt(this.zoom * (ereignis.deltaY < 0 ? 1.12 : 1 / 1.12), ereignis.clientX, ereignis.clientY);
  }

  /**
   * Zoomt so, dass die Stelle unter dem Zeiger stehen bleibt.
   *
   * Vorher zog die Ansicht immer zur Bildmitte: wer ein Stueck am Rand
   * ansehen wollte, musste erst zoomen und dann hinschieben. Jetzt waechst
   * das Bild unter dem Zeiger - wie in jeder Karte.
   *
   * Gerechnet wird ueber die Stelle im Bild, die gerade unter dem Zeiger
   * liegt: Bildschirmpunkt minus Versatz, geteilt durch den Massstab. Nach
   * dem Zoomen wird der Versatz so gesetzt, dass dieselbe Stelle wieder dort
   * liegt.
   *
   * @param {number} wert    Der neue Zoomwert.
   * @param {number} zeigerX Bildschirmpunkt.
   * @param {number} zeigerY
   * @param {boolean} [gefahren] Ob die Aenderung sichtbar hinfahren soll.
   */
  zoomeAufPunkt(wert, zeigerX, zeigerY, gefahren) {
    if (this.istVideo()) return;
    const zelle = this.streifen.children[this.platz];
    /*
     * Beim Rad kommt Schritt auf Schritt - da waere eine Fahrt im Weg. Beim
     * Doppeltipp ist es ein einzelner Sprung, und der gehoert gefahren
     * (siehe zurueckAufAusgang).
     */
    if (gefahren) this.fahreZoom();
    const vorher = this.zoom * this.grundZoom;
    if (!(zelle instanceof HTMLElement) || !vorher) {
      this.setzeZoom(wert);
      return;
    }

    const rahmen = this.rahmenVon(zelle);
    const punktX = zeigerX - (rahmen.left + rahmen.width / 2);
    const punktY = zeigerY - (rahmen.top + rahmen.height / 2);
    const merke = { x: this.versatz.x, y: this.versatz.y };

    this.setzeZoom(wert);
    const nachher = this.zoom * this.grundZoom;
    if (!nachher || nachher === vorher) return;

    const faktor = nachher / vorher;
    this.versatz = {
      x: punktX - (punktX - merke.x) * faktor,
      y: punktY - (punktY - merke.y) * faktor,
    };
    this.begrenzeVersatz();
    this.zeichneZoom();
  }

  /**
   * Haelt das Bild im Rahmen: geschoben wird hoechstens so weit, wie es
   * ueber die Flaeche hinausragt. Sonst zoege das Rad das Bild aus dem Bild.
   */
  begrenzeVersatz() {
    const grenze = this.zoomGrenzen();
    if (!grenze) return;
    this.versatz = {
      x: Math.max(-grenze.x, Math.min(grenze.x, this.versatz.x)),
      y: Math.max(-grenze.y, Math.min(grenze.y, this.versatz.y)),
    };
  }

  /**
   * Wie weit das gezoomte Bild nach jeder Seite geschoben werden kann - so
   * weit, wie es ueber die Flaeche hinausragt.
   *
   * @returns {{ x: number, y: number } | null}
   */
  zoomGrenzen() {
    const zelle = this.streifen.children[this.platz];
    const bild = zelle && zelle.firstElementChild;
    if (!(bild instanceof HTMLElement) || !(zelle instanceof HTMLElement)) return null;
    const mass = this.bildMasse(zelle);
    if (!mass) return null;
    const massstab = this.zoom * this.grundZoom;
    const flaeche = this.flaeche(zelle);
    return {
      x: Math.max(0, (mass.breite * massstab - flaeche.breite) / 2),
      y: Math.max(0, (mass.hoehe * massstab - flaeche.hoehe) / 2),
    };
  }

  /** Setzt den Streifen um den Randzug versetzt - das Nachbarbild schiebt sich herein. */
  zeigeRandZug(weg) {
    this.randZug = weg;
    this.streifen.style.transition = 'none';
    this.streifen.style.transform = 'translateX(calc(' + -this.platz * 100 + '% + ' + Math.round(weg) + 'px))';
  }

  /** @param {PointerEvent} ereignis */
  beiZeigerAn(ereignis) {
    // Der Ton-Knopf auf dem Video bekommt seinen Tipp selbst - kein Wischen, kein Erfassen.
    if (this.istVideo() && ereignis.target instanceof Element && ereignis.target.closest('.dp-bildfeld__ton')) return;
    /*
     * Die Knoepfe der 3D-Buehne (Starten, Standbild, Flaechen-Pillen) bekommen
     * ihren Tipp selbst. Vor dem ersten Start gilt die Zelle als leer und die
     * ganze Flaeche wischt - die Erfassung nahm dem Knopf sonst das Loslassen
     * (Jimmi, Oktober 2026: "beim allerersten Mal tut der Knopf nichts").
     */
    if (ereignis.target instanceof Element && ereignis.target.closest('.dp-modell3d__steuer, .dp-modell3d__flaechenwahl')) return;
    this.videoTipp = this.istVideo() ? { x: ereignis.clientX, y: ereignis.clientY } : null;
    /*
     * Auf der 3D-Vorschau dreht der Zeiger das Modell - kein Wischen, kein
     * Ziehen; nur die Randbereiche blaettern. Ohne Modell (Zelle leer, nur
     * der Hinweis) gibt es nichts zu drehen: dann wischt die ganze Flaeche.
     */
    if (this.istLive()) {
      const leer = !!this.streifen.querySelector('.dp-bildfeld__zelle--live.dp-bildfeld__zelle--leer');
      const amRand = ereignis.target instanceof Element && ereignis.target.closest('.dp-3d-galerie__rand');
      /* Die 3D-Kachel im Standbild-Zustand (3D nicht gestartet) ist ein normales Bild: die ganze Flaeche wischt (Jimmi, 22.10.2026). Nur die laufende Szene dreht. */
      const zelle3d = this.streifen.children[this.platz];
      const ruhend3d = !!(zelle3d && zelle3d.classList.contains('dp-bildfeld__zelle--modell3d') && !zelle3d.querySelector('.dp-modell3d__buehne--laeuft'));
      if (!leer && !amRand && !ruhend3d) return;
    }
    /*
     * Die Erfassung kann fehlschlagen, etwa wenn der Finger schon wieder weg
     * ist. Das darf die Geste nicht abbrechen.
     */
    try {
      this.buehne.setPointerCapture(ereignis.pointerId);
    } catch {
      /* ohne Erfassung geht es auch, nur weniger zuverlaessig */
    }

    this.zeiger.set(ereignis.pointerId, { x: ereignis.clientX, y: ereignis.clientY });

    if (this.zeiger.size === 1) {
      this.startPunkt = { x: ereignis.clientX, y: ereignis.clientY };
      this.startVersatz = { x: this.versatz.x, y: this.versatz.y };
    }
  }

  /** @param {PointerEvent} ereignis */
  beiZeigerBewegt(ereignis) {
    if (!this.zeiger.has(ereignis.pointerId)) return;
    this.zeiger.set(ereignis.pointerId, { x: ereignis.clientX, y: ereignis.clientY });

    // Zwei Finger gehoeren dem Zoom - der laeuft ueber die Beruehrungsereignisse.
    if (this.zeiger.size > 1) return;

    /*
     * Bei vergroessertem Bild schiebt ein Finger das Bild - bis an seinen Rand.
     *
     * Was der Finger darueber hinaus zieht, schiebt den Streifen: Das naechste
     * Bild kommt herein wie beim Wischen, und losgelassen wird weitergeblaettert
     * oder zurueckgefedert (beiZeigerAus). Frueher musste man erst ganz
     * herauszoomen, um zum naechsten Bild zu kommen - am Rand zog der Finger
     * ins Leere.
     */
    if (this.zoom > 1.01 && this.startPunkt) {
      ereignis.preventDefault();
      const roh = {
        x: this.startVersatz.x + (ereignis.clientX - this.startPunkt.x),
        y: this.startVersatz.y + (ereignis.clientY - this.startPunkt.y),
      };
      const grenze = this.zoomGrenzen();
      if (!grenze) {
        this.versatz = roh;
        this.zeichneZoom();
        return;
      }
      const x = Math.max(-grenze.x, Math.min(grenze.x, roh.x));
      this.versatz = { x, y: Math.max(-grenze.y, Math.min(grenze.y, roh.y)) };
      this.zeichneZoom();
      const ueber = roh.x - x;
      if (ueber || this.randZug) this.zeigeRandZug(ueber);
      return;
    }

    /*
     * Sonst wandert der Streifen mit dem Finger: Das naechste Bild schiebt
     * sich schon waehrend der Bewegung ins Bild, statt erst am Ende
     * umzuspringen. Losgelassen wird entweder weitergeblaettert oder
     * zurueckgefedert - beides uebernimmt gehe().
     */
    if (!this.startPunkt) return;

    const weg = ereignis.clientX - this.startPunkt.x;
    const hochWeg = ereignis.clientY - this.startPunkt.y;
    const hoch = Math.abs(hochWeg);

    /*
     * Senkrecht schieben beim Ausgangszoom (Jimmi, Oktober 2026: "Ist das
     * Bild höher als das Bildoverlay, kann man durch einfaches hoch und
     * runter Wischen das Bild nicht verschieben"). Ragt das Bild oben oder
     * unten ueber die Flaeche, schiebt ein senkrechter Zug es - bis seine
     * Kanten die Flaeche gerade noch decken. Waagerecht bleibt es beim
     * Blaettern. Die Richtung steht nach den ersten Punkten fest (die
     * staerkere Achse) und bleibt fuer die ganze Geste - kein Zittern
     * zwischen Schieben und Blaettern.
     */
    if (this.schiebtHoch) {
      ereignis.preventDefault();
      const grenze = this.zoomGrenzen();
      const roh = this.startVersatz.y + hochWeg;
      this.versatz = { x: this.versatz.x, y: grenze ? Math.max(-grenze.y, Math.min(grenze.y, roh)) : roh };
      this.kundeHand = true;
      this.merkeBlick();
      this.zeichneZoom();
      return;
    }
    if (!this.zieht && Math.abs(weg) < 6 && hoch < 6) return;
    if (!this.zieht && hoch > Math.abs(weg)) {
      const grenze = this.istLive() || this.istVideo() ? null : this.zoomGrenzen();
      if (grenze && grenze.y >= 1) {
        this.schiebtHoch = true;
        /* Ab hier folgt das Bild dem Finger unmittelbar - ohne Nachlauf des Stilblatts. */
        const zelle = this.streifen.children[this.platz];
        const bild = zelle && zelle.firstElementChild;
        if (bild instanceof HTMLElement) bild.style.transition = 'none';
        this.beiZeigerBewegt(ereignis);
      }
      return;
    }
    if (!this.zieht && Math.abs(weg) < 6) return;

    this.zieht = true;
    ereignis.preventDefault();
    this.streifen.style.transition = 'none';
    this.streifen.style.transform =
      'translateX(calc(' + -this.platz * 100 + '% + ' + Math.round(weg) + 'px))';
  }

  /** @param {PointerEvent} ereignis */
  beiZeigerAus(ereignis) {
    const start = this.startPunkt;
    const einzeln = this.zeiger.size === 1;
    this.zeiger.delete(ereignis.pointerId);

    if (this.zeiger.size < 2) this.startAbstand = 0;

    /* Senkrecht geschoben: kein Blaettern, kein Tipp - die Lage bleibt, wo der Finger sie liess. */
    if (this.schiebtHoch) {
      this.schiebtHoch = false;
      this.zieht = false;
      this.startPunkt = null;
      this.letzterTipp = 0;
      this.tippOrt = null;
      const zelle = this.streifen.children[this.platz];
      const bild = zelle && zelle.firstElementChild;
      if (bild instanceof HTMLElement) bild.style.transition = '';
      return;
    }

    /*
     * Zwei kurze Tipps hintereinander zoomen: aus der Ausgangsgroesse an die
     * getippte Stelle heran, aus der Vergroesserung wieder zurueck. Das ist
     * am Telefon der einzige Weg, mit einer Hand zu zoomen - der Fingergriff
     * braucht zwei, und die beiden Knoepfe liegen am Rand.
     */
    if (einzeln && start && this.pruefeDoppeltipp(ereignis, start)) {
      this.zieht = false;
      this.startPunkt = null;
      return;
    }

    /*
     * Am Rand des gezoomten Bildes weitergezogen: ab derselben Schwelle wie
     * beim Wischen wird geblaettert, sonst federt der Streifen zurueck. Das
     * neue Bild kommt ungezoomt (gehe), das alte behaelt seinen Zoom, bis es
     * aus dem Rahmen ist.
     */
    const randZug = this.randZug || 0;
    if (randZug) {
      this.randZug = 0;
      this.zieht = false;
      this.startPunkt = null;
      this.streifen.style.transition = '';
      const schwelle = Math.max(WISCH_SCHWELLE, this.buehne.clientWidth * 0.18);
      if (einzeln && randZug <= -schwelle) this.geheVomKunden(this.index + 1);
      else if (einzeln && randZug >= schwelle) this.geheVomKunden(this.index - 1);
      else this.streifen.style.transform = 'translateX(' + -this.platz * 100 + '%)';
      return;
    }

    if (!einzeln || !start || this.zoom > 1.01) {
      this.zieht = false;
      this.startPunkt = null;
      return;
    }

    const weg = ereignis.clientX - start.x;
    const gezogen = this.zieht;
    this.zieht = false;
    this.startPunkt = null;

    /*
     * Umgeblaettert wird ab einem knappen Fuenftel der Breite - so viel, dass
     * ein Antippen nicht blaettert, und so wenig, dass eine ruhige Geste
     * genuegt. Darunter federt der Streifen zurueck; genau ein Bild je Geste.
     */
    const schwelle = Math.max(WISCH_SCHWELLE, this.buehne.clientWidth * 0.18);
    let ziel = this.index;
    if (weg <= -schwelle) ziel = this.index + 1;
    if (weg >= schwelle) ziel = this.index - 1;

    /*
     * Ein blosser Tipp bleibt folgenlos.
     *
     * Frueher lief auch er durch gehe() - dieselbe Ansicht noch einmal -, und
     * die richtet sich dabei neu ein: das Bild sprang auf seinen Fokuspunkt.
     * Zu sehen war das beim zweiten Doppeltipp, wo der erste der beiden Tippe
     * das Bild zurechtrueckte, bevor der zweite heranzog. Zurueckfedern muss
     * der Streifen nur, wenn er wirklich mitgewandert ist.
     */
    if (ziel === this.index && !gezogen) return;

    this.streifen.style.transition = '';
    ziel === this.index ? this.gehe(ziel) : this.geheVomKunden(ziel);
  }

  /**
   * Erkennt den zweiten von zwei kurz aufeinanderfolgenden Tipps.
   *
   * Ein Tipp ist eine Beruehrung, die sich kaum bewegt hat - alles andere ist
   * ein Wischen oder ein Schieben und loescht den Merkposten. Der zweite Tipp
   * muss zeitlich und raeumlich nah am ersten liegen; sonst gilt er wieder als
   * erster.
   *
   * Der Doppeltipp geht in beide Richtungen: steht das Bild in seiner
   * Ausgangsgroesse, zieht er an die getippte Stelle heran; steht es
   * vergroessert, holt er es wieder zurueck. Dass sich im herangezogenen
   * Zustand nicht mehr blaettern laesst, ist gewollt - dort schiebt die Geste
   * das Bild, und der naechste Doppeltipp gibt das Blaettern wieder frei.
   *
   * @param {PointerEvent} ereignis
   * @param {{ x: number, y: number }} start
   * @returns {boolean} Ob der Doppeltipp etwas ausgeloest hat.
   */
  pruefeDoppeltipp(ereignis, start) {
    // Auf einem Video ist ein Tipp ein Tipp (anhalten, spielen) - kein Zoom.
    if (this.istVideo()) return false;
    const bewegt = Math.hypot(ereignis.clientX - start.x, ereignis.clientY - start.y);
    if (bewegt > TIPP_WEG) {
      this.letzterTipp = 0;
      this.tippOrt = null;
      return false;
    }

    const jetzt = performance.now();
    const nahDran =
      this.tippOrt &&
      Math.hypot(ereignis.clientX - this.tippOrt.x, ereignis.clientY - this.tippOrt.y) <
        DOPPEL_WEG;

    if (nahDran && jetzt - this.letzterTipp < DOPPEL_ZEIT) {
      this.letzterTipp = 0;
      this.tippOrt = null;
      this.zuletztGedoppelt = jetzt;
      this.doppelzoom(ereignis.clientX, ereignis.clientY);
      return true;
    }

    this.letzterTipp = jetzt;
    this.tippOrt = { x: ereignis.clientX, y: ereignis.clientY };
    return false;
  }

  /**
   * Was ein Doppeltipp oder Doppelklick tut: aus der Ausgangsgroesse an die
   * getippte Stelle heran, aus der Vergroesserung wieder zurueck.
   *
   * @param {number} x Bildschirmpunkt.
   * @param {number} y
   */
  doppelzoom(x, y) {
    if (this.zoom > 1.01) this.zurueckAufAusgang();
    else this.zoomeAufPunkt(DOPPEL_ZOOM, x, y, true);
  }

  /**
   * Holt das Bild in seine Ausgangsgroesse zurueck - gefahren, nicht
   * gesprungen.
   */
  zurueckAufAusgang() {
    this.fahreZoom();

    /*
     * Zurueck heisst: genau dorthin, wo das Bild stand, bevor gezoomt wurde.
     *
     * Nachgerechnet wird das nicht - der Fokuspunkt ergibt je nach Massstab
     * eine andere Lage, und schon stuende das Bild nach dem Herauszoomen
     * woanders als beim Oeffnen. Gemerkt wird der Zustand selbst (siehe
     * zeichneJetzt), und der kommt hier unveraendert zurueck.
     */
    const zurueck = this.ausgangVersatz;
    this.versatz = zurueck ? { x: zurueck.x, y: zurueck.y } : { x: 0, y: 0 };
    this.setzeZoom(1);
  }

  /**
   * Laesst die naechste Zoomaenderung sichtbar hinfahren statt zu springen.
   *
   * Gilt nur fuer den einen Schritt: danach wird die Fahrt wieder geloest,
   * damit Rad und Fingergriff weiter unmittelbar folgen.
   */
  fahreZoom() {
    const zelle = this.streifen.children[this.platz];
    const bild = zelle && zelle.firstElementChild;
    if (!(bild instanceof HTMLElement)) return;

    bild.style.transition = 'transform ' + AUSGANG_DAUER + 'ms var(--dp-ease, ease-out)';
    const loesen = () => {
      bild.style.transition = '';
    };
    bild.addEventListener('transitionend', loesen, { once: true });
    // Faellt der Uebergang aus (gleicher Wert, reduzierte Bewegung), kommt
    // kein Ereignis - der Wecker raeumt dann auf.
    window.setTimeout(loesen, AUSGANG_DAUER + 120);
  }

  /**
   * Die Mitte zwischen zwei Beruehrungen - der Punkt, um den die Geste
   * aufzieht.
   *
   * @param {TouchList} beruehrungen
   * @returns {{ x: number, y: number } | null}
   */
  griffMitte(beruehrungen) {
    const a = beruehrungen[0];
    const b = beruehrungen[1];
    if (!a || !b) return null;
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }

  /**
   * Abstand zweier Beruehrungen.
   *
   * @param {TouchList} beruehrungen
   * @returns {number}
   */
  griffAbstand(beruehrungen) {
    // Bewusst ueber den Index: eine TouchList laesst sich nicht ueberall
    // auseinandernehmen wie ein Feld.
    const a = beruehrungen[0];
    const b = beruehrungen[1];
    if (!a || !b) return 0;
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  }

  /** @param {TouchEvent} ereignis */
  beiGriffAn(ereignis) {
    if (this.istLive() || this.istVideo()) return;

    /*
     * Auch ein einzelner Finger gehoert der Ansicht.
     *
     * Die Buehne traegt zwar touch-action: none, aber darauf allein war kein
     * Verlass: Bei offener Tastatur nahm der Browser eine Wischbewegung mit
     * einem Finger trotzdem fuer sich, rollte die Seite dahinter und brach
     * die Zeigerfolge ab (pointercancel). Damit fielen Wischen, Ziehen und
     * Doppeltipp aus - alles, was ueber die Zeigerereignisse laeuft -,
     * waehrend zwei Finger weiter gingen: Deren Weg sagt hier ausdruecklich
     * ab, und genau das fehlte dem einen Finger.
     *
     * Die Knoepfe sind davon nicht betroffen: Kreuz, Pfeile und Lupe liegen
     * neben der Buehne, nicht darin.
     */
    ereignis.preventDefault();
    if (ereignis.touches.length !== 2) return;
    /* Frisch messen, dann fuer die Dauer der Geste merken. */
    this.griffMerk = null;
    this.griffStart = this.griffAbstand(ereignis.touches);
    this.griffZoom = this.zoom;
    this.griffMerk = {};

    // Die einzelne Wischbewegung ist damit beendet.
    this.zieht = false;
    this.startPunkt = null;
  }

  /** @param {TouchEvent} ereignis */
  beiGriffBewegt(ereignis) {
    if (this.istLive() || this.istVideo()) return;
    /* Aus demselben Grund wie beim Aufsetzen - sonst rollt die Seite dahinter mit. */
    ereignis.preventDefault();
    if (ereignis.touches.length !== 2 || !this.griffStart) return;
    const wert = (this.griffZoom * this.griffAbstand(ereignis.touches)) / this.griffStart;

    /*
     * Aufgezogen wird um die Stelle zwischen den Fingern - genau wie das Rad
     * um den Zeiger zieht. Vorher wuchs das Bild immer aus der Mitte der
     * Flaeche: wer eine Ecke ansehen wollte, musste erst zoomen und dann
     * hinschieben. Weil die Mitte mit den Fingern wandert, wandert das Bild
     * bei dieser Rechnung ganz nebenbei mit.
     */
    const mitte = this.griffMitte(ereignis.touches);
    if (mitte) this.zoomeAufPunkt(wert, mitte.x, mitte.y);
    else this.setzeZoom(wert);
  }

  beiGriffAus() {
    this.griffStart = 0;
    this.griffMerk = null;
    /* Was waehrend der Geste eingeplant war, gehoert noch gezeichnet. */
    if (this.zeichenPlan) {
      cancelAnimationFrame(this.zeichenPlan);
      this.zeichenPlan = 0;
      this.zeichneJetzt();
    }
  }

  /** @returns {number} Abstand der beiden Finger. */
  abstand() {
    const punkte = [...this.zeiger.values()];
    if (punkte.length < 2) return 0;
    return Math.hypot(punkte[0].x - punkte[1].x, punkte[0].y - punkte[1].y);
  }

  /**
   * Der Platz reicht bis knapp ueber das Kauffenster. Dessen Hoehe aendert sich
   * mit der Beschreibung, also wird sie bei jedem Oeffnen gemessen. Am Rechner
   * haftet es nicht unten, dort bleibt der ganze Bereich frei.
   */
  miss() {
    /*
     * Waehrend sich unter der Ansicht etwas bewegt, ruht das Milchglas.
     *
     * Der Rahmen zeichnet den Hintergrund weich, und weich gezeichnet wird
     * die Seite darunter. Aendert dort etwas seine Groesse - beim Wechsel
     * des Formats etwa waechst oder schrumpft die Vorschaukachel deutlich -,
     * muss der Weichzeichner staendig neu abtasten. Manche Browser bauen ihn
     * dabei sichtbar neu auf, und weil der Rahmen fast den ganzen Bildschirm
     * einnimmt, flackert es ueber die ganze Flaeche. Fuer den Moment der
     * Bewegung deckt der Rahmen deshalb voll; bei 82 Prozent Deckung faellt
     * der Unterschied nicht auf.
     */
    if (this.feld && !this.feld.hidden) {
      const feld = this.feld;
      feld.classList.add('dp-bildfeld--ruhig');
      window.clearTimeout(feld.dpRuhigUhr);
      feld.dpRuhigUhr = window.setTimeout(() => feld.classList.remove('dp-bildfeld--ruhig'), 400);
    }

    /*
     * Die Ansicht deckt genau die Spalte der Bildergalerie ab. Sie einfach ueber
     * die Seitenbreite zu legen liess links einen Streifen der Galerie stehen
     * und schob sie rechts ueber das Kauffenster - dessen Spalte gehoert ihr
     * nicht. Gemessen wird deshalb die Galerie selbst.
     */
    /*
     * Die Bilder einer Bewertung oeffnen sich an derselben Stelle wie die
     * Produktbilder - gemessen wird dann die Galerie des Artikels, nicht die
     * kleine Bildreihe in der Bewertung.
     */
    /*
     * Eine Galerie kann ihre Flaeche selbst nennen (data-dp-flaeche, ein
     * Selektor): die Artikel auf "Bestellung verfolgen" (dp-bestellung.js)
     * sind kleine Kacheln - die Ansicht nimmt dort am Rechner den Platz neben
     * der Spalte der Personalisierung ein.
     */
    const eigeneFlaeche = this.galerie.dataset.dpFlaeche ? document.querySelector(this.galerie.dataset.dpFlaeche) : null;
    const flaeche =
      eigeneFlaeche ||
      (this.kachel === KACHEL_BEWERTUNG &&
        (document.querySelector('.product-information media-gallery') || document.querySelector('.product-information__media'))) ||
      this.galerie;
    /*
     * Die Ansicht des Chats nimmt nicht die Spalte des Chat-Verlaufs, sondern die Seite: von Seitenrand zu Seitenrand
     * (--dp-kopf-seitenrand, am Handy 0). Der Wert ist ein Ausdruck (max(...)) - er wird ueber den Innenabstand
     * einer unsichtbaren Probe in Punkte umgerechnet.
     */
    let rechteck = flaeche.getBoundingClientRect();
    if (this.istChat) {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;width:0;height:0;padding-left:var(--dp-kopf-seitenrand,0px)';
      document.body.appendChild(probe);
      const rand = Number.parseFloat(getComputedStyle(probe).paddingLeft) || 0;
      probe.remove();
      rechteck = { left: rand, width: Math.max(0, document.documentElement.clientWidth - 2 * rand), top: 0 };
    }
    this.feld.style.setProperty('--dp-galerie-links', Math.round(rechteck.left) + 'px');
    this.feld.style.setProperty('--dp-galerie-breite', Math.round(rechteck.width) + 'px');

    /*
     * Die Oberkante liegt auf Hoehe der obersten Bilder. Ist bereits gescrollt,
     * waere das oberhalb des sichtbaren Bereichs - dann setzt sie knapp unter
     * den Header, der ja stehen bleibt.
     */
    const header = document.querySelector('#header-component');
    const untenAmHeader = header ? header.getBoundingClientRect().bottom : 12;
    /*
     * Handy quer: Ober- und Unterkante der Ansicht sind die des Kauffensters
     * selbst - gemessen am Fenster, nicht nachgerechnet (es haftet je nach
     * Seitenlage unterschiedlich, etwa am Ende der Bilderspalte). Ohne Messung
     * (kein Fenster) gilt der Abstand unter dem Header (--dp-kauf-luft) wie dort.
     * Die Unterkante steht im Stilblatt (--dp-galerie-unten, Abschnitt
     * "Grossansicht quer: gleiche Hoehe wie das Kauffenster"). Ohne Runden:
     * beide Kanten stimmen auf einen halben Punkt.
     */
    const quer = window.matchMedia(
      'screen and (min-width: 750px) and (orientation: landscape) and (max-height: 500px) and (pointer: coarse)'
    ).matches;
    const kaufQuer = quer ? document.querySelector('.dp-kauffenster') : null;
    const kaufMass = kaufQuer ? kaufQuer.getBoundingClientRect() : null;
    if (kaufMass && kaufMass.height > 0) {
      /*
       * Ohne die Verschiebung des Anmalens: Beim Malen schwebt das Kauffenster 56 Punkte nach unten weg (dp-anmalen.css, translate); wird in
       * dieser Zeit gemessen, rutschten Ansicht und Anmalen-Buehne mit (Handy quer). Gemessen wird die Lage ohne diese Verschiebung.
       */
      const verschiebung = Number.parseFloat((getComputedStyle(kaufQuer).translate || '').split(' ')[1]) || 0;
      this.feld.style.setProperty('--dp-galerie-oben', (kaufMass.top - verschiebung).toFixed(2) + 'px');
      this.feld.style.setProperty('--dp-galerie-unten', (window.innerHeight - kaufMass.bottom + verschiebung).toFixed(2) + 'px');
    } else {
      this.feld.style.removeProperty('--dp-galerie-unten');
      this.feld.style.setProperty('--dp-galerie-oben', Math.round(Math.max(rechteck.top, untenAmHeader)) + 'px');
    }

    /*
     * Auf dem Handy legt sich das Kauffenster waehrend der Grossansicht an den
     * unteren Bildschirmrand. Der Platz dafuer wird hier freigehalten, sonst
     * liefe das Bild dahinter weiter.
     *
     * Entschieden wird das an der Fensterbreite und nicht an der Art der
     * Positionierung: das Kauffenster ist mal am Bildschirm festgemacht, mal im
     * Fluss verankert - vor der Ansicht liegt es in beiden Faellen. Am Rechner
     * steht es dagegen in der eigenen Spalte neben den Bildern und nimmt der
     * Ansicht nichts weg.
     */
    // Der Chat laesst das Kauffenster in Ruhe: unten bleibt nichts frei, die Ansicht liegt ueber ihm (Stilblatt).
    const kauf = this.istChat ? null : document.querySelector('.dp-kauffenster');
    const schmal = window.matchMedia('(max-width: 749px)').matches;

    /*
     * Ist die Tastatur offen, steht dort nicht mehr das ganze Kauffenster,
     * sondern nur noch die eine Zeile, an der gearbeitet wird
     * (dp-tastaturzeile.js). Freizuhalten ist dann ihre Oberkante - und das
     * Bild bekommt den Rest zurueck.
     */
    const band = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--dp-tastaturband')
    );
    let hoehe = kauf && schmal ? Math.round(kauf.getBoundingClientRect().height) : 0;
    /*
     * Bei den Bildern einer Bewertung steht unten nicht das Kauffenster,
     * sondern die Bewertung (dp-bewertungen.js) - mit festem Platz, gleich
     * welche Hoehe das Kauffenster gerade hat. Die Bewertung liest den Wert
     * an --dp-kauf-hoehe ab und fluchtet so mit dem Bild.
     */
    /* Ebenso bei einer Galerie mit eigener Seitenflaeche (data-dp-seite, etwa die Personalisierung auf "Bestellung verfolgen"). */
    if (schmal && (this.kachel === KACHEL_BEWERTUNG || this.galerie.dataset.dpSeite)) {
      /* Braucht die Seitenflaeche mehr Platz (zwei Flaechen auf "Bestellung verfolgen"), nennt die Galerie ihn selbst. */
      const eigene = Number(this.galerie.dataset.dpSeiteHoehe);
      hoehe = Number.isFinite(eigene) && eigene > 0 ? Math.round(eigene) : Math.round(Math.min(320, Math.max(180, window.innerHeight * 0.34)));
    }

    /*
     * Bei offener Tastatur liegt die Ansicht auf dem sichtbaren Fenster, nicht
     * auf dem Layout-Fenster.
     *
     * Unter iOS bleibt das Layout-Fenster bei offener Tastatur in voller
     * Hoehe, das sichtbare Fenster ist kleiner und kann darin nach unten
     * verschoben stehen (visualViewport.offsetTop), sobald die Seite etwas
     * gerollt ist. Die Ansicht haengt als fest gelegtes Element am
     * Layout-Fenster: Ihre Oberkante lag dann um genau diesen Versatz ueber dem
     * Bildschirm, und die ganze Ansicht wirkte nach oben verschoben, statt
     * sauber ueber der gehobenen Zeile zu sitzen. Jetzt nimmt sie Ober- und
     * Unterkante des sichtbaren Fensters an. Unter Android ist beides dasselbe,
     * dort bleiben die Werte null.
     */
    const sicht = window.visualViewport;
    const tastatur = schmal && Number.isFinite(band) && band > 0;
    let untenVerdeckt = 0;
    if (tastatur && sicht) {
      const oben = Math.max(0, Math.round(sicht.offsetTop));
      untenVerdeckt = Math.max(0, Math.round(window.innerHeight - sicht.offsetTop - sicht.height));
      this.feld.style.top = oben + 'px';
      this.feld.style.bottom = untenVerdeckt + 'px';
    } else {
      this.feld.style.removeProperty('top');
      this.feld.style.removeProperty('bottom');
    }

    /*
     * Das Band frisch an der Zeile gemessen, nicht der gemeldete Wert.
     *
     * Die Zeile meldet ihr Band (--dp-tastaturband) nur, wenn sie gelegt wird.
     * Rollt man danach mit offener Tastatur, faehrt sie unter iOS per
     * Verschiebung mit (dp-tastaturzeile.js, folgeVersatz) - der gemeldete Wert
     * bleibt aber beim alten Stand. Die Ansicht endete dann um genau die
     * gerollte Strecke zu hoch und stand weit oben. Gemessen wird die
     * Oberkante ihrer Flaeche: die ragt um den Rand (::before) ueber die Zeile.
     */
    /*
     * Gerechnet wird vom unteren Rand der Ansicht selbst bis zur Zeile - beide
     * mit derselben Messung. Auf dem iPhone gemessen (Spur A44): Dort liefert
     * getBoundingClientRect bei offener Tastatur Werte im sichtbaren Fenster
     * (die Ansicht mit top 105px stand bei 0), nicht im Layout-Fenster. Wer
     * die Zeile gegen window.innerHeight rechnete, mischte beide und zog den
     * Versatz doppelt ab. Der Abstand zweier gemessener Kanten stimmt dagegen
     * in jedem Bezugssystem.
     */
    /*
     * Nachtrag: Auch die Unterkante der Ansicht zu messen, ging schief - beim
     * ersten Messen ist sie noch nicht sichtbar und misst null, der Rahmen lief
     * dann hinter die Zeile. Deshalb gar keine Fensterkante mehr, nur noch die
     * Zeile selbst: Sie steht immer mit ihrer Flaeche LUFT (10 Punkte, wie in
     * dp-tastaturzeile.js) ueber der Tastatur, und die Ansicht endet genau an
     * der Tastatur. Das Band ist damit Flaeche plus Luft - die Hoehe der Zeile,
     * der Ueberstand ihrer Flaeche oben und unten und die Luft darunter.
     */
    let bandJetzt = Number.isFinite(band) ? band - untenVerdeckt : 0;
    const gehoben = tastatur ? document.querySelector('.dp-veredelung__feld--gehoben') : null;
    if (gehoben) {
      const ueberstand = Math.max(0, -(Number.parseFloat(getComputedStyle(gehoben, '::before').top) || 0));
      bandJetzt = Math.max(0, Math.round(gehoben.getBoundingClientRect().height + 2 * ueberstand + 10));
    }

    /* Beim Anmalen (SOLID) steht statt des Kauffensters das Fenster der Werkzeuge - die Szene reicht bis darueber (Jimmi, Oktober 2026). */
    const anmalPlatz = Number(document.documentElement.dataset.dpAnmalenPlatz);
    if (schmal && anmalPlatz > 0) hoehe = Math.round(anmalPlatz);

    if (tastatur) {
      /*
       * Dieselbe Luft wie zum Kauffenster.
       *
       * Beim Fenster zaehlt hier nur seine eigene Hoehe; den Abstand, den es
       * zum unteren Rand haelt (zwoelf Punkte und die Gestenleiste), traegt es
       * selbst. Im Band der gehobenen Zeile steckt dieser Abstand dagegen schon
       * drin - es reicht bis zum Rand des sichtbaren Fensters. Ohne den Abzug
       * stand das Bild ueber der Zeile spuerbar hoeher als ueber dem Fenster.
       */
      const sicher = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dp-sicher-unten'));
      /* Das Band zaehlt vom unteren Rand des Layout-Fensters; die Ansicht endet jetzt am sichtbaren - der verdeckte Teil faellt heraus. */
      hoehe = Math.max(0, Math.round(bandJetzt - 12 - (Number.isFinite(sicher) ? sicher : 0)));
    }

    /*
     * Der freigehaltene Platz wechselt weich.
     *
     * Geht ueber der offenen Grossansicht die Tastatur auf, steht unten statt
     * des ganzen Kauffensters nur noch die eine gehobene Zeile - der Platz
     * darunter faellt in einem Schritt von mehreren hundert Punkten auf
     * wenige, und beim Schliessen ebenso zurueck. Ungeregelt sprang der Rahmen
     * dabei, und das Bild sprang mit.
     *
     * Die Hoehe ist darum eine registrierte Laenge und laeuft (Stilblatt).
     * Beim ersten Messen einer frisch geoeffneten Ansicht gibt es nichts zu
     * bewegen: Dort wird der Wert nur gesetzt.
     */
    const vorher = this.kaufHoehe;
    this.kaufHoehe = hoehe;
    if (vorher !== undefined && vorher !== hoehe) {
      /* Waechst der freigehaltene Platz, wird das Bild kleiner - das bekommt mehr Zeit. */
      this.feld.style.setProperty('--dp-flaeche-dauer', (hoehe > vorher ? FLAECHE_ZU : FLAECHE_AUF) + 'ms');
    }
    /* Wo die Mitte der Flaeche vor der Aenderung stand - ziehBildNach gleicht ihre Wanderung fuer die Ansicht des Kunden aus. */
    if (vorher !== undefined && vorher !== hoehe && this.streifen) {
      const z = this.streifen.children[this.platz];
      if (z && !(this.zellMitte && performance.now() - this.zellMitte.zeit < 300)) {
        const q = z.getBoundingClientRect();
        const m0 = this.bildMasseRoh(z);
        this.zellMitte = { index: this.index, y: q.top + q.height / 2, zeit: performance.now(), breite: m0 ? m0.breite : 0 };
      }
    }
    this.feld.style.setProperty('--dp-kauf-hoehe', hoehe + 'px');
    if (vorher !== undefined && vorher !== hoehe) this.begleiteFlaeche(hoehe > vorher ? FLAECHE_ZU : FLAECHE_AUF);

    this.setzeLoch();

    /*
     * Mit der Flaeche aendert sich, wie gross das Bild liegen muss. Ohne
     * dieses Nachrechnen behielt es seine alte Groesse, bis irgendwann etwas
     * anderes ein Zeichnen ausloeste - auf dem Telefon sah man es beim
     * Aufziehen der Tastatur einen Moment lang verzerrt stehen.
     *
     * Der Zoom des Kunden bleibt dabei, wie er ist; nachgezogen wird nur die
     * Grundlage, auf der er sitzt.
     */
    this.ziehBildNach();
  }

  /**
   * Rechnet nach, wie gross das Bild in der jetzigen Flaeche liegen muss.
   *
   * Der Zoom des Kunden bleibt dabei, wie er ist; nachgezogen wird nur die
   * Grundlage, auf der er sitzt.
   */
  ziehBildNach() {
    if (!this.feld || this.feld.hidden || !this.streifen) return;
    const zelle = this.streifen.children[this.platz];
    if (!zelle) return;
    if (this.zoom <= 1.01) {
      const frisch = this.grundVon(zelle);
      if (frisch) this.grundZoom = frisch;
    }
    /*
     * Waechst oder schrumpft die Flaeche (Kauffenster klappt auf, Tastatur geht auf), wandert ihre Mitte - und das Bild sitzt auf ihr.
     * Hat der Kunde seine Ansicht gewaehlt, bleibt das Bild dabei an seinem Platz auf dem Bildschirm: der Versatz gleicht die Wanderung aus.
     */
    const r = zelle.getBoundingClientRect();
    const jetzt = performance.now();
    const mitteY = r.top + r.height / 2;
    const vorige = this.zellMitte;
    const masse = zelle instanceof HTMLElement ? this.bildMasseRoh(zelle) : null;
    if (vorige && vorige.index === this.index && jetzt - vorige.zeit < 300 && this.hatKundenAnsicht()) {
      const wanderung = mitteY - vorige.y;
      if (Math.abs(wanderung) > 0.01) this.versatz = { x: this.versatz.x, y: this.versatz.y - wanderung };
      /* Das Bild liegt in der Flaeche, so gross wie sie es zulaesst - aendert sie sich, aendert sich seine Grundgroesse. Der Zoom gleicht es aus, sodass es gleich gross bleibt. */
      if (masse && vorige.breite && Math.abs(masse.breite - vorige.breite) > 0.01) this.zoom = Math.max(0.01, (this.zoom * vorige.breite) / masse.breite);
    }
    this.zellMitte = { index: this.index, y: mitteY, zeit: jetzt, breite: masse ? masse.breite : 0 };
    this.begrenzeVersatz();
    this.zeichneZoom();
  }

  /**
   * Begleitet den laufenden Wechsel der Flaeche, Bild fuer Bild.
   *
   * Die Unterkante des Rahmens laeuft im Stilblatt weich, Bild und Loch
   * rechnet dagegen dieses Skript. Ohne Begleitung stuenden beide sofort am
   * Ziel, waehrend der Rahmen noch unterwegs waere - das Bild haette einen
   * Moment nicht in seinen Rahmen gepasst. Solange die Bewegung laeuft, deckt
   * der Rahmen ausserdem voll (--ruhig), sonst tastet der Weichzeichner in
   * jedem Bild neu ab und flackert.
   */
  begleiteFlaeche(dauer) {
    const bis = performance.now() + (dauer || FLAECHE_ZU) + 100;
    this.flaecheBis = Math.max(this.flaecheBis || 0, bis);

    /* Die Ruhe gehoert der ganzen Bewegung - auch wenn miss() zwischendurch
       ihre eigene kurze Uhr stellt. */
    const feld = this.feld;
    feld.classList.add('dp-bildfeld--ruhig');
    window.clearTimeout(feld.dpRuhigUhr);

    if (this.flaecheLaeuft) return;
    this.flaecheLaeuft = true;

    const schritt = () => {
      if (!this.feld || this.feld.hidden) {
        this.flaecheLaeuft = false;
        feld.classList.remove('dp-bildfeld--ruhig');
        return;
      }
      this.setzeLoch();
      this.ziehBildNach();
      if (performance.now() >= this.flaecheBis) {
        this.flaecheLaeuft = false;
        feld.classList.remove('dp-bildfeld--ruhig');
        return;
      }
      requestAnimationFrame(schritt);
    };
    requestAnimationFrame(schritt);
  }

  /**
   * Setzt das Loch, das die Abdunklung um das Kauffenster herum aussparen
   * laesst.
   *
   * Die Abdunklung spart das Fenster aus - als Loch in seiner Form, nicht als
   * abgeschnittener Streifen: rundherum bleibt sie stehen, nur dahinter
   * faellt sie weg. Das Fenster ist selbst dunkles Milchglas und wurde mit
   * der Kulisse dahinter sichtbar dunkler als sonst.
   *
   * Gestanzt wird das Loch von einem leeren Kasten in der Form des Fensters,
   * der die Abdunklung als Schlagschatten ringsum wirft - nicht mehr von
   * einer Maske. Eine neue Maske liess die Abdunklung ueber den ganzen
   * Bildschirm neu aufbauen; deshalb wurde sie frueher erst nach einer
   * Ruhepause geschrieben, und waehrend die Beschreibung auf- oder zuklappte
   * stand das Loch sichtbar neben dem Fenster. Der Schatten folgt jeder
   * Aenderung sofort.
   *
   * Das Loch sitzt am gemessenen Rechteck des Fensters - am Handy wie am
   * Rechner. Aus der blossen Hoehe nachgerechnet ging es daneben, sobald das
   * Geraet unten Platz fuer seine Gestenleiste reserviert
   * (--dp-sicher-unten): das Fenster sitzt darueber, das Loch hing am nackten
   * Bildschirmrand. Unter dem Fenster blieb dann ein heller Streifen ohne
   * Abdunklung stehen, darueber lag einer zu viel.
   */
  setzeLoch() {
    const kulisse = this.feld && this.feld.querySelector('.dp-bildfeld__kulisse');
    const lochKasten = this.feld && this.feld.querySelector('.dp-bildfeld__loch');
    if (!(kulisse instanceof HTMLElement) || !(lochKasten instanceof HTMLElement)) return;

    /*
     * Ausgespart wird, was gerade unten steht - und das ist bei offener
     * Tastatur nicht mehr das Kauffenster, sondern nur noch die eine gehobene
     * Zeile (dp-tastaturzeile.js). Das Fenster behaelt dabei seine Masse und
     * wird bloss unsichtbar; wer weiter danach stanzt, laesst ein Loch stehen,
     * hinter dem nichts mehr ist.
     */
    /*
     * Bei den Bildern einer Bewertung steht neben der Ansicht die Bewertung
     * (dp-bewertungen.js), nicht das Kauffenster - ausgespart wird dann sie.
     * Mit dem Loch am Kauffenster schien dort der Header unverdunkelt durch,
     * wo beide sich ueberlappten (Jimmi, Oktober 2026).
     */
    /* Eine Galerie mit eigener Seitenflaeche (data-dp-seite: ihr Selektor) spart diese aus - wie die Bewertung. */
    const seite = this.galerie.dataset.dpSeite || '';
    const bewertung = this.kachel === KACHEL_BEWERTUNG || !!seite;
    // Die Ansicht des Chats spart nichts aus: sie liegt ueber dem Kauffenster, nicht neben ihm.
    const zeile = bewertung || this.istChat ? null : document.querySelector('.dp-veredelung__feld--gehoben');
    /* data-dp-seite="-": Platz freihalten, aber nichts aussparen - die Flaechen liegen auf der Abdunklung. */
    const kauf = this.istChat ? null : bewertung ? (seite === '-' ? null : document.querySelector(seite || '.dp-bewertung-seite')) : zeile || document.querySelector('.dp-kauffenster');
    let fenster = kauf ? kauf.getBoundingClientRect() : null;
    let eckenStil = kauf ? getComputedStyle(kauf) : null;
    if (zeile && fenster) {
      /*
       * Sichtbar ist bei der Zeile ihre Flaeche (::before), und die ragt um
       * ihren Rand ueber die Zeile hinaus. Gestanzt wird deshalb die Flaeche -
       * mit der Zeile als Mass blieb rundherum ein abgedunkelter Streifen auf
       * ihr liegen.
       */
      const flaeche = getComputedStyle(zeile, '::before');
      const masse = [flaeche.left, flaeche.top, flaeche.width, flaeche.height].map(parseFloat);
      if (masse.every(Number.isFinite) && masse[2] > 2 && masse[3] > 2) {
        fenster = { left: fenster.left + masse[0], top: fenster.top + masse[1], width: masse[2], height: masse[3] };
        eckenStil = flaeche;
      }
    }
    const stanzen = !!fenster && fenster.width > 2 && fenster.height > 2;
    let stand = 'ohne';

    if (stanzen) {
      /*
       * Einen Pixel in das Fenster hinein: seine Masse stehen auf Bruchteilen
       * von Pixeln, und ein Loch genau auf der Kante liesse ringsum einen
       * hellen Haarstrich stehen. Die knappe Ueberlappung schluckt das dunkle
       * Glas des Fensters.
       */
      const grund = kulisse.getBoundingClientRect();
      const links = Math.round(fenster.left - grund.left) + 1;
      const oben = Math.round(fenster.top - grund.top) + 1;
      const breite = Math.max(0, Math.round(fenster.width) - 2);
      const hoehe = Math.max(0, Math.round(fenster.height) - 2);
      const ecke = Math.round(parseFloat(eckenStil.borderTopLeftRadius)) || 18;
      stand = [links, oben, breite, hoehe, ecke].join('|');
      if (stand !== this.lochStand) {
        lochKasten.style.left = links + 'px';
        lochKasten.style.top = oben + 'px';
        lochKasten.style.width = breite + 'px';
        lochKasten.style.height = hoehe + 'px';
        lochKasten.style.borderRadius = ecke + 'px';
      }
    }

    // Geschrieben wird nur, wenn sich wirklich etwas geaendert hat.
    if (stand === this.lochStand) return;
    this.lochStand = stand;
    lochKasten.hidden = !stanzen;
    kulisse.classList.toggle('dp-bildfeld__kulisse--gelocht', stanzen);
  }

  /**
   * Laesst das Loch dem Kauffenster folgen, solange die Ansicht offen ist.
   *
   * Der ResizeObserver allein reicht nicht: Beim Oeffnen wechselt das Fenster
   * am Handy von seinem Platz im Fluss an den unteren Bildschirmrand - es
   * bewegt sich, ohne seine Groesse zu aendern, und das Loch blieb an der
   * alten Stelle stehen. Dasselbe gilt, wenn die Beschreibung auf- oder
   * zuklappt: dort aendert sich die Hoehe bewegt, Bild fuer Bild. Gemessen
   * wird deshalb in jedem Bild; geschrieben wird nur bei einer Aenderung.
   */
  verfolgeFenster() {
    if (this.verfolgung) return;
    const schritt = () => {
      if (!this.feld || this.feld.hidden || !this.feld.isConnected) {
        this.verfolgung = 0;
        return;
      }
      this.setzeLoch();
      this.verfolgung = requestAnimationFrame(schritt);
    };
    this.verfolgung = requestAnimationFrame(schritt);
  }
}

/*
 * Was als Galerie zaehlt: Horizons media-gallery - und auf der 3D-Vorlage
 * ohne Fotos die alleinstehende Live-Vorschau (dp-3d-galerie--allein aus
 * dp-3d-vorschau.js), sonst liesse sie sich nicht gross ansehen.
 */
/*
 * Dazu die Bildreihe jeder Bewertung (sections/dp-bewertungen.liquid, Jimmi,
 * Oktober 2026): dieselbe Grossansicht, nur mit den Bildern dieser einen
 * Bewertung. Ihre Kacheln heissen anders (KACHEL_BEWERTUNG) - als
 * Produktgalerie verkleidet, griffen sonst Skripte nach ihr, die nur die
 * Produktbilder meinen (Kauffenster, Farbfilter).
 */
const GALERIEN = 'media-gallery, .dp-3d-galerie--allein, .dp-druck-galerie--allein, .dp-bewertung__bilder, ' + CHAT_VERLAUF;

/**
 * Gehoert diese Galerie in den Warenkorb (Bilderreihe der Personalisierung dort, Fenster, Seite)? Dann ist sie nie die Produktgalerie der offenen
 * Seite: Eine offene Grossansicht des Artikels uebernimmt sie nicht (Jimmi, Oktober 2026: nach dem Legen in den Warenkorb zeigte sie die Bilder
 * aus dem Warenkorb) - die Warenkorb-Reihe bekommt eine eigene Ansicht mit ihrer eigenen Menge. Dieselbe Auswahl wie in dp-veredelung.js:
 * media-gallery:not(.dp-korb-perso-galerie), nicht in cart-drawer oder Dialog.
 */
function istKorbGalerie(galerie) {
  return (
    galerie instanceof Element &&
    (galerie.classList.contains('dp-korb-perso-galerie') ||
      !!galerie.closest('.dp-korb-perso-galerie, cart-drawer, cart-drawer-component, .cart-drawer, cart-items-component, .cart-items, #cart-drawer, dialog'))
  );
}

/**
 * Baut die Ansicht fuer den Verlauf des Livechats: eine eigene Instanz, die sich nie mit der Ansicht einer
 * Produktgalerie vermischt (keine Uebernahme, kein Tausch - siehe starte). Ohne Bildanhang steht hier eine
 * leere Instanz ohne Feld und ohne Horcher; raeumeAuf() vertraegt das.
 */
function baueChatAnsicht(galerie) {
  galerie.dataset.dpBildfeld = '';
  const chat = new DpBildOverlay(galerie);
  galerie.dpBildfeld = chat;
  if (chat.feld) ansichten.add(chat);
}

function starte() {
  for (const galerie of document.querySelectorAll(GALERIEN)) {
    if (galerie.dataset.dpBildfeld !== undefined) continue;

    // Der Chat hat seinen eigenen Weg: keine Uebernahme einer offenen Produktansicht, kein Austausch.
    if (galerie.matches(CHAT_VERLAUF)) {
      baueChatAnsicht(galerie);
      continue;
    }

    galerie.dataset.dpBildfeld = '';

    /*
     * Eine Galerie des Warenkorbs bekommt ihre eigene Ansicht - und nimmt nie die offene Ansicht des Artikels an sich (siehe istKorbGalerie).
     */
    if (istKorbGalerie(galerie)) {
      const korb = new DpBildOverlay(galerie);
      galerie.dpBildfeld = korb;
      if (korb.feld) ansichten.add(korb);
      continue;
    }

    /*
     * Stand vor diesem Austausch eine Ansicht offen, deren Galerie es nicht
     * mehr gibt, wird sie hier weitergereicht: dieselbe Stelle, nur mit den
     * Bildern der neuen Variante. Ohne das bliebe die alte Ansicht mit den
     * Bildern der vorigen Farbe stehen - und ihre Galerie, an der Blaettern
     * und Messen haengen, waere gar nicht mehr auf der Seite.
     */
    const vorgaenger = offeneAnsicht && !offeneAnsicht.galerie.isConnected ? offeneAnsicht : null;

    /*
     * Auf der Vorlage mit der Druckvorschau wechselt beim Formatwechsel
     * kein einziges Bild: dasselbe Foto, dieselbe lebende Vorschau. Die
     * offene Ansicht bleibt deshalb stehen und uebernimmt nur die neue
     * Galerie - die Ueberblendung dazwischen war als Ausblenden und
     * Wiederaufgehen zu sehen.
     */
    /*
     * Geprueft wird an der Vorlage, nicht am Inhalt der neuen Galerie: die
     * Vorschaukachel haengt unser eigenes Skript erst nachtraeglich ein, und
     * je nach Browser laeuft das mal vor, mal nach diesem Aufbau. Beim
     * spaeteren Lauf griff die Uebernahme nicht, und die Ansicht ging kurz
     * zu und wieder auf.
     */
    /*
     * Und zwar auch dann, wenn die alte Galerie noch am Baum haengt: beim
     * Austausch stehen beide einen Augenblick nebeneinander. Wer nur auf
     * die abgehaengte alte Galerie wartet, verpasst diesen Moment - dann
     * entstuende eine zweite Ansicht, und die offene wuerde kurz darauf
     * abgeraeumt. Genau das war das kurze Zugehen.
     */
    const offenHier = offeneAnsicht && offeneAnsicht.feld && !offeneAnsicht.feld.hidden && offeneAnsicht.galerie !== galerie;
    if (offenHier && document.querySelector('.dp-kauffenster--transfer')) {
      offeneAnsicht.uebernimmGalerie(galerie);
      continue;
    }

    if (vorgaenger && document.querySelector('.dp-kauffenster--transfer')) {
      vorgaenger.uebernimmGalerie(galerie);
      continue;
    }

    /*
     * Farbwechsel bei offener Grossansicht (Jimmi, Oktober 2026: "Kann man es
     * so einstellen, dass das Bildoverlay immer eingeblendet bleibt und das
     * entsprechende Bild nur ausgetauscht wird?"): Die offene Ansicht bleibt
     * stehen - Kulisse, Rahmen, X, Zaehler und Pfeile -, sie uebernimmt die
     * neue Galerie und tauscht nur ihre Bilder, mit einer Ueberblendung vom
     * alten zum neuen Bild. Die 3D-Buehne bleibt dabei in ihrer Zelle.
     */
    /*
     * Auch eine offene Ansicht, die nicht (mehr) als offeneAnsicht vermerkt ist - etwa
     * weil sie beim ersten Wechsel nach dem Laden eine andere Instanz war: Jede offene
     * Ansicht einer Produktgalerie bleibt stehen und tauscht nur ihre Bilder.
     */
    const offeneProdukt = galerie.matches('media-gallery') && !galerie.classList.contains('dp-korb-perso-galerie')
      ? [...ansichten].find((a) => a && a.feld && !a.feld.hidden && a.galerie !== galerie && !a.feld.classList.contains('dp-bildfeld--korb') && a.galerie instanceof Element && a.galerie.matches('media-gallery') && !a.galerie.classList.contains('dp-korb-perso-galerie'))
      : null;
    const offeneHier = offenHier ? offeneAnsicht : vorgaenger && vorgaenger.feld && !vorgaenger.feld.hidden ? vorgaenger : offeneProdukt || null;
    if (offeneHier && typeof offeneHier.tauscheGalerie === 'function') {
      offeneHier.tauscheGalerie(galerie);
      continue;
    }

    const neu = new DpBildOverlay(galerie);
    // Die Instanz bleibt am Element erreichbar - hilfreich beim Nachmessen.
    galerie.dpBildfeld = neu;
    if (neu.feld) ansichten.add(neu);

    if (vorgaenger) {
      /*
       * Die alte Ansicht bleibt fuer den Moment der Ueberblendung liegen.
       *
       * Die neue legt sich darueber (sie steht spaeter im Dokument) und
       * blendet ueber ihre normale Oeffnungs-Blende ein - unter ihr bleibt
       * dabei durchgehend die alte zu sehen: ein weicher Wechsel vom alten
       * zum neuen Bild, ohne dass dazwischen die Seite aufblitzt.
       *
       * Die Markierung schuetzt sie vor der Aufraeumschleife unten: waehrend
       * des Umbaus laeuft starte() mehrfach, und die Schleife raeumte die
       * alte Ansicht sonst schon im ersten Durchlauf ab - lange bevor die
       * neue sichtbar war. Genau das war als kurzes Schliessen und
       * Wiederaufgehen zu sehen.
       */
      if (neu.feld && neu.bilder.length) {
        vorgaenger.dpUebergabe = true;

        /*
         * Waehrend der Ueberblendung liegt die Kulisse nur einmal auf der
         * Seite: die neue Ansicht laesst ihre eigene solange weg und nutzt
         * die der alten als Unterlage. Zwei halbdurchsichtige Abdunklungen
         * uebereinander hatten den Hintergrund sichtbar doppelt verdunkelt.
         * Beim Abraeumen wechseln beide im selben Zug - die Deckung bleibt
         * dadurch durchgehend gleich.
         */
        neu.feld.classList.add('dp-bildfeld--wechsel');
        /*
         * Nach einem Farbwechsel bleibt die Ansicht beim entsprechenden Bild
         * (Jimmi, Oktober 2026): Stand die Vorschau offen, ist es die
         * Vorschau der neuen Farbe, sonst dasselbe Bild oder derselbe Platz
         * (entsprechung) - frueher immer das erste Bild.
         */
        const ziel = neu.entsprechung(vorgaenger.bilder[vorgaenger.index], vorgaenger.index);
        neu.oeffne(ziel);
        neu.meldeBildwechsel();

        /*
         * Und einmal anwischen, wenn das Ziel ein anderer Platz ist: der
         * Streifen startet einen Platz weiter und gleitet aufs Ziel - dieselbe
         * Bewegung wie beim Zurueckblaettern, damit der Wechsel nicht einfach
         * dasteht. Nur der Streifen faehrt; Zaehler und Ladeumfeld hat
         * oeffne() schon richtig gesetzt.
         *
         * Bleibt es beim selben Platz, gibt es nichts zu zeigen - dann bleibt
         * es bei der reinen Ueberblendung.
         */
        if (
          ziel !== vorgaenger.index &&
          neu.bilder.length > 1 &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          const streifen = neu.streifen;
          streifen.style.transition = 'none';
          streifen.style.transform = 'translateX(' + -(neu.platz + 1) * 100 + '%)';
          void streifen.offsetWidth;
          streifen.style.transition = '';
          requestAnimationFrame(() => {
            streifen.style.transform = 'translateX(' + -neu.platz * 100 + '%)';
          });
        }
        window.setTimeout(() => {
          neu.feld.classList.remove('dp-bildfeld--wechsel');
          vorgaenger.raeumeAuf();
        }, 320);
      } else if (vorgaenger.feld && !vorgaenger.feld.hidden) {
        /*
         * Die neue Farbe bringt kein einziges Bild mit - Bilder mit
         * Farb-Marke stehen erst mit ihrer Farbe, und ohne Bilder ohne Marke
         * bleibt die Galerie leer (dp-bildfilter). Dann gibt es nichts zum
         * Weiterzeigen: Die Ansicht schliesst mit ihrer gewohnten Blende,
         * statt schlagartig zu verschwinden, und wird erst danach abgeraeumt.
         */
        vorgaenger.dpUebergabe = true;
        vorgaenger.schliesse();
        window.setTimeout(() => vorgaenger.raeumeAuf(), 420);
      } else {
        vorgaenger.raeumeAuf();
      }
    }
  }

  // Ansichten ohne Galerie haben keine Aufgabe mehr - ausser sie liegen
  // gerade als Unterlage einer Ueberblendung (siehe oben).
  for (const ansicht of ansichten) {
    if (!ansicht.galerie.isConnected && !ansicht.dpUebergabe) ansicht.raeumeAuf();
  }
}

/*
 * Ein Variantenwechsel laesst das Theme den Abschnitt neu zeichnen; die
 * Bildergalerie wird dabei durch eine neue ersetzt. Ein Beobachter meldet das,
 * damit die Ansicht mitwandert.
 *
 * Bewusst mit Aufschub: waehrend des Austauschs kommen viele Meldungen, gerechnet
 * wird erst danach und nur einmal.
 */
function beobachteAustausch() {
  let geplant = false;

  const waechter = new MutationObserver(() => {
    if (geplant) return;
    if (!document.querySelector('media-gallery:not([data-dp-bildfeld])')) return;

    /*
     * Sobald der Austausch beginnt, ruht das Milchglas der offenen Ansicht.
     * Es zeichnet den Hintergrund weich, und der wird gerade neu aufgebaut -
     * das Nachziehen des Weichzeichners ist ueber die ganze Flaeche als
     * Flackern zu sehen. Hier, im Beobachter, faengt es frueher an als in
     * der Uebernahme weiter unten, und es greift auch dann, wenn die
     * Ansicht am Ende doch neu gebaut wird.
     */
    if (offeneAnsicht && offeneAnsicht.feld) {
      const feld = offeneAnsicht.feld;
      feld.classList.add('dp-bildfeld--ruhig');
      window.clearTimeout(feld.dpRuhigUhr);
      feld.dpRuhigUhr = window.setTimeout(() => feld.classList.remove('dp-bildfeld--ruhig'), 700);
    }

    geplant = true;
    window.setTimeout(() => {
      geplant = false;
      starte();
    }, 0);
  });

  waechter.observe(document.body, { childList: true, subtree: true });
}

/*
 * Manche Kacheln entstehen erst nach dem Aufbau der Ansicht (Transferdruck) - die
 * Bilderliste und der Streifen kennen sie dann noch nicht. Das Galerie-Modul
 * meldet sich mit diesem Ereignis, und die Ansicht wird neu aufgebaut.
 *
 * Eine gerade geoeffnete Ansicht wird nicht unter dem Betrachter abgerissen -
 * dann wartet der Neuaufbau, bis sie zu ist.
 */
function beiGalerieNeu() {
  if (offeneAnsicht) {
    /*
     * Die offene Ansicht wird nicht abgerissen - neue Kacheln fuegt sie
     * sich direkt hinten an. Nur wenn das nicht reicht (die Liste hat sich
     * anders veraendert als durch reines Anwachsen), wartet der Neuaufbau,
     * bis sie zu ist.
     */
    if (offeneAnsicht.ergaenze()) return;
    window.setTimeout(beiGalerieNeu, 1500);
    return;
  }

  for (const galerie of document.querySelectorAll(GALERIEN)) {
    const bisher = galerie.dpBildfeld;
    if (!bisher) continue;
    bisher.raeumeAuf();
    delete galerie.dataset.dpBildfeld;
    delete galerie.dpBildfeld;
  }
  starte();
}

/**
 * Neue Bilder im Verlauf des Livechats (dp-chat.js meldet sich nach dem Anhaengen, entprellt). Nur der
 * Chat wird angefasst - die Produktgalerien bleiben, wie sie sind (anders als dp:galerie-neu).
 * Eine offene Ansicht bekommt die neuen Bilder hinten angefuegt (ergaenze) und wird nicht abgerissen;
 * reicht das nicht (ein Bild fiel weg), wartet der Neuaufbau, bis sie zu ist. Ist sie zu, wird sie
 * neu gebaut - auch aus einer leeren Instanz (noch kein Bild), die ja keine Kacheln kennt.
 */
let chatBilderUhr = 0;
function beiChatBilderNeu() {
  window.clearTimeout(chatBilderUhr);
  for (const galerie of document.querySelectorAll(CHAT_VERLAUF)) {
    const bisher = galerie.dpBildfeld;
    if (bisher && bisher.feld && !bisher.feld.hidden) {
      if (!bisher.ergaenze()) chatBilderUhr = window.setTimeout(beiChatBilderNeu, 1500);
      continue;
    }
    if (bisher) bisher.raeumeAuf();
    delete galerie.dpBildfeld;
    baueChatAnsicht(galerie);
  }
}

function los() {
  starte();
  beobachteAustausch();
  document.addEventListener('dp:galerie-neu', beiGalerieNeu);
  document.addEventListener('dp:chat-bilder-neu', beiChatBilderNeu);
  // Gefiltert ohne Neubau (Vorschaufarbe) - die offene Ansicht zieht ihre Liste nach.
  document.addEventListener('dp:galerie-gefiltert', () => {
    if (offeneAnsicht) offeneAnsicht.folgeFilter();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', los, { once: true });
} else {
  los();
}
